import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import api, { API } from "@/lib/api";
import {
    prepareDownload, triggerSave, openInNewTab, shareFile, isMobile,
} from "@/lib/exports";
import { toast } from "sonner";
import {
    Send, Download, Copy, Upload, Trash2, FileText, Clipboard, Sparkles, Share2,
    ExternalLink, ImageIcon, RefreshCcw, ChevronRight, X, Archive, ArchiveRestore, History,
} from "lucide-react";
import { STAGES, AGENTS, ASSET_USAGE_TAGS, stageStatusColor, USER_STAGES, userStageForTab } from "@/lib/constants";
import { useCanonicalWorkflow } from "@/lib/workflow";
import CampaignInputsGate from "@/components/CampaignInputsGate";
import ApprovalBar from "@/components/layout/ApprovalBar";
import RunAgentButton from "@/components/layout/RunAgentButton";
import CeoEmbeddedPanel from "@/components/CeoEmbeddedPanel";
import StageVersions from "@/components/StageVersions";
import { LeaseBadge } from "@/components/lease/LeaseBadge";
import { LeaseDetailTooltip } from "@/components/lease/LeaseDetailTooltip";
import CompetitorProfilesPanel from "@/components/CompetitorProfilesPanel";
import SocialProfilesPanel from "@/components/SocialProfilesPanel";
import DistributionOpportunityPanel from "@/components/DistributionOpportunityPanel";
import SocialStrategyPanel from "@/components/SocialStrategyPanel";
import VideoPromptsPanel from "@/components/VideoPromptsPanel";
import SmartBriefBuilder from "@/components/SmartBriefBuilder";
import VideoStudioPanel from "@/components/VideoStudioPanel";
import ProductionContinuityPanel from "@/components/ProductionContinuityPanel";   // iter69 — pre-Storyboard reference lock
import DetailedProductionScriptPanel from "@/components/DetailedProductionScriptPanel";   // iter70 — detailed production script + character dialogue map
import RegenerateWithNotesModal from "@/components/RegenerateWithNotesModal";
import VersionCompareModal from "@/components/VersionCompareModal";
import ResearchEvidencePanel from "@/components/ResearchEvidencePanel";   // T3 — operator evidence + queue visibility
import CompetitorDiscoveryExternalPanel from "@/components/CompetitorDiscoveryExternalPanel"; // Sprint C — controlled external discovery
import SocialEvidenceIntakePanel from "@/components/SocialEvidenceIntakePanel"; // Sprint D — operator social intake
import DiscoveryWizard from "@/components/DiscoveryWizard"; // Sprint E — readiness wizard
import PersonasPanel from "@/components/PersonasPanel";                     // Sprint A — operator persona CRUD
import ResearchCompetitorInsightsCard from "@/components/ResearchCompetitorInsightsCard"; // Sprint A — render hidden research.competitor_analysis sub-fields
import BusinessPlanGoldPanel from "@/components/BusinessPlanGoldPanel";     // Sprint A — read-time business plan gold composer
// UX Sprint 1 — new premium board components.
import WorkflowTimeline from "@/components/board/WorkflowTimeline";
import StageSubTabs from "@/components/board/StageSubTabs";
import ProjectHeader from "@/components/board/ProjectHeader";
import MoreToolsDrawer from "@/components/board/MoreToolsDrawer";
import ProjectAssistantDrawer from "@/components/board/ProjectAssistantDrawer";
// UX Sprint 2 — stage workspace primitives + composite overviews.
import StageWorkspaceShell from "@/components/board/stage/StageWorkspaceShell";
import CampaignStrategyWorkspace from "@/components/board/stage/CampaignStrategyWorkspace";
import VisualBibleWorkspace from "@/components/board/stage/VisualBibleWorkspace";
import PresentationDeliveryWorkspace from "@/components/board/stage/PresentationDeliveryWorkspace";
// Storyboard Continuity Audit — surfaces what continuity the agent received + DPS-required state.
import DpsRequiredBanner from "@/components/board/storyboard/DpsRequiredBanner";
import ContinuityReferencesHeader, { useContinuityLookup } from "@/components/board/storyboard/ContinuityReferencesHeader";
import ContinuityThumb from "@/components/board/storyboard/ContinuityThumb";
// Reference Image Preview / Lightbox Fix — shared full-size preview modal.
import ImageLightbox from "@/components/common/ImageLightbox";
/** Universal `[object Object]` guard.
 *  Coerces any error-shaped value (string, axios error, FastAPI validation
 *  array, plain object, Error instance) into a human-readable string. Used
 *  everywhere we render `stage.error`, `state.last_error`, or any field that
 *  could leak from the backend as a non-string. */
const safeText = (v) => {
    if (v == null) return "";
    if (typeof v === "string") {
        // P2 / M8 — kill the legacy "[object Object]" literal that was
        // persisted by a pre-Phase-O frontend regression. We see ~20 such
        // rows in chat_messages today. safeText is invoked at every render
        // site, so this single transform retires the UI symptom without
        // needing a DB scrub.
        if (v === "[object Object]") return "[unreadable historical message]";
        return v;
    }
    if (typeof v === "number" || typeof v === "boolean") return String(v);
    if (v instanceof Error) return v.message || "Error";
    if (Array.isArray(v)) {
        return v.map((x) => (typeof x === "string"
            ? x
            : (x?.msg || x?.message || (() => {
                try { return JSON.stringify(x); } catch { return ""; }
            })())
        )).filter(Boolean).join("; ");
    }
    if (typeof v === "object") {
        if (typeof v.message === "string") return v.message;
        if (typeof v.detail === "string") return v.detail;
        if (typeof v.error === "string") return v.error;
        try { return JSON.stringify(v); } catch { return "Error"; }
    }
    return String(v);
};

/** Render a text blob with URLs auto-converted to clickable links + a
 *  one-tap copy button per link. Used in the CEO chat so the agent's
 *  "open your project board at <url>" / "share with the client at <url>"
 *  messages become actionable. Preserves whitespace + newlines via
 *  `whitespace-pre-wrap` on the parent. Safe-text-aware (coerces any
 *  non-string input via safeText first). */
const _URL_RE = /(https?:\/\/[^\s)\]}<>"']+)/g;
function LinkifiedText({ value }) {
    const text = safeText(value);
    if (!text) return null;
    const parts = text.split(_URL_RE);
    return (
        <span>
            {parts.map((part, idx) => {
                if (idx % 2 === 1) {
                    // URL segment
                    const clean = part.replace(/[.,;:!?)]+$/, "");
                    const trail = part.slice(clean.length);
                    return (
                        <span key={idx} className="inline-flex items-center gap-1 align-baseline">
                            <a
                                href={clean}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[#FF7B40] underline underline-offset-2 break-all hover:text-[#FF4400]"
                                data-testid="ceo-chat-link"
                            >
                                {clean}
                            </a>
                            <button
                                type="button"
                                title="Copy link"
                                data-testid="ceo-chat-link-copy"
                                onClick={async () => {
                                    try {
                                        await navigator.clipboard.writeText(clean);
                                        toast.success("Link copied");
                                    } catch (_e) {
                                        toast.error("Copy failed");
                                    }
                                }}
                                className="text-zinc-500 hover:text-zinc-200 shrink-0"
                            >
                                <Copy className="w-3 h-3" />
                            </button>
                            {trail}
                        </span>
                    );
                }
                return <span key={idx}>{part}</span>;
            })}
        </span>
    );
}

/** Always-visible link bar at the top of the CEO chat: project board URL
 *  + (when a public token exists) the client share/review URL. Each chip
 *  shows the link as a clickable anchor with a tap-to-copy button so the
 *  user never has to hunt for "where is the share link" mid-conversation. */
function CeoChatLinksBar({ project }) {
    if (!project?.id) return null;
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const boardUrl = `${origin}/projects/${project.id}`;
    const shareUrl = project?.public_token
        ? `${origin}/share/${project.id}/${project.public_token}`
        : "";
    const copy = async (url, label) => {
        try {
            await navigator.clipboard.writeText(url);
            toast.success(`${label} copied`);
        } catch (_e) {
            toast.error("Copy failed");
        }
    };
    return (
        <div
            className="border-b border-zinc-900 bg-[#0a0a0a] px-6 py-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-[11px] font-mono uppercase tracking-wider"
            data-testid="ceo-chat-links-bar"
        >
            <div className="flex items-center gap-1.5 min-w-0">
                <span className="text-zinc-500">Board</span>
                <a
                    href={boardUrl}
                    className="text-[#FF7B40] hover:text-[#FF4400] underline underline-offset-2 truncate max-w-[260px]"
                    data-testid="ceo-chat-board-link"
                >
                    {boardUrl}
                </a>
                <button
                    type="button"
                    onClick={() => copy(boardUrl, "Project board link")}
                    className="text-zinc-500 hover:text-zinc-200"
                    data-testid="ceo-chat-board-copy"
                    title="Copy project board link"
                >
                    <Copy className="w-3 h-3" />
                </button>
            </div>
            {shareUrl ? (
                <div className="flex items-center gap-1.5 min-w-0">
                    <span className="text-zinc-500">Client share</span>
                    <a
                        href={shareUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-emerald-300 hover:text-emerald-200 underline underline-offset-2 truncate max-w-[260px]"
                        data-testid="ceo-chat-share-link"
                    >
                        {shareUrl}
                    </a>
                    <button
                        type="button"
                        onClick={() => copy(shareUrl, "Client share link")}
                        className="text-zinc-500 hover:text-zinc-200"
                        data-testid="ceo-chat-share-copy"
                        title="Copy public review link"
                    >
                        <Copy className="w-3 h-3" />
                    </button>
                </div>
            ) : (
                <span
                    className="text-zinc-600 normal-case"
                    data-testid="ceo-chat-no-share-link"
                >
                    No public client share link yet — enable Sharing in the project header.
                </span>
            )}
        </div>
    );
}

/** Mobile-safe export download button.
 *  - Idle → tap → Preparing → fetch blob → trigger anchor save.
 *  - On mobile, also opens a persistent toast with "Open" / "Share" / "Save"
 *    actions so the user has a visible fallback if Android Chrome / iOS Safari
 *    silently dropped the auto-download.
 */
function DownloadBtn({ path, filename, label, icon: Icon = Download, testid }) {
    const [busy, setBusy] = useState(false);

    const onClick = async () => {
        if (busy) return;
        setBusy(true);
        const prepToastId = toast.loading("Preparing file…", { duration: Infinity });
        try {
            const { blob, blobUrl } = await prepareDownload(path, filename);
            toast.dismiss(prepToastId);
            // 1) Always attempt a direct save first (works on desktop + most Android)
            triggerSave(blobUrl, filename);
            // 2) On mobile, browsers may silently block auto-save. Show a sticky
            //    fallback toast with explicit Open / Share buttons.
            if (isMobile()) {
                showMobileFallbackToast({ blob, blobUrl, filename });
            } else {
                toast.success(`${filename} ready`, { duration: 3500 });
            }
        } catch (err) {
            toast.dismiss(prepToastId);
            toast.error(err?.message || "Download failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <button
            type="button"
            data-testid={testid}
            disabled={busy}
            onClick={onClick}
            className="btn-secondary text-xs px-3 py-1.5 disabled:opacity-50"
        >
            <Icon className="w-3.5 h-3.5" />
            {busy ? "Preparing…" : label}
        </button>
    );
}

/** Persistent mobile-only toast offering "Open" / "Share" / "Save" fallbacks. */
let _activeFallbackToastId = null;
function showMobileFallbackToast({ blob, blobUrl, filename }) {
    // Dismiss any previous fallback toast so they don't stack on rapid taps
    if (_activeFallbackToastId !== null) {
        toast.dismiss(_activeFallbackToastId);
    }
    const id = toast.message(
        <MobileDownloadFallback
            blob={blob}
            blobUrl={blobUrl}
            filename={filename}
            onClose={() => {
                toast.dismiss(id);
                if (_activeFallbackToastId === id) _activeFallbackToastId = null;
            }}
        />,
        { duration: 30_000, dismissible: true },
    );
    _activeFallbackToastId = id;
}

function MobileDownloadFallback({ blob, blobUrl, filename, onClose }) {
    const [sharing, setSharing] = useState(false);
    const onShare = async () => {
        if (sharing) return;
        setSharing(true);
        const ok = await shareFile(blob, filename);
        setSharing(false);
        if (ok) onClose && onClose();
        else toast.error("Sharing not supported on this device. Use Open instead.");
    };
    const onOpen = () => {
        openInNewTab(blobUrl);
        // Don't auto-close — user may come back to share too
    };
    const onSave = () => {
        triggerSave(blobUrl, filename);
    };
    return (
        <div className="flex flex-col gap-2 w-full" data-testid="mobile-download-fallback">
            <div className="text-xs font-mono uppercase tracking-wider text-zinc-400">
                {filename}
            </div>
            <div className="text-[11px] text-zinc-500">
                If the file didn't save automatically, tap an option below.
            </div>
            <div className="flex flex-wrap gap-2 mt-1">
                <button
                    type="button"
                    onClick={onOpen}
                    data-testid="mobile-dl-open"
                    className="btn-primary text-[11px] px-3 py-1.5"
                >
                    <ExternalLink className="w-3 h-3" /> Open
                </button>
                <button
                    type="button"
                    onClick={onSave}
                    data-testid="mobile-dl-save"
                    className="btn-secondary text-[11px] px-3 py-1.5"
                >
                    <Download className="w-3 h-3" /> Save
                </button>
                <button
                    type="button"
                    onClick={onShare}
                    disabled={sharing}
                    data-testid="mobile-dl-share"
                    className="btn-secondary text-[11px] px-3 py-1.5 disabled:opacity-50"
                >
                    <Share2 className="w-3 h-3" /> {sharing ? "Sharing…" : "Share"}
                </button>
            </div>
            {/* Last-resort: visible anchor users can long-press to "Save link as…" without losing the page */}
            <a
                href={blobUrl}
                target="_blank"
                rel="noopener noreferrer"
                download={filename}
                data-testid="mobile-dl-link"
                className="text-[10px] uppercase tracking-wider font-mono text-zinc-500 underline hover:text-[#FF4400] mt-1"
            >
                or tap here / long-press to save
            </a>
        </div>
    );
}

const TABS = [
    { key: "ceo", label: "Assistant Chat" },
    { key: "tasks", label: "Agent Tasks" },
    { key: "strategy", label: "Strategy Lock" },
    { key: "competitor", label: "Competitor" },
    { key: "brand_dna", label: "Brand DNA" },
    { key: "research", label: "Research" },
    { key: "business_plan", label: "Business Plan" },
    { key: "concepts", label: "Concepts" },
    { key: "script", label: "Script" },
    { key: "storyboard", label: "Storyboard" },
    // iter66 — User-facing rename: Asset Refinement → Reference Lock.
    // Backend key/route unchanged ("assets") so all existing project
    // data + integrations keep working.
    { key: "assets", label: "Reference Lock" },
    { key: "presentation", label: "Presentation" },
    // iter66 — Video Workflow relabelled "Sequence Planning (Advanced)" to
    // reduce confusion with Video Studio (which is the real generation
    // surface). Backend key unchanged.
    { key: "video_workflow", label: "Sequence Planning (Advanced)" },
    { key: "influencers", label: "Influencers" },
    { key: "distribution", label: "Distribution" },
    { key: "social_strategy", label: "Social Strategy" },
    // iter66 — Video Prompts + Prompt Library merged under the user-facing
    // "Prompt Pack" name. Both tabs still exist (separate panels still
    // serve their purposes) but the labels emphasise the canonical pack.
    { key: "video_prompts", label: "Prompt Pack" },
    { key: "prompts", label: "Prompt Pack · Library" },
    // V1-A — Video Studio tab. Renders only when project.video_studio_enabled
    // is true (gating is enforced inside VideoStudioPanel; the tab visibility
    // is decided at TabBar render time).
    { key: "video_studio", label: "Video Studio" },
    { key: "qc", label: "QC Review" },
    { key: "post", label: "Post-Production" },
    { key: "delivery", label: "Final Delivery" },
    { key: "share", label: "Share / Review Link" },
];

export default function ProjectBoard() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [project, setProject] = useState(null);
    const [client, setClient] = useState(null);
    const [tab, setTab] = useState("ceo");
    const [pendingPrompt, setPendingPrompt] = useState(null);
    // UX Sprint 1 — query-param flag `?ux=legacy` reverts to the old layout.
    const [searchParams] = useSearchParams();
    const isLegacy = (searchParams.get("ux") || "").toLowerCase() === "legacy";
    const initialTabFromUrl = searchParams.get("tab");
    // Drawer state for the new layout.
    const [assistantOpen, setAssistantOpen] = useState(false);
    const [moreToolsOpen, setMoreToolsOpen] = useState(false);
    // Track which user-stage is currently selected on the new timeline.
    // Defaults to whichever USER_STAGE contains the current tab (so a deep
    // link like `?tab=storyboard` lands on the right tile).
    const [activeUserStageKey, setActiveUserStageKey] = useState(() => {
        const us = userStageForTab(initialTabFromUrl || tab);
        return us ? us.key : "brief";
    });

    /** Triggered by CEO chip clicks: switches to the CEO tab and (optionally)
     *  injects the chip's prompt so CeoChatPanel auto-sends it. Passing null
     *  just opens the tab (used by the "Open chat →" link). */
    const askCeo = (prompt) => {
        setTab("ceo");
        if (prompt) setPendingPrompt({ text: prompt, ts: Date.now() });
    };

    /** UX Sprint 1 — when the user clicks a tile on the new timeline,
     *  flip both the user-facing stage AND the underlying legacy tab to
     *  the tile's primaryTab. Every existing tab panel keeps rendering. */
    const handleStageSelect = (userStage) => {
        if (!userStage) return;
        setActiveUserStageKey(userStage.key);
        setTab(userStage.primaryTab);
    };

    /** Sub-tab selection inside a user-stage. Pure setter — same
     *  `tab` state the legacy tab bar drives. */
    const handleSubTabSelect = (tabKey) => {
        if (!tabKey) return;
        setTab(tabKey);
    };

    /** UX Sprint 2 — Stage hero primary-action dispatcher.
     *  `primary` is the `STAGE_META[user_stage].primary` object:
     *    - kind:"tab"    → route to the given tab key (existing panel)
     *    - kind:"scroll" → smooth-scroll to a DOM testid (e.g. auto-start-btn)
     *  Pure UX wiring; no new backend calls. */
    const handleStagePrimaryAction = (primary) => {
        if (!primary) return;
        if (primary.kind === "tab") {
            setTab(primary.target);
            return;
        }
        if (primary.kind === "scroll") {
            const el = document.querySelector(`[data-testid="${primary.target}"]`);
            if (el) {
                el.scrollIntoView({ behavior: "smooth", block: "center" });
                el.classList.add("ring-2", "ring-[#FF4400]");
                setTimeout(() => el.classList.remove("ring-2", "ring-[#FF4400]"), 1800);
            }
        }
    };

    const reload = async () => {
        const { data } = await api.get(`/projects/${id}`);
        setProject(data);
        const c = await api.get(`/clients/${data.client_id}`);
        setClient(c.data);
    };

    useEffect(() => { reload(); /* eslint-disable-next-line */ }, [id]);

    /** UX Sprint 1 — honor `?tab=X` deep links on mount/URL change.
     *  Deep-link preservation: every legacy tab key remains valid. */
    useEffect(() => {
        if (!initialTabFromUrl) return;
        setTab(initialTabFromUrl);
        const us = userStageForTab(initialTabFromUrl);
        if (us) setActiveUserStageKey(us.key);
    }, [initialTabFromUrl]);

    /** UX Sprint 1 — when `tab` flips from anywhere (CEO chip, sub-tab,
     *  More Tools entry, deep link), pull the activeUserStageKey along
     *  so the timeline tile highlight stays accurate. */
    useEffect(() => {
        const us = userStageForTab(tab);
        if (us && us.key !== activeUserStageKey) setActiveUserStageKey(us.key);
    }, [tab, activeUserStageKey]);

    // Phase H — cross-device state sync. Polling + `visibilitychange` +
    // `focus` make the backend the single source of truth for mode /
    // auto_pipeline / stage status, so desktop↔mobile always converge
    // without a WebSocket. Poll every 15s while the tab is visible; also
    // re-sync immediately when the user returns to the tab.
    useEffect(() => {
        if (!id) return;
        const silent = () => {
            api.get(`/projects/${id}`).then(({ data }) => {
                setProject(prev => {
                    if (!prev) return data;
                    // Keep prev if nothing meaningful changed — avoids
                    // clobbering in-flight optimistic UI (stage-edit drafts).
                    const samePipeline = JSON.stringify(prev.auto_pipeline || {})
                        === JSON.stringify(data.auto_pipeline || {});
                    const sameStage = prev.current_stage === data.current_stage;
                    const sameMode = prev.execution_mode === data.execution_mode;
                    const sameUpdated = prev.updated_at === data.updated_at;
                    if (samePipeline && sameStage && sameMode && sameUpdated) return prev;
                    return data;
                });
            }).catch(() => {});
        };
        const t = setInterval(() => {
            if (document.visibilityState === "visible") silent();
        }, 15000);
        const onVis = () => { if (document.visibilityState === "visible") silent(); };
        window.addEventListener("visibilitychange", onVis);
        window.addEventListener("focus", silent);
        return () => {
            clearInterval(t);
            window.removeEventListener("visibilitychange", onVis);
            window.removeEventListener("focus", silent);
        };
    }, [id]);

    if (!project) return <div className="p-10 text-zinc-500">Loading project…</div>;

    // NATURAL PAGE SCROLL EVERYWHERE — no viewport locks. The page grows
    // with its content and the browser scrollbar handles it. The CEO Chat
    // tab uses a BOUNDED message list (max-h) for its own scroll, but does
    // not lock the outer container.

    return (
        <div className="flex flex-col min-h-[calc(100vh-3.5rem)]" data-testid="project-board-root" data-ux-mode={isLegacy ? "legacy" : "v2"}>
            {/* UX Sprint 1 — NEW premium layout. Renders by default. */}
            {!isLegacy && (
                <>
                    <ProjectHeader
                        project={project}
                        client={client}
                        currentStageLabel={
                            project.current_stage === "business_plan" && project.campaign_inputs_ready === false
                                ? "Campaign Strategy Inputs"
                                : (STAGES.find((s) => s.key === project.current_stage)?.label || project.current_stage)
                        }
                        onOpenAssistant={() => setAssistantOpen(true)}
                        onOpenMoreTools={() => setMoreToolsOpen(true)}
                        extraControls={
                            <>
                                <ModeToggle project={project} onChange={reload} />
                                <CostPill projectId={project.id} />
                                <DownloadBtn
                                    testid="export-report"
                                    path={`/exports/${project.id}/report.pdf`}
                                    filename={`report-${project.id}.pdf`}
                                    label="Project Report PDF"
                                    icon={FileText}
                                />
                                <ArchiveProjectButton project={project} reload={reload} navigate={navigate} />
                            </>
                        }
                    />

                    {/* Status banners + pipeline controls — kept intact, just rendered below the clean header. */}
                    <div className="px-6 lg:px-10 pt-2 pb-0 bg-[#0a0a0a] border-b border-zinc-900">
                        {/* Sprint E — Discovery Wizard / Readiness UX. Soft guidance only. */}
                        <DiscoveryWizard projectId={project.id} />
                        {project.is_archived && (
                            <div
                                data-testid="project-archived-banner"
                                className="mb-3 border border-amber-500/30 bg-amber-500/5 rounded-sm px-4 py-3 flex items-center justify-between gap-3"
                            >
                                <div className="flex items-center gap-3 text-sm text-amber-200">
                                    <Archive className="w-4 h-4" />
                                    <span>
                                        This project is archived
                                        {project.archived_at ? ` on ${new Date(project.archived_at).toLocaleString()}` : ""}.
                                        It is hidden from the active dashboard. Outputs, versions, and approvals are preserved.
                                    </span>
                                </div>
                            </div>
                        )}
                        <AutoPipelineBar project={project} reload={reload} hidePhaseChips={true} />
                        <PipelineStateConsistencyBanner project={project} reload={reload} />
                        <GenieProgressBanner project={project} />
                        {(() => {
                            const cur = project.current_stage;
                            const s = project.stages?.[cur] || {};
                            const active = s.status === "processing";
                            return active ? (
                                <div className="mt-3 flex items-center gap-2 text-xs font-mono text-amber-400 uppercase tracking-wider" data-testid="active-agent-banner">
                                    <span className="w-2 h-2 rounded-full bg-amber-400 pulse-dot" />
                                    Agent running: {STAGES.find(x => x.key === cur)?.label || cur}
                                </div>
                            ) : null;
                        })()}

                        {/* Single user-facing workflow timeline. */}
                        <WorkflowTimeline
                            project={project}
                            activeKey={activeUserStageKey}
                            onSelect={handleStageSelect}
                        />
                    </div>

                    {/* Sub-tab pills for the active user-stage. */}
                    <StageSubTabs
                        userStageKey={activeUserStageKey}
                        activeTab={tab}
                        onSelect={handleSubTabSelect}
                    />

                    {/* UX Sprint 2 — Stage workspace shell (hero + chips + hint). */}
                    <StageWorkspaceShell
                        userStageKey={activeUserStageKey}
                        project={project}
                        onPrimaryAction={handleStagePrimaryAction}
                        onOpenAssistant={() => setAssistantOpen(true)}
                        onOpenMoreTools={() => setMoreToolsOpen(true)}
                    />
                </>
            )}

            {/* LEGACY layout — reachable via `?ux=legacy` for instant rollback / comparison. */}
            {isLegacy && (
                <>
                    {/* Header */}
                    <div className="px-6 lg:px-10 py-6 border-b border-zinc-900 bg-[#0a0a0a]">
                        <div className="flex items-start justify-between gap-6 flex-wrap">
                            <div className="min-w-0">
                                <div className="flex items-center gap-2 text-xs font-mono text-zinc-500 uppercase tracking-wider mb-2">
                                    <Link to={`/clients/${client?.id}`} className="hover:text-white">{client?.company_name || "client"}</Link>
                                    <ChevronRight className="w-3 h-3" />
                                    <span className="text-zinc-700">{(project.project_type || "project").replaceAll("_"," ")}</span>
                                </div>
                                <h1 className="font-display text-3xl sm:text-4xl font-medium tracking-tight">{project.title}</h1>
                                <p className="text-sm text-zinc-500 mt-2 max-w-3xl line-clamp-2">{project.brief}</p>
                            </div>
                            <div className="flex items-center gap-2 flex-wrap">
                                <ModeToggle project={project} onChange={reload} />
                                <CostPill projectId={project.id} />
                                <DownloadBtn
                                    testid="export-report"
                                    path={`/exports/${project.id}/report.pdf`}
                                    filename={`report-${project.id}.pdf`}
                                    label="Project Report PDF"
                                    icon={FileText}
                                />
                                <ArchiveProjectButton project={project} reload={reload} navigate={navigate} />
                            </div>
                        </div>

                        {/* Phase N.6 — Archived banner. */}
                        {project.is_archived && (
                            <div
                                data-testid="project-archived-banner"
                                className="mt-4 border border-amber-500/30 bg-amber-500/5 rounded-sm px-4 py-3 flex items-center justify-between gap-3"
                            >
                                <div className="flex items-center gap-3 text-sm text-amber-200">
                                    <Archive className="w-4 h-4" />
                                    <span>
                                        This project is archived
                                        {project.archived_at ? ` on ${new Date(project.archived_at).toLocaleString()}` : ""}.
                                        It is hidden from the active dashboard. Outputs, versions, and approvals are preserved.
                                    </span>
                                </div>
                            </div>
                        )}

                        {/* Auto pipeline status / approval bar */}
                        <AutoPipelineBar project={project} reload={reload} />

                        {/* Phase N.5 — State-corruption banner. */}
                        <PipelineStateConsistencyBanner project={project} reload={reload} />

                        {/* Genie progress banner (Phase N). */}
                        <GenieProgressBanner project={project} />

                        {/* CEO embedded greeting + quick suggestions (Phase A) */}
                        <CeoEmbeddedPanel project={project} onAskCeo={askCeo} />

                        {/* Active stage banner */}
                        {(() => {
                            const cur = project.current_stage;
                            const s = project.stages?.[cur] || {};
                            const active = s.status === "processing";
                            return active ? (
                                <div className="mt-4 flex items-center gap-2 text-xs font-mono text-amber-400 uppercase tracking-wider" data-testid="active-agent-banner">
                                    <span className="w-2 h-2 rounded-full bg-amber-400 pulse-dot" />
                                    Agent running: {STAGES.find(x => x.key === cur)?.label || cur}
                                </div>
                            ) : null;
                        })()}

                        {/* Stage timeline */}
                        <div className="mt-6 -mx-1 overflow-x-auto scrollbar-thin">
                            <div className="flex gap-2 px-1 min-w-max">
                                {STAGES.map((st, idx) => {
                                    const status = project.stages?.[st.key]?.status;
                                    const isCurrent = project.current_stage === st.key;
                                    return (
                                        <div key={st.key} className={`px-3 py-2 border rounded-sm text-xs font-mono uppercase tracking-wider flex items-center gap-2 ${isCurrent ? "border-white text-white bg-zinc-900" : status === "approved" ? "border-emerald-500/30 text-emerald-400" : "border-zinc-900 text-zinc-500"}`}>
                                            <span className="text-zinc-700">{String(idx).padStart(2, "0")}</span>
                                            <span>{st.label}</span>
                                            {status === "approved" && <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    {/* Legacy 24-tab bar. */}
                    <div className="border-b border-zinc-900 bg-[#080808]">
                        <div className="px-4 lg:px-8 overflow-x-auto scrollbar-thin">
                            <div className="flex gap-1 min-w-max">
                                {TABS.map(t => (
                                    <button
                                        key={t.key}
                                        data-testid={`tab-${t.key}`}
                                        onClick={() => setTab(t.key)}
                                        className={`px-4 py-3 text-sm border-b-2 transition-colors ${tab === t.key ? "border-[#FF4400] text-white" : "border-transparent text-zinc-500 hover:text-white"}`}
                                    >
                                        {t.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* Content — natural block flow. CEO Chat panel handles its own bounded message scroll internally. */}
            <div className="flex-1">
                {/* UX Sprint 2 — synthetic overview tabs for composite stages.
                    Rendered only in normal UX mode. Reuse existing panels via onOpenTab. */}
                {!isLegacy && tab === "_campaign_overview" && (
                    <CampaignStrategyWorkspace project={project} onOpenTab={setTab} />
                )}
                {!isLegacy && tab === "_visual_bible_overview" && (
                    <VisualBibleWorkspace project={project} onOpenTab={setTab} />
                )}
                {!isLegacy && tab === "_delivery_overview" && (
                    <PresentationDeliveryWorkspace project={project} onOpenTab={setTab} />
                )}

                {tab === "ceo" && <CeoChatPanel project={project} client={client} reload={reload} pendingPrompt={pendingPrompt} onPromptConsumed={() => setPendingPrompt(null)} />}
                {tab === "tasks" && <TasksPanel project={project} reload={reload} />}
                {tab === "strategy" && <StrategyPanel project={project} reload={reload} />}
                {tab === "competitor" && <CompetitorPanel project={project} reload={reload} />}
                {tab === "brand_dna" && <BrandDnaPanel project={project} reload={reload} />}
                {tab === "business_plan" && <BusinessPlanPanel project={project} reload={reload} />}
                {tab === "research" && <ResearchPanel project={project} reload={reload} />}
                {tab === "concepts" && <ConceptsPanel project={project} reload={reload} />}
                {tab === "script" && <ScriptPanel project={project} reload={reload} />}
                {tab === "storyboard" && <StoryboardPanel project={project} reload={reload} />}
                {tab === "prompts" && <PromptsPanel project={project} reload={reload} />}
                {tab === "video_studio" && <VideoStudioPanel project={project} reload={reload} />}
                {tab === "presentation" && <PresentationPanel project={project} reload={reload} />}
                {tab === "video_workflow" && <VideoWorkflowPanel project={project} />}
                {tab === "influencers" && <InfluencersPanel project={project} reload={reload} />}
                {tab === "distribution" && <DistributionPanel project={project} client={client} />}
                {tab === "social_strategy" && <SocialStrategyTab project={project} client={client} />}
                {tab === "video_prompts" && <VideoPromptsTab project={project} client={client} />}
                {tab === "assets" && <AssetsPanel project={project} reload={reload} />}
                {tab === "qc" && <QcPanel project={project} reload={reload} />}
                {tab === "post" && <PostPanel project={project} reload={reload} />}
                {tab === "delivery" && <DeliveryPanel project={project} reload={reload} />}
                {tab === "share" && <SharePanel project={project} reload={reload} />}
            </div>

            {/* UX Sprint 1 — global drawers. Hidden until user opens. */}
            {!isLegacy && (
                <>
                    <ProjectAssistantDrawer
                        open={assistantOpen}
                        onClose={() => setAssistantOpen(false)}
                        project={project}
                        onAskAssistant={askCeo}
                        onOpenMoreTools={() => setMoreToolsOpen(true)}
                    />
                    <MoreToolsDrawer
                        open={moreToolsOpen}
                        onClose={() => setMoreToolsOpen(false)}
                        onPickTab={(k) => setTab(k)}
                    />
                </>
            )}
        </div>
    );
}

/* ============================ CEO CHAT ============================ */
function CeoChatPanel({ project, reload, pendingPrompt, onPromptConsumed }) {
    const [messages, setMessages] = useState([]);
    const [text, setText] = useState("");
    const [busy, setBusy] = useState(false);
    const [chatMode, setChatMode] = useState("chat");  // "chat" | "plan"
    const scrollRef = useRef(null);

    const load = async () => {
        const { data } = await api.get(`/projects/${project.id}/chat`);
        setMessages(data);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [project.id]);
    useEffect(() => { scrollRef.current?.scrollTo({ top: 1e9 }); }, [messages]);

    const send = async (override) => {
        // Phase I — defensive coercion. `override` can arrive as an object
        // from chip callbacks or from CEO auto-inject (asset-link array).
        // Ensure we always pass a string to the API so .trim() crashes
        // never reach the user.
        // P3 / M11 — also guarantee we NEVER persist the literal
        // "[object Object]" string. If JSON.stringify falls back through
        // a TypeError (circular refs / SyntheticEvent / DOM node) we
        // refuse the send and toast a friendly error.
        const raw = override ?? text;
        let t = "";
        if (typeof raw === "string") {
            t = raw.trim();
        } else if (Array.isArray(raw)) {
            t = raw.filter(Boolean).map(String).join("\n").trim();
        } else if (raw && typeof raw === "object") {
            try {
                t = JSON.stringify(raw);
            } catch {
                // Last-resort: pull a readable field; refuse if nothing useful.
                t = raw?.text || raw?.message || raw?.value || "";
            }
        } else if (raw != null) {
            t = String(raw).trim();
        }
        // P3 / M11 — final guard: never let the literal coerced object
        // marker reach the backend or the chat history.
        if (t === "[object Object]") t = "";
        if (!t) return;
        setBusy(true);
        if (override === undefined) setText("");
        setMessages(m => [...m, { id: "tmp", role: "user", text: t, timestamp: new Date().toISOString() }]);
        try {
            if (chatMode === "plan" && override === undefined) {
                const { data } = await api.post(`/agents/plan`, { project_id: project.id, user_text: t });
                setMessages(m => m.filter(x => x.id !== "tmp").concat([
                    { id: "u" + Date.now(), role: "user", text: t, timestamp: new Date().toISOString() },
                    data.message,
                ]));
                toast.success("Plan ready — review steps below.");
            } else {
                const { data } = await api.post(`/projects/${project.id}/chat`, { project_id: project.id, text: t });
                setMessages(m => m.filter(x => x.id !== "tmp").concat([data.user, data.agent]));
            }
        } catch (e) {
            toast.error(e?.userMessage || e?.response?.data?.detail || "Failed");
            setMessages(m => m.filter(x => x.id !== "tmp"));
        } finally { setBusy(false); }
    };

    /** Auto-send when a CEO chip injects a prompt. Always uses chat mode
     *  (skips plan-mode parsing) so the user gets a direct CEO reply. */
    useEffect(() => {
        if (!pendingPrompt?.text) return;
        send(pendingPrompt.text);
        onPromptConsumed && onPromptConsumed();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingPrompt?.ts]);

    const runPlan = async (plan) => {
        const agents = (plan.plan || []).map(p => p.agent);
        if (!agents.length) { toast.error("Plan has no agents."); return; }
        setBusy(true);
        toast.message(`Running ${agents.length} agents in AUTO mode…`, { duration: 4000 });
        try {
            const { data } = await api.post("/agents/run-plan", { project_id: project.id, plan: agents, mode: "auto" });
            const done = data.results.filter(r => r.status === "completed").length;
            const failed = data.results.filter(r => r.status === "failed").length;
            toast[failed ? "error" : "success"](`AUTO run: ${done} done${failed ? `, ${failed} failed` : ""}.`);
            reload && reload();
            load();
        } catch (e) {
            toast.error(e?.userMessage || "AUTO run failed.");
        } finally { setBusy(false); }
    };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-0" data-testid="ceo-chat-grid">
            <div className="flex flex-col bg-[#070707]" data-testid="ceo-chat">
                <CeoChatLinksBar project={project} />
                <div ref={scrollRef} className="max-h-[65vh] min-h-[320px] overflow-y-auto scrollbar-thin px-6 py-6 space-y-4">
                    {messages.length === 0 && (
                        <div className="text-zinc-500 text-sm">Project Assistant is ready. Ask for a status update or say "kick off Brand Analyst".</div>
                    )}
                    {messages.map(m => (
                        <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                            <div className={`max-w-[85%] p-4 rounded-sm text-sm whitespace-pre-wrap leading-relaxed ${m.role === "user" ? "bg-white text-black" : "bg-[#141414] border border-zinc-800 text-zinc-100"}`}>
                                {m.role !== "user" && (
                                    <div className="label-micro mb-2 text-[#FF4400]">Project Assistant</div>
                                )}
                                <div><LinkifiedText value={m.text} /></div>
                                {m.plan && <PlanCard plan={m.plan} onRun={runPlan} />}
                                {m.role !== "user" && (
                                    <CeoActionButtons message={m} project={project} reload={reload} />
                                )}
                            </div>
                        </div>
                    ))}
                    {busy && <div className="text-zinc-500 text-xs font-mono uppercase tracking-wider">Assistant is thinking…</div>}
                </div>
                <div className="border-t border-zinc-900 p-4 space-y-2">
                    <div className="flex items-center gap-2">
                        <div className="inline-flex border border-zinc-800 rounded-sm overflow-hidden" data-testid="chat-mode-toggle">
                            {["chat", "plan"].map(m => (
                                <button
                                    key={m}
                                    data-testid={`chat-mode-${m}`}
                                    onClick={() => setChatMode(m)}
                                    className={`px-3 py-1.5 text-[11px] font-mono uppercase tracking-wider ${chatMode === m ? "bg-[#FF4400] text-white" : "text-zinc-400"}`}
                                >
                                    {m === "chat" ? "Chat" : "Plan"}
                                </button>
                            ))}
                        </div>
                        <span className="text-[10px] font-mono text-zinc-600">
                            {chatMode === "plan" ? "Describe the task — Project Assistant picks the agents." : "General Q&A with the Project Assistant."}
                        </span>
                    </div>
                    <div className="flex gap-2">
                        <input
                            data-testid="ceo-input"
                            value={text} onChange={e => setText(e.target.value)}
                            onKeyDown={e => e.key === "Enter" && !e.shiftKey && send()}
                            placeholder={chatMode === "plan" ? 'e.g. "animate our logo" or "launch full campaign"' : "Brief the Project Assistant…"}
                            className="flex-1 bg-black border border-zinc-800 rounded-sm px-3 py-2.5 focus:border-zinc-500 outline-none"
                        />
                        <button data-testid="ceo-send" onClick={send} disabled={busy} className="btn-primary px-4">
                            <Send className="w-4 h-4" /> Send
                        </button>
                    </div>
                </div>
            </div>
            <aside className="hidden lg:block border-l border-zinc-900 bg-[#0a0a0a] p-5">
                <div className="label-micro mb-3">Quick actions</div>
                <div className="space-y-2">
                    {(() => {
                        const _g = (agent) => isActionAllowed(project, "run", { agent });
                        const ga = _g("brand_analyst");
                        const gb = _g("business_plan");
                        const gr = _g("research");
                        const gc = _g("creative_director");
                        return (
                            <>
                                <RunAgentButton project={project} agent="brand_analyst" label="Run Brand Analyst" onDone={reload} disabled={!ga.ok} />
                                {!ga.ok && <div className="text-[10px] font-mono text-amber-400/80">{ga.reason}</div>}
                                <div />
                                <RunAgentButton project={project} agent="business_plan" label="Run Business Plan" onDone={reload} disabled={!gb.ok} />
                                {!gb.ok && <div className="text-[10px] font-mono text-amber-400/80">{gb.reason}</div>}
                                <div />
                                <RunAgentButton project={project} agent="research" label="Run Research" extra={{ notes: "" }} onDone={reload} disabled={!gr.ok} />
                                {!gr.ok && <div className="text-[10px] font-mono text-amber-400/80">{gr.reason}</div>}
                                <div />
                                <RunAgentButton project={project} agent="creative_director" label="Run Concepts" onDone={reload} disabled={!gc.ok} />
                                {!gc.ok && <div className="text-[10px] font-mono text-amber-400/80">{gc.reason}</div>}
                            </>
                        );
                    })()}
                </div>
                <div className="label-micro mt-6 mb-3">Status</div>
                <div className="space-y-2">
                    {STAGES.map(st => (
                        <div key={st.key} className="flex items-center justify-between text-xs">
                            <span className="text-zinc-300">{st.label}</span>
                            <span className={`px-2 py-0.5 border rounded-sm font-mono uppercase ${stageStatusColor(project.stages?.[st.key]?.status)}`}>
                                {(project.stages?.[st.key]?.status || "—").replaceAll("_"," ")}
                            </span>
                        </div>
                    ))}
                </div>
            </aside>
        </div>
    );
}

/* ============================ AGENT TASK BOARD ============================ */
function TasksPanel({ project, reload }) {
    const cols = ["pending", "in_progress", "approved", "rejected"];
    const groups = useMemo(() => {
        const g = { pending: [], in_progress: [], approved: [], rejected: [], other: [] };
        STAGES.forEach(st => {
            const s = project.stages?.[st.key];
            if (!s) return;
            const k = cols.includes(s.status) ? s.status : "other";
            g[k].push({ ...st, ...s });
        });
        return g;
    // eslint-disable-next-line
    }, [project]);
    return (
        <div className="p-6 lg:p-10" data-testid="tasks-panel">
            <h2 className="font-display text-2xl mb-6">Agent Task Board</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {cols.map(col => (
                    <div key={col} className="card-base p-4">
                        <div className="label-micro mb-3">{col.replaceAll("_"," ")}</div>
                        <div className="space-y-2">
                            {(groups[col] || []).map(item => (
                                <div key={item.key} className="border border-zinc-900 p-3 rounded-sm">
                                    <div className="font-medium text-sm">{item.label}</div>
                                    <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono mt-1">{item.key}</div>
                                </div>
                            ))}
                            {(groups[col] || []).length === 0 && (
                                <div className="text-xs text-zinc-600">Empty</div>
                            )}
                        </div>
                    </div>
                ))}
            </div>
            <div className="mt-8">
                <div className="label-micro mb-3">Run agents</div>
                <div className="flex flex-wrap gap-2">
                    {AGENTS.filter(a => a.stage).map(a => (
                        <RunAgentButton key={a.key} project={project} agent={a.key} label={`Run ${a.label}`} onDone={reload} />
                    ))}
                </div>
            </div>
        </div>
    );
}

/* ============================ STRATEGY LOCK PANEL ============================ */
const BUSINESS_MODELS = [
    { value: "", label: "—" },
    { value: "enterprise_first", label: "Enterprise-first" },
    { value: "talent_first", label: "Talent-first" },
    { value: "hybrid", label: "Hybrid" },
];
const NARRATIVE_TYPES = [
    { value: "", label: "—" },
    { value: "ecosystem", label: "Ecosystem" },
    { value: "marketplace", label: "Marketplace" },
    { value: "infrastructure", label: "Infrastructure" },
];

function StrategyPanel({ project, reload }) {
    const sc = project.strategy_context || {};
    const [model, setModel] = useState(sc.business_model || "");
    const [revenue, setRevenue] = useState(sc.revenue_priority || "");
    const [diffs, setDiffs] = useState(sc.differentiators || []);
    const [narrative, setNarrative] = useState(sc.narrative_type || "");
    const [locked, setLocked] = useState(!!sc.locked);
    const [draft, setDraft] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        const sc2 = project.strategy_context || {};
        setModel(sc2.business_model || "");
        setRevenue(sc2.revenue_priority || "");
        setDiffs(sc2.differentiators || []);
        setNarrative(sc2.narrative_type || "");
        setLocked(!!sc2.locked);
    }, [project.id, project.strategy_context]);

    const addDiff = () => {
        const v = draft.trim();
        if (!v) return;
        if (diffs.includes(v)) { setDraft(""); return; }
        setDiffs([...diffs, v]);
        setDraft("");
    };
    const removeDiff = (v) => setDiffs(diffs.filter(x => x !== v));

    const save = async () => {
        setBusy(true);
        try {
            await api.patch(`/projects/${project.id}/strategy`, {
                business_model: model,
                revenue_priority: revenue,
                differentiators: diffs,
                narrative_type: narrative,
                locked,
            });
            toast.success(locked ? "Strategy locked — all agents will follow it." : "Strategy saved.");
            reload && reload();
        } catch (err) {
            toast.error(err?.userMessage || "Save failed");
        } finally { setBusy(false); }
    };

    return (
        <div className="p-6 lg:p-10" data-testid="strategy-panel">
            <PanelHeader title="Strategy Lock" actions={[]} />
            <p className="text-sm text-zinc-500 mb-5">
                Define the strategic anchors every agent must respect. When <span className="text-[#FF4400] font-mono">Lock Strategy</span> is on, agents are required to honor these constraints — no drift into generic messaging.
            </p>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <div className="card-base p-5">
                    <div className="label-micro mb-2">Business model</div>
                    <select data-testid="strategy-business-model"
                            value={model} onChange={e => setModel(e.target.value)}
                            className="w-full bg-black border border-zinc-800 rounded-sm px-3 py-2 text-sm">
                        {BUSINESS_MODELS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                </div>
                <div className="card-base p-5">
                    <div className="label-micro mb-2">Revenue priority</div>
                    <input data-testid="strategy-revenue"
                           value={revenue} onChange={e => setRevenue(e.target.value)}
                           placeholder="e.g. Enterprise hiring + verified profiles"
                           className="w-full bg-black border border-zinc-800 rounded-sm px-3 py-2 text-sm" />
                </div>
                <div className="card-base p-5">
                    <div className="label-micro mb-2">Narrative type</div>
                    <select data-testid="strategy-narrative"
                            value={narrative} onChange={e => setNarrative(e.target.value)}
                            className="w-full bg-black border border-zinc-800 rounded-sm px-3 py-2 text-sm">
                        {NARRATIVE_TYPES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                </div>
                <div className="card-base p-5">
                    <div className="label-micro mb-2">Lock strategy for all outputs</div>
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" data-testid="strategy-lock"
                               checked={locked} onChange={e => setLocked(e.target.checked)}
                               className="w-4 h-4 accent-[#FF4400]" />
                        <span className="text-sm text-zinc-300">
                            {locked ? "ON — agents must follow this context" : "OFF — used as soft guidance"}
                        </span>
                    </label>
                </div>
                <div className="card-base p-5 lg:col-span-2">
                    <div className="label-micro mb-2">Differentiators (chips — what makes this brand uniquely win)</div>
                    <div className="flex flex-wrap gap-2 mb-3" data-testid="strategy-diffs">
                        {diffs.length === 0 && <span className="text-xs text-zinc-600">No differentiators yet.</span>}
                        {diffs.map(d => (
                            <span key={d} className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider font-mono px-2 py-1 rounded-sm border border-[#FF4400] bg-[#FF4400]/15 text-[#FF4400]">
                                {d}
                                <button onClick={() => removeDiff(d)} data-testid={`strategy-diff-remove-${d.toLowerCase().replace(/\s+/g,'-')}`}
                                        className="hover:text-white">
                                    <X className="w-3 h-3" />
                                </button>
                            </span>
                        ))}
                    </div>
                    <div className="flex gap-2">
                        <input value={draft} onChange={e => setDraft(e.target.value)}
                               onKeyDown={e => e.key === "Enter" && (e.preventDefault(), addDiff())}
                               placeholder="e.g. Verified profiles, Project boards, Enterprise hiring"
                               data-testid="strategy-diff-input"
                               className="flex-1 bg-black border border-zinc-800 rounded-sm px-3 py-2 text-sm" />
                        <button type="button" onClick={addDiff} data-testid="strategy-diff-add"
                                className="btn-secondary text-xs px-3 py-2">Add</button>
                    </div>
                </div>
            </div>

            <div className="mt-5 flex items-center gap-3">
                <button type="button" onClick={save} disabled={busy} data-testid="strategy-save"
                        className="btn-primary text-xs px-4 py-2 disabled:opacity-50">
                    <Sparkles className="w-3.5 h-3.5" />
                    {busy ? "Saving…" : (locked ? "Save & Lock" : "Save Strategy")}
                </button>
                {sc.updated_at && (
                    <span className="text-[10px] uppercase tracking-wider font-mono text-zinc-600">
                        Updated {new Date(sc.updated_at).toLocaleString()}
                    </span>
                )}
            </div>
        </div>
    );
}

/* ============================ COMPETITOR ANALYST PANEL ============================ */
const TIER_BADGE = {
    1: { label: "TIER 1 · LOCAL", cls: "border-[#FF4400] bg-[#FF4400]/15 text-[#FF4400]" },
    2: { label: "TIER 2 · CITY", cls: "border-amber-500/40 bg-amber-500/10 text-amber-300" },
    3: { label: "TIER 3 · BENCHMARK", cls: "border-zinc-600 bg-zinc-900 text-zinc-400" },
};

function CompetitorPanel({ project, reload }) {
    const stage = project.stages?.competitor_analysis || {};
    const out = stage.output?.competitor_analysis;
    const isFallback = !!(stage.output?._fallback || out?._deterministic_fallback);
    const fallbackReason = stage.output?._fallback_reason || "";
    const [seeds, setSeeds] = useState(project.competitor_seeds || []);
    const [draft, setDraft] = useState("");
    const [seedBusy, setSeedBusy] = useState(false);
    const [suggestBusy, setSuggestBusy] = useState(false);
    const [showCeoIntake, setShowCeoIntake] = useState(false);

    useEffect(() => {
        setSeeds(project.competitor_seeds || []);
    }, [project.id, project.competitor_seeds]);

    const addSeed = () => {
        const v = draft.trim();
        if (!v) return;
        if (seeds.includes(v)) { setDraft(""); return; }
        setSeeds([...seeds, v]);
        setDraft("");
    };
    const removeSeed = (v) => setSeeds(seeds.filter(x => x !== v));

    const persistSeeds = async (next) => {
        setSeedBusy(true);
        try {
            await api.patch(`/projects/${project.id}/competitor-seeds`, { competitor_seeds: next });
            toast.success("Competitors saved.");
            reload && reload();
        } catch (err) {
            toast.error(err?.userMessage || "Save failed");
        } finally { setSeedBusy(false); }
    };

    const suggest = async () => {
        setSuggestBusy(true);
        try {
            const { data } = await api.post(`/projects/${project.id}/competitor-suggest`);
            const sugg = data.competitor_seeds || [];
            const merged = Array.from(new Set([...seeds, ...sugg])).slice(0, 12);
            setSeeds(merged);
            toast.success(`Suggested ${sugg.length} competitors${data.source === "ai" ? " (AI)" : " (local heuristic)"}. Edit and save.`);
        } catch (err) {
            toast.error(err?.userMessage || "Suggest failed");
        } finally { setSuggestBusy(false); }
    };

    const geo = out?.geography_detected || {};
    const grouped = { 1: [], 2: [], 3: [] };
    (out?.competitors || []).forEach((c) => {
        const t = [1, 2, 3].includes(c.tier) ? c.tier : 2;
        grouped[t].push(c);
    });

    return (
        <div className="p-6 lg:p-10" data-testid="competitor-panel">
            <PanelHeader title="Competitor Analysis" actions={[
                <button key="suggest" type="button" disabled={suggestBusy} onClick={suggest}
                        data-testid="competitor-suggest"
                        className="btn-secondary text-xs px-3 py-1.5 disabled:opacity-50">
                    <Sparkles className="w-3.5 h-3.5" />
                    {suggestBusy ? "Suggesting…" : "AI auto-suggest"}
                </button>,
                <button key="save" type="button" disabled={seedBusy} onClick={() => persistSeeds(seeds)}
                        data-testid="competitor-save-seeds"
                        className="btn-secondary text-xs px-3 py-1.5 disabled:opacity-50">
                    {seedBusy ? "Saving…" : "Save list"}
                </button>,
                <DownloadBtn key="pdf" testid="dl-competitor"
                    path={`/exports/${project.id}/competitor.pdf`}
                    filename={`competitor-${project.id}.pdf`}
                    label="Competitor PDF" />,
                <RunAgentButton key="run" project={project} agent="competitor_analyst" label="Run Competitor Analyst" onDone={reload} />,
            ]} />
            <p className="text-sm text-zinc-500 mb-5">
                The Competitor Analyst is a local market scout. <span className="text-zinc-300">Add competitors manually</span>
                {" "}by typing names into the chips below, or click <span className="text-zinc-300">AI auto-suggest</span> to
                generate a geo-tiered starter list you can then edit. Tier 1 = your neighbourhood / same area,
                Tier 2 = city-wide, Tier 3 = national benchmark. Click <span className="text-zinc-300">Run Competitor Analyst</span> when your seed list looks right.
            </p>

            {/* Sprint B — Ask CEO to extract competitors from a paste. */}
            {!showCeoIntake ? (
                <div className="mb-5 card-base p-4 ceo-glow flex items-center justify-between gap-3 flex-wrap">
                    <div>
                        <div className="label-micro mb-1">CEO smart competitor intake</div>
                        <div className="text-xs text-zinc-300">
                            Paste an analysis of multiple competitors and the CEO will separate them into individual rows you can confirm.
                        </div>
                    </div>
                    <button
                        data-testid="competitor-ceo-open"
                        onClick={() => setShowCeoIntake(true)}
                        className="btn-secondary text-xs px-3 py-1.5"
                    >
                        <Sparkles className="w-3.5 h-3.5" /> Ask CEO to extract competitors
                    </button>
                </div>
            ) : (
                <SmartBriefBuilder
                    context="competitor"
                    targetClientId={project.client_id || null}
                    targetProjectId={project.id}
                    title="Paste analyses of Competitor 1, Competitor 2, Competitor 3…"
                    placeholder="Paste analyses of multiple competitors. The CEO will separate each one and let you confirm name / website / positioning / strengths / weaknesses individually."
                    sectionVisibility={{ client: false, project: false, competitors: true, social: true, missing: true }}
                    onApply={() => { reload && reload(); }}
                />
            )}

            {/* Editable chips */}
            <div className="card-base p-5 mb-6">
                <div className="flex items-center justify-between gap-3 mb-2">
                    <div className="label-micro">Competitor seeds</div>
                    <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-600">
                        Manual or AI auto-suggest
                    </div>
                </div>
                <div className="text-xs text-zinc-500 mb-3 leading-relaxed">
                    <span className="text-zinc-300">Type a competitor name</span> and press Enter to add a chip
                    (best signal — you know the local players). Or click
                    {" "}<span className="text-zinc-300">AI auto-suggest</span> above for a geo-tiered starter list.
                    Either way, hit <span className="text-zinc-300">Save list</span> before
                    {" "}<span className="text-zinc-300">Run Competitor Analyst</span>.
                </div>
                <div className="flex flex-wrap gap-2 mb-3" data-testid="competitor-seeds">
                    {seeds.length === 0 && (
                        <span className="text-xs text-zinc-600" data-testid="competitor-auto-hint">
                            No competitors yet — add manually below or click AI auto-suggest. Running Competitor Analyst with an empty list will trigger a geo-aware fallback.
                        </span>
                    )}
                    {seeds.map(s => (
                        <span key={s} className="inline-flex items-center gap-1 text-xs uppercase tracking-wider font-mono px-2.5 py-1 rounded-sm border border-zinc-700 text-zinc-200 bg-zinc-900">
                            {s}
                            <button onClick={() => removeSeed(s)} data-testid={`competitor-remove-${s.toLowerCase().replace(/\s+/g,'-')}`}
                                    className="hover:text-red-400">
                                <X className="w-3 h-3" />
                            </button>
                        </span>
                    ))}
                </div>
                <div className="flex gap-2">
                    <input value={draft} onChange={e => setDraft(e.target.value)}
                           onKeyDown={e => e.key === "Enter" && (e.preventDefault(), addSeed())}
                           placeholder="Type a competitor name (e.g. nearby studio, direct rival, local operator)…"
                           data-testid="competitor-input"
                           className="flex-1 bg-black border border-zinc-800 rounded-sm px-3 py-2 text-sm" />
                    <button type="button" onClick={addSeed} data-testid="competitor-add"
                            className="btn-secondary text-xs px-3 py-2">Add manually</button>
                </div>
            </div>

            <StageError project={project} stageKey="competitor_analysis" />

            {isFallback && (
                <div className="border border-amber-500/30 bg-amber-500/5 p-4 rounded-sm mb-4 flex items-start gap-3"
                     data-testid="competitor-fallback-banner">
                    <Sparkles className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
                    <div className="text-sm">
                        <div className="font-mono uppercase text-xs tracking-wider text-amber-300 mb-1">
                            Generated without AI (geo-aware heuristic)
                        </div>
                        <div className="text-zinc-300">
                            {fallbackReason === "ai_provider_unavailable"
                                ? "AI provider was unavailable, so the list was built from the client's geography + category. Edit the chips above with real local names and re-run for a richer analysis."
                                : "Built from the client's geography + category. Edit the seeds above with real local names and re-run for a richer analysis."}
                        </div>
                    </div>
                </div>
            )}

            {!out ? (
                <div className="space-y-5">
                    <CompetitorProfilesPanel projectId={project.id} />
                    <SocialProfilesPanel projectId={project.id} />
                    <Empty hint="Add or auto-suggest competitors, then click Run Competitor Analyst. On flaky AI, a geo-aware fallback always produces output — you'll never see a blank panel." />
                </div>
            ) : (
                <div className="space-y-5">
                    {/* Phase 2A-2B — structured competitor profiles + website analysis */}
                    <CompetitorProfilesPanel projectId={project.id} />

                    {/* Phase 2E-lite — safe social profile registry (per-project view) */}
                    <SocialProfilesPanel projectId={project.id} />

                    {(geo.locality || geo.city) && (
                        <div className="card-base p-4" data-testid="competitor-geo">
                            <div className="label-micro mb-1">Geography detected</div>
                            <div className="text-sm text-zinc-200">
                                {geo.locality && <><span className="text-[#FF4400]">{geo.locality}</span> · </>}
                                {geo.city && <span>{geo.city}</span>}
                                {geo.region && <span className="text-zinc-500"> · {geo.region}</span>}
                                {geo.digital_first && <span className="ml-2 text-[10px] font-mono uppercase tracking-wider text-blue-300 border border-blue-500/40 bg-blue-500/10 px-1.5 py-0.5 rounded-sm">Digital-first</span>}
                            </div>
                        </div>
                    )}

                    {[1, 2, 3].map(tier => grouped[tier].length > 0 && (
                        <div key={tier} className="space-y-3" data-testid={`competitor-tier-${tier}`}>
                            <div className={`inline-block text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 rounded-sm border ${TIER_BADGE[tier].cls}`}>
                                {TIER_BADGE[tier].label} · {grouped[tier].length}
                            </div>
                            {grouped[tier].map((c, i) => (
                                <div key={`${tier}-${i}`} className="card-base p-5" data-testid={`competitor-card-${tier}-${i}`}>
                                    <div className="flex items-center justify-between flex-wrap gap-2">
                                        <div className="font-display text-xl">{c.name}</div>
                                        <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-wider text-zinc-500">
                                            {c.location && <span>📍 {c.location}</span>}
                                            {c.pricing_positioning && c.pricing_positioning !== "unknown" && <span>· {c.pricing_positioning}</span>}
                                            {c.url && (
                                                <a href={c.url} target="_blank" rel="noopener noreferrer"
                                                   className="hover:text-[#FF4400]">
                                                    <ExternalLink className="w-3 h-3 inline" />
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                    {c.category && <div className="text-xs text-zinc-500 mt-0.5 font-mono uppercase tracking-wider">{c.category}</div>}
                                    <div className="text-sm text-zinc-300 mt-2">{c.positioning}</div>
                                    {c.content_style && (
                                        <div className="text-xs text-zinc-400 mt-2"><span className="text-zinc-600 font-mono uppercase tracking-wider mr-1.5">Content:</span>{c.content_style}</div>
                                    )}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                                        <KVList title="Strengths" items={c.strengths} accent="emerald" />
                                        <KVList title="Weaknesses" items={c.weaknesses} accent="red" />
                                    </div>
                                    <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2 text-xs text-zinc-400">
                                        {c.monetization_model && <div><span className="text-zinc-600 font-mono uppercase tracking-wider mr-2">Monetization:</span>{c.monetization_model}</div>}
                                        {c.ux_workflow_insights && <div><span className="text-zinc-600 font-mono uppercase tracking-wider mr-2">UX:</span>{c.ux_workflow_insights}</div>}
                                    </div>
                                    {c.opportunity_for_client && (
                                        <div className="mt-3 text-sm text-zinc-200 border-l-2 border-[#FF4400] pl-3">
                                            <span className="text-[10px] font-mono uppercase tracking-wider text-[#FF4400] block mb-0.5">Opportunity for us</span>
                                            {c.opportunity_for_client}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    ))}

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <KVList title="Where the client wins" items={out.client_wins} accent="emerald" />
                        <KVList title="Where the client must improve" items={out.client_must_improve} accent="red" />
                        <KVList title="Differentiation opportunities" items={out.differentiation_opportunities} accent="emerald" />
                    </div>
                    {out.gap_analysis && (
                        <div className="card-base p-5">
                            <div className="label-micro mb-2">Gap analysis</div>
                            <div className="text-sm text-zinc-300">{out.gap_analysis}</div>
                        </div>
                    )}
                    {out.strategic_positioning_recommendation && (
                        <div className="card-base p-5">
                            <div className="label-micro mb-2">Strategic positioning recommendation</div>
                            <div className="text-sm text-zinc-300">{out.strategic_positioning_recommendation}</div>
                        </div>
                    )}
                </div>
            )}
            <ApprovalBar project={project} stage="competitor_analysis" onUpdated={reload} />
        </div>
    );
}

/* ============================ STRATEGIC QC BANNER ============================ */
const QC_VERDICT_STYLE = {
    pass: { color: "text-emerald-300 border-emerald-500/30 bg-emerald-500/5", label: "PASS" },
    warn: { color: "text-amber-300 border-amber-500/30 bg-amber-500/5", label: "WARN" },
    fail: { color: "text-red-300 border-red-500/30 bg-red-500/5", label: "FAIL" },
    skip: { color: "text-zinc-400 border-zinc-700 bg-zinc-900/40", label: "SKIPPED" },
};

function StrategicQcBanner({ project, stageKey, onChanged }) {
    const stage = project.stages?.[stageKey];
    const out = stage?.output;
    const qc = stage?.qc;
    const [busyAudit, setBusyAudit] = useState(false);
    const [busyImprove, setBusyImprove] = useState(false);
    if (!out) return null;

    const audit = async () => {
        setBusyAudit(true);
        const t = toast.loading("Running Strategic QC…", { duration: Infinity });
        try {
            await api.post(`/projects/${project.id}/qc/${stageKey}`);
            toast.dismiss(t);
            toast.success("QC complete.");
            onChanged && onChanged();
        } catch (err) {
            toast.dismiss(t);
            toast.error(err?.userMessage || "QC failed");
        } finally { setBusyAudit(false); }
    };
    const improve = async () => {
        setBusyImprove(true);
        const t = toast.loading("Regenerating with QC feedback…", { duration: Infinity });
        try {
            await api.post("/agents/improve", { project_id: project.id, stage: stageKey });
            toast.dismiss(t);
            toast.success("Improved output ready.");
            onChanged && onChanged();
        } catch (err) {
            toast.dismiss(t);
            toast.error(err?.userMessage || "Improve failed");
        } finally { setBusyImprove(false); }
    };

    return (
        <div className="mb-4 p-4 rounded-sm border border-zinc-800 bg-[#0a0a0a]" data-testid={`qc-banner-${stageKey}`}>
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-zinc-400" />
                    <span className="label-micro">Strategic QC</span>
                    {qc?.verdict && (
                        <span className={`text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 rounded-sm border ${QC_VERDICT_STYLE[qc.verdict]?.color || QC_VERDICT_STYLE.skip.color}`}>
                            {QC_VERDICT_STYLE[qc.verdict]?.label || qc.verdict}
                        </span>
                    )}
                </div>
                <div className="flex gap-2">
                    <button type="button" onClick={audit} disabled={busyAudit}
                            data-testid={`qc-audit-${stageKey}`}
                            className="btn-secondary text-xs px-3 py-1.5 disabled:opacity-50">
                        {busyAudit ? "Auditing…" : (qc ? "Re-audit" : "Audit Output")}
                    </button>
                    {qc?.verdict && qc.verdict !== "pass" && (
                        <button type="button" onClick={improve} disabled={busyImprove}
                                data-testid={`qc-improve-${stageKey}`}
                                className="btn-primary text-xs px-3 py-1.5 disabled:opacity-50">
                            <RefreshCcw className="w-3 h-3" />
                            {busyImprove ? "Improving…" : "Improve Output"}
                        </button>
                    )}
                </div>
            </div>
            {qc?.fail_tags?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5" data-testid={`qc-tags-${stageKey}`}>
                    {qc.fail_tags.map((t, i) => (
                        <span key={i} className="text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 rounded-sm border border-amber-500/40 text-amber-300 bg-amber-500/10">
                            ⚠ {t}
                        </span>
                    ))}
                </div>
            )}
            {qc?.summary && (
                <div className="text-xs text-zinc-400 mt-2">{qc.summary}</div>
            )}
        </div>
    );
}

/* ============================ BRAND DNA ============================ */
function BrandDnaPanel({ project, reload }) {
    const out = project.stages?.brand_dna?.output?.brand_dna;
    return (
        <div className="p-6 lg:p-10" data-testid="brand-dna-panel">
            <PanelHeader title="Brand DNA"
                actions={[
                    <RunAgentButton key="run" project={project} agent="brand_analyst" label="Run Brand Analyst" onDone={reload} />,
                    <DownloadBtn key="pdf" testid="dl-brand-dna" path={`/exports/${project.id}/brand-dna.pdf`} filename={`brand-dna-${project.id}.pdf`} label="Brand DNA PDF" />,
                ]} />
            <StageError project={project} stageKey="brand_dna" />
            <StrategicQcBanner project={project} stageKey="brand_dna" onChanged={reload} />
            {!out ? <Empty hint="Run the Brand Analyst to generate the DNA report." /> : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <KV title="Essence" value={out.essence} />
                    <KV title="Positioning" value={out.positioning} />
                    <KVList title="Tone of Voice" items={out.tone_of_voice} />
                    <KVList title="Visual Style" items={out.visual_style} />
                    <KVList title="Offers" items={out.offers} />
                    <div className="grid grid-cols-2 gap-4">
                        <KVList title="Strengths" items={out.strengths} accent="emerald" />
                        <KVList title="Weaknesses" items={out.weaknesses} accent="red" />
                        <KVList title="Opportunities" items={out.opportunities} accent="emerald" />
                        <KVList title="Risks" items={out.risks || out.threats} accent="red" />
                    </div>
                    {(out.key_insights || []).length > 0 && (
                        <div className="card-base p-5 lg:col-span-2" data-testid="brand-dna-key-insights">
                            <div className="label-micro mb-3">Key insights — this quarter</div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                {(out.key_insights || []).map((k, i) => (
                                    <div key={i} className="border border-zinc-800 p-3 rounded-sm">
                                        <div className="text-sm text-zinc-100 font-medium leading-snug">
                                            {typeof k === "string" ? k : k.insight}
                                        </div>
                                        {typeof k === "object" && k?.action && (
                                            <div className="text-[11px] text-zinc-500 mt-1.5">
                                                <span className="uppercase tracking-wider text-zinc-600 mr-1">Action:</span>
                                                <span className="text-zinc-300">{k.action}</span>
                                            </div>
                                        )}
                                        {typeof k === "object" && k?.owner && (
                                            <div className="text-[10px] font-mono uppercase tracking-wider text-[#FF4400] mt-1">
                                                {k.owner}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    <div className="card-base p-5 lg:col-span-2" data-testid="brand-dna-personas-mount">
                        {/* Sprint A — replaced the broken `out.audience_personas` reader
                            (Brand DNA agent never emitted that field). Now wires to the
                            real `personas` collection via existing routes. */}
                        <PersonasPanel projectId={project.id} />
                    </div>
                </div>
            )}
            <ApprovalBar project={project} stage="brand_dna" onUpdated={reload} />
        </div>
    );
}

/* ============================ BUSINESS PLAN ============================ */
function BusinessPlanPanel({ project, reload }) {
    const out = project.stages?.business_plan?.output?.business_plan;
    return (
        <div className="p-6 lg:p-10" data-testid="business-plan-panel">
            <PanelHeader title="Business Development Plan"
                actions={[
                    <RunAgentButton key="run" project={project} agent="business_plan" label="Run Business Plan" onDone={reload} />,
                    <DownloadBtn key="pdf" testid="dl-business-plan" path={`/exports/${project.id}/business-plan.pdf`} filename={`business-plan-${project.id}.pdf`} label="Business Plan PDF" />,
                ]} />
            <StageError project={project} stageKey="business_plan" />
            {!out ? <Empty hint="Generate the strategy after Brand DNA is approved." /> : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <KV title="Business Objective" value={out.business_objective} />
                    <KV title="Target Audience" value={out.target_audience} />
                    <KV title="Positioning" value={out.positioning} />
                    <KV title="Revenue Model" value={out.revenue_model} />
                    <KV title="Pricing Model" value={out.pricing_model} />
                    <KV title="Sales Approach" value={out.sales_approach} />
                    <KVList title="Objectives" items={out.objectives} />
                    <KV title="Campaign Strategy" value={out.campaign_strategy} />
                    <KVList title="KPIs" items={out.kpis} />
                    <KVList title="Channels" items={out.channels} />
                    <KVList title="Risks" items={out.risks} accent="red" />
                    <KVList title="Next Steps" items={out.next_steps} accent="emerald" />
                    <div className="card-base p-5 lg:col-span-2">
                        <div className="label-micro mb-3">Execution Roadmap</div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            {(out.execution_roadmap || []).map((p, i) => (
                                <div key={i} className="border border-zinc-800 p-4 rounded-sm">
                                    <div className="font-mono text-xs uppercase tracking-wider text-[#FF4400]">{p.phase}</div>
                                    <div className="text-xs text-zinc-500 mt-1 font-mono">{p.weeks}</div>
                                    <ul className="mt-3 space-y-1">
                                        {(p.actions || []).map((a, j) => (
                                            <li key={j} className="text-sm text-zinc-200 flex gap-2">
                                                <span className="text-zinc-500">•</span>{a}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="card-base p-5 lg:col-span-2">
                        <div className="label-micro mb-3">Monthly content direction</div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            {(out.monthly_content_direction || []).map((m, i) => (
                                <div key={i} className="border border-zinc-800 p-4 rounded-sm">
                                    <div className="font-mono text-xs uppercase tracking-wider text-zinc-500">{m.month}</div>
                                    <div className="text-sm mt-2">Themes: {(m.themes || []).join(", ")}</div>
                                    <div className="text-sm mt-1 text-zinc-400">Formats: {(m.formats || []).join(", ")}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
            {/* Sprint A — Business Plan Gold Layer (read-time composer).
                Joins competitor + brand_dna + research + social_strategy
                + distribution + personas + approved evidence. NO LLM. */}
            <BusinessPlanGoldPanel projectId={project.id} />
            {/* Workflow UI Completion Sprint — Campaign Strategy Input
                Gate. Business Plan run/approve is 409-blocked on the
                backend until every input is reviewed OR skipped-with-
                reason. This block makes that requirement VISIBLE and
                ACTIONABLE. Backend endpoints:
                  · GET  /api/projects/{id}/campaign-inputs
                  · PATCH /api/projects/{id}/campaign-inputs/{key}/review
                  · POST  /api/projects/{id}/campaign-inputs/{key}/skip */}
            <CampaignInputsGate projectId={project.id} onChanged={reload} />
            <ApprovalBar project={project} stage="business_plan" onUpdated={reload} />
        </div>
    );
}

/* ============================ RESEARCH ============================ */
function ResearchPanel({ project, reload }) {
    const out = project.stages?.research?.output?.research;
    // iter73.1 — defensive shape guard. Stored Research outputs may
    // have wrong-shape values (string / object / null / number /
    // undefined) for fields that the schema expects to be arrays.
    // `asArray` collapses ALL of those to an empty array so the panel
    // never crashes with `(…).map is not a function`.
    const asArray = (v) => (Array.isArray(v) ? v : []);
    // iter73.2 — item-level defensive helpers. Codex YELLOW on
    // iter73.1 flagged that `asArray` only guarded TOP-LEVEL shape:
    // an array of malformed items (`[null, {}, {x:1}]`) could still
    // crash via `(…).name` (TypeError) or render a raw object as a
    // React child ("Objects are not valid as a React child"). These
    // two helpers neutralize both classes of failure.
    const asObject = (v) =>
        (v && typeof v === "object" && !Array.isArray(v) ? v : {});
    const asText = (v) =>
        (typeof v === "string" || typeof v === "number" ? String(v) : "");
    const [notes, setNotes] = useState("");
    // T1B + T2 — Multi-page Website Discovery (rule-based, no LLM).
    const [discoBusy, setDiscoBusy] = useState(false);
    const [discoSummary, setDiscoSummary] = useState(null);
    const runWebsiteDiscovery = async () => {
        setDiscoBusy(true);
        try {
            const { data } = await api.post(
                `/research-intel/projects/${project.id}/run`, {},
            );
            setDiscoSummary(data);
            const failedNote = (data?.pages_failed || 0) > 0
                ? ` · ${data.pages_failed} failed`
                : "";
            toast.success(
                `Discovery: ${data?.pages_fetched || 0} pages · ` +
                `${(data?.social_links || []).length} social links · ` +
                `${data?.evidence_rows || 0} evidence rows${failedNote}`,
            );
            await reload?.();
        } catch (e) {
            toast.error(
                `Discovery: ${e?.userMessage || safeText(e?.response?.data?.detail) || e?.message}`,
            );
        } finally {
            setDiscoBusy(false);
        }
    };
    return (
        <div className="p-6 lg:p-10" data-testid="research-panel">
            <PanelHeader title="Research" actions={[
                <DownloadBtn key="pdf"
                    path={`/exports/${project.id}/research.pdf`}
                    filename={`research-${project.id}.pdf`}
                    label="Research PDF"
                    testid="dl-research" />,
                <button
                    key="discovery"
                    type="button"
                    onClick={runWebsiteDiscovery}
                    disabled={discoBusy}
                    data-testid="run-website-discovery-btn"
                    title="Rule-based multi-page website discovery (no LLM)"
                    className="px-3 py-1.5 text-xs font-mono uppercase tracking-wider border border-zinc-700 text-zinc-300 hover:border-zinc-500 hover:text-white rounded-sm disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                    {discoBusy ? "Discovering…" : "Multi-Page Discovery"}
                </button>,
                <RunAgentButton key="run" project={project} agent="research" label="Run Research" extra={{ notes }} onDone={reload} />,
            ]} />
            {discoSummary && (
                <div className="mb-4 flex flex-wrap gap-2 text-[10px] font-mono uppercase tracking-wider"
                     data-testid="discovery-summary-pills">
                    <span className="px-2 py-0.5 border border-emerald-500/30 text-emerald-300 bg-emerald-500/5 rounded-sm"
                          data-testid="disco-pages-fetched">
                        Pages fetched: {discoSummary.pages_fetched || 0}/{discoSummary.pages_discovered || 0}
                    </span>
                    <span className="px-2 py-0.5 border border-sky-500/30 text-sky-300 bg-sky-500/5 rounded-sm"
                          data-testid="disco-social-links">
                        Social links: {(discoSummary.social_links || []).length}
                    </span>
                    <span className="px-2 py-0.5 border border-violet-500/30 text-violet-300 bg-violet-500/5 rounded-sm"
                          data-testid="disco-evidence-rows">
                        Evidence rows: {discoSummary.evidence_rows || 0}
                    </span>
                    <span className="px-2 py-0.5 border border-amber-500/30 text-amber-300 bg-amber-500/5 rounded-sm"
                          data-testid="disco-queue-rows">
                        Confirmations: {discoSummary.queue_rows || 0}
                    </span>
                    {(discoSummary.pages_failed || 0) > 0 && (
                        <span className="px-2 py-0.5 border border-red-500/40 text-red-300 bg-red-500/5 rounded-sm"
                              data-testid="disco-failed-pages">
                            Failed: {discoSummary.pages_failed}
                        </span>
                    )}
                    <span className="px-2 py-0.5 border border-zinc-700 text-zinc-400 bg-zinc-900/40 rounded-sm"
                          data-testid="disco-duration">
                        {Math.round((discoSummary.duration_ms || 0) / 1000)}s
                    </span>
                </div>
            )}
            {/* T3 — Research Evidence Panel: operator visibility over
                evidence_records + user_confirmation_queue. No agent
                wiring (deferred to T5). */}
            <ResearchEvidencePanel projectId={project.id} />
            {/* Sprint C+D — controlled external competitor discovery + social intake. */}
            <CompetitorDiscoveryExternalPanel projectId={project.id} />
            <SocialEvidenceIntakePanel projectId={project.id} />
            {/* Sprint A — render the 6 hidden research.competitor_analysis.*
                sub-fields. Defensive: renders nothing when absent. */}
            <ResearchCompetitorInsightsCard research={out} />
            <StageError project={project} stageKey="research" />
            {/* Phase 2C Preflight Wiring — research-missing warning. Surfaces
                when the stage failed AND no verified facts exist. */}
            {(() => {
                const stage = project.stages?.research || {};
                const failed = (stage.status || "").toLowerCase() === "failed"
                    || (stage.status || "").toLowerCase() === "recoverable";
                const empty = !stage.output;
                if (!(failed || empty)) return null;
                return (
                    <div className="mb-4 p-3 border border-red-500/40 bg-red-500/5 rounded-sm flex items-start gap-2"
                         data-testid="research-missing-banner">
                        <span className="w-2 h-2 rounded-full bg-red-400 pulse-dot mt-1.5 shrink-0" />
                        <div className="text-xs text-red-200 leading-relaxed">
                            <span className="font-medium">Research failed / no verified research available.</span>
                            <div className="text-zinc-400 mt-0.5">
                                Downstream stages (Business Plan, Concepts, Script, Storyboard, Proposal)
                                will run in <span className="text-zinc-200">assumption mode</span> —
                                using only the client brief. No social-media (Instagram / Facebook /
                                LinkedIn / YouTube) analysis is performed in this phase.
                            </div>
                        </div>
                    </div>
                );
            })()}
            {out?.degraded_mode && (
                <div className="mb-4 p-3 border border-amber-500/40 bg-amber-500/5 rounded-sm flex items-start gap-2" data-testid="research-degraded-banner">
                    <span className="w-2 h-2 rounded-full bg-amber-400 pulse-dot mt-1.5 shrink-0" />
                    <div className="text-xs text-amber-200 leading-relaxed">
                        <span className="font-medium">Research partially failed — proceeding with best available intelligence.</span>
                        <div className="text-zinc-400 mt-0.5">
                            {/* iter73 — backend now sets a deterministic short
                                code in `degraded_reason` and (optionally) a
                                truncated 200-char `degraded_reason_detail`.
                                Render both with a graceful fallback for
                                legacy entries that only have the old string. */}
                            <span data-testid="research-degraded-reason">
                                {out.degraded_reason || "Provider returned an error; light-mode research was used. Re-run when provider is healthy."}
                            </span>
                            {out.degraded_reason_detail && (
                                <span className="block mt-1 text-[10px] text-amber-200/70 font-mono break-all" data-testid="research-degraded-reason-detail">
                                    Detail: {out.degraded_reason_detail}
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            )}
            <textarea
                data-testid="research-notes"
                placeholder="Optional: paste reference links / notes for the Research Agent…"
                value={notes} onChange={e => setNotes(e.target.value)} rows={3}
                className="w-full bg-black border border-zinc-800 rounded-sm px-3 py-2 mb-4 outline-none focus:border-zinc-500"
            />
            {!out ? <Empty hint="Provide notes (optional) and run the Research Agent." /> : (() => {
                // iter73 — render the CURRENT Research schema. Old projects
                // that still carry only the legacy schema fields fall through
                // to the Legacy view <details> block below.
                const hasCurrent =
                    asArray(out.local_market_insights).length > 0
                    || asArray(out.audience_segments).length > 0
                    || asArray(out.pain_points).length > 0
                    || asArray(out.buying_triggers).length > 0
                    || asArray(out.top_insights).length > 0
                    || asArray(out.platform_strategy).length > 0
                    || asArray(out.competitor_gap_opportunities).length > 0
                    || asArray(out.opportunity_gaps).length > 0
                    || asArray(out.growth_angles).length > 0
                    || asArray(out.verified_facts).length > 0
                    || asArray(out.assumptions).length > 0;
                const hasLegacy =
                    asArray(out.reference_directions).length > 0
                    || asArray(out.trend_angles).length > 0
                    || asArray(out.competitor_observations).length > 0
                    || asArray(out.campaign_inspiration).length > 0;
                return (
                    <div className="space-y-4" data-testid="research-body">
                        {!hasCurrent && !hasLegacy && (
                            <Empty hint="Research has run but produced no insights yet. Re-run when provider is healthy." />
                        )}
                        {hasCurrent && (
                            <>
                                {/* Local market insights — full-width list */}
                                {!!asArray(out.local_market_insights).length && (
                                    <div className="card-base p-5" data-testid="research-local-insights">
                                        <div className="label-micro mb-3">Local market insights</div>
                                        <ul className="space-y-2">
                                            {asArray(out.local_market_insights).map((s, i) => {
                                                // iter73.2 — guard malformed items: object/null/array
                                                // collapse to "" via asText so we never render a raw
                                                // object as a React child. Empty items are skipped.
                                                const txt = asText(s);
                                                if (!txt) return null;
                                                return (
                                                    <li key={i} className="text-sm text-zinc-200 leading-relaxed">
                                                        <span className="text-zinc-500 mr-2">·</span>{txt}
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </div>
                                )}
                                {/* Audience segments — 2-col cards */}
                                {!!asArray(out.audience_segments).length && (
                                    <div className="card-base p-5" data-testid="research-audience-segments">
                                        <div className="label-micro mb-3">Audience segments</div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                            {asArray(out.audience_segments).map((s, i) => {
                                                // iter73.2 — guard malformed items. `seg` is always
                                                // a plain object (never null), so `seg.name` etc.
                                                // never throw. All field reads route through asText
                                                // so a wrong-shape value renders cleanly.
                                                const seg = asObject(s);
                                                return (
                                                <div key={i} className="border border-zinc-900 rounded-sm p-3" data-testid={`research-audience-${i}`}>
                                                    <div className="flex items-baseline justify-between gap-2">
                                                        <div className="font-medium text-zinc-100">{asText(seg.name) || `Segment ${i + 1}`}</div>
                                                        <div className="text-[10px] font-mono text-zinc-500">
                                                            {typeof seg.share_pct === "number" ? `${seg.share_pct}%` : ""}
                                                            {asText(seg.confidence) ? ` · ${asText(seg.confidence)}` : ""}
                                                        </div>
                                                    </div>
                                                    {asText(seg.who) && <div className="text-xs text-zinc-400 mt-1">{asText(seg.who)}</div>}
                                                    {asArray(seg.motivators).length > 0 && (
                                                        <div className="text-[11px] text-zinc-500 mt-2">
                                                            <span className="text-zinc-400">Motivators: </span>
                                                            {asArray(seg.motivators).map(asText).filter(Boolean).join(" · ")}
                                                        </div>
                                                    )}
                                                    {asArray(seg.barriers).length > 0 && (
                                                        <div className="text-[11px] text-zinc-500 mt-1">
                                                            <span className="text-zinc-400">Barriers: </span>
                                                            {asArray(seg.barriers).map(asText).filter(Boolean).join(" · ")}
                                                        </div>
                                                    )}
                                                </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                                {/* Pain points / Buying triggers — 2-col side-by-side */}
                                {(!!asArray(out.pain_points).length || !!asArray(out.buying_triggers).length) && (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="card-base p-5" data-testid="research-pain-points">
                                            <div className="label-micro mb-3">Pain points</div>
                                            {asArray(out.pain_points).length ? (
                                                <ul className="space-y-1.5 text-sm text-zinc-200">
                                                    {asArray(out.pain_points).map((p, i) => {
                                                        const txt = asText(p);
                                                        if (!txt) return null;
                                                        return (
                                                            <li key={i}><span className="text-zinc-500 mr-2">·</span>{txt}</li>
                                                        );
                                                    })}
                                                </ul>
                                            ) : <div className="text-xs text-zinc-500">—</div>}
                                        </div>
                                        <div className="card-base p-5" data-testid="research-buying-triggers">
                                            <div className="label-micro mb-3">Buying triggers</div>
                                            {asArray(out.buying_triggers).length ? (
                                                <ul className="space-y-1.5 text-sm text-zinc-200">
                                                    {asArray(out.buying_triggers).map((p, i) => {
                                                        const txt = asText(p);
                                                        if (!txt) return null;
                                                        return (
                                                            <li key={i}><span className="text-zinc-500 mr-2">·</span>{txt}</li>
                                                        );
                                                    })}
                                                </ul>
                                            ) : <div className="text-xs text-zinc-500">—</div>}
                                        </div>
                                    </div>
                                )}
                                {/* Top insights — full-width */}
                                {!!asArray(out.top_insights).length && (
                                    <div className="card-base p-5" data-testid="research-top-insights">
                                        <div className="label-micro mb-3">Top insights</div>
                                        <div className="space-y-3">
                                            {asArray(out.top_insights).map((t, i) => {
                                                // iter73.2 — guard against null/array/primitive items.
                                                const ti = asObject(t);
                                                const insight = asText(ti.insight);
                                                // If the primary "insight" field is empty after coercion,
                                                // skip rendering this row — prevents an empty card from
                                                // appearing in the list.
                                                if (!insight) return null;
                                                return (
                                                <div key={i} className="border-b border-zinc-900 pb-3 last:border-b-0 last:pb-0">
                                                    <div className="flex items-baseline justify-between gap-2">
                                                        <div className="font-medium text-zinc-100 text-sm">{insight}</div>
                                                        <div className="text-[10px] font-mono text-zinc-500 whitespace-nowrap">
                                                            {asText(ti.data_source)}{asText(ti.confidence) ? ` · ${asText(ti.confidence)}` : ""}
                                                        </div>
                                                    </div>
                                                    {asText(ti.why_it_matters) && <div className="text-xs text-zinc-400 mt-1">{asText(ti.why_it_matters)}</div>}
                                                    {asText(ti.action) && <div className="text-xs text-[#FF4400] mt-1">→ {asText(ti.action)}</div>}
                                                    {asText(ti.impact) && <div className="text-[11px] text-zinc-500 mt-1">Impact: {asText(ti.impact)}</div>}
                                                </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                                {/* Platform strategy — 2-col */}
                                {!!asArray(out.platform_strategy).length && (
                                    <div className="card-base p-5" data-testid="research-platform-strategy">
                                        <div className="label-micro mb-3">Platform strategy</div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                            {asArray(out.platform_strategy).map((p, i) => {
                                                const ps = asObject(p);
                                                const platform = asText(ps.platform);
                                                if (!platform) return null;
                                                return (
                                                <div key={i} className="border border-zinc-900 rounded-sm p-3" data-testid={`research-platform-${i}`}>
                                                    <div className="flex items-baseline justify-between gap-2">
                                                        <div className="font-medium text-zinc-100 capitalize">{platform}</div>
                                                        <div className="text-[10px] font-mono text-zinc-500">
                                                            {asText(ps.priority)}{typeof ps.share_pct === "number" ? ` · ${ps.share_pct}%` : ""}
                                                        </div>
                                                    </div>
                                                    {asText(ps.why) && <div className="text-xs text-zinc-400 mt-1">{asText(ps.why)}</div>}
                                                    {asArray(ps.focus_formats).length > 0 && (
                                                        <div className="text-[11px] text-zinc-500 mt-2">
                                                            Formats: {asArray(ps.focus_formats).map(asText).filter(Boolean).join(" · ")}
                                                        </div>
                                                    )}
                                                </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                                {/* Competitor gap opportunities — full-width */}
                                {!!asArray(out.competitor_gap_opportunities).length && (
                                    <div className="card-base p-5" data-testid="research-competitor-gaps">
                                        <div className="label-micro mb-3">Competitor gap opportunities</div>
                                        <ul className="space-y-2">
                                            {asArray(out.competitor_gap_opportunities).map((g, i) => {
                                                const cg = asObject(g);
                                                const gap = asText(cg.gap);
                                                if (!gap) return null;
                                                return (
                                                <li key={i} className="text-sm border-b border-zinc-900 pb-2 last:border-b-0 last:pb-0">
                                                    <div className="text-zinc-100">{gap}</div>
                                                    {asText(cg.how_to_exploit) && <div className="text-xs text-zinc-500 mt-0.5">→ {asText(cg.how_to_exploit)}</div>}
                                                </li>
                                                );
                                            })}
                                        </ul>
                                    </div>
                                )}
                                {/* Opportunity gaps — full-width */}
                                {!!asArray(out.opportunity_gaps).length && (
                                    <div className="card-base p-5" data-testid="research-opportunity-gaps">
                                        <div className="label-micro mb-3">Opportunity gaps</div>
                                        <ul className="space-y-2">
                                            {asArray(out.opportunity_gaps).map((g, i) => {
                                                const og = asObject(g);
                                                const gap = asText(og.gap);
                                                if (!gap) return null;
                                                return (
                                                <li key={i} className="text-sm border-b border-zinc-900 pb-2 last:border-b-0 last:pb-0">
                                                    <div className="text-zinc-100">{gap}</div>
                                                    {asText(og.why_client_can_win) && <div className="text-xs text-zinc-400 mt-0.5">Why we can win: {asText(og.why_client_can_win)}</div>}
                                                    {asText(og.action) && <div className="text-xs text-[#FF4400] mt-0.5">→ {asText(og.action)}</div>}
                                                </li>
                                                );
                                            })}
                                        </ul>
                                    </div>
                                )}
                                {/* Growth angles — inline chips */}
                                {!!asArray(out.growth_angles).length && (
                                    <div className="card-base p-5" data-testid="research-growth-angles">
                                        <div className="label-micro mb-3">Growth angles</div>
                                        <div className="flex flex-wrap gap-2">
                                            {asArray(out.growth_angles).map((g, i) => {
                                                const txt = asText(g);
                                                if (!txt) return null;
                                                return (
                                                    <span key={i} className="text-xs px-2 py-1 border border-zinc-800 rounded-sm text-zinc-200">{txt}</span>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                                {/* Verified facts / Assumptions — 2-col, color-coded */}
                                {(!!asArray(out.verified_facts).length || !!asArray(out.assumptions).length) && (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="card-base p-5 border-emerald-900/40" data-testid="research-verified-facts">
                                            <div className="label-micro mb-3 text-emerald-300/80">Verified facts</div>
                                            {asArray(out.verified_facts).length ? (
                                                <ul className="space-y-1.5 text-sm text-zinc-200">
                                                    {asArray(out.verified_facts).map((f, i) => {
                                                        // iter73.2 — `f` may be a string (typical),
                                                        // a structured object (legacy `{fact, source}`),
                                                        // null, or a primitive. Coerce to readable text
                                                        // without ever rendering a raw object as a
                                                        // React child.
                                                        let txt = asText(f);
                                                        if (!txt && f && typeof f === "object") {
                                                            // Object form — pull out useful fields if present,
                                                            // else stringify defensively.
                                                            txt = asText(f.fact) || asText(f.statement) || JSON.stringify(f);
                                                        }
                                                        if (!txt) return null;
                                                        return (
                                                            <li key={i}><span className="text-emerald-500/70 mr-2">✓</span>{txt}</li>
                                                        );
                                                    })}
                                                </ul>
                                            ) : <div className="text-xs text-zinc-500">— none recorded —</div>}
                                        </div>
                                        <div className="card-base p-5 border-amber-900/40" data-testid="research-assumptions">
                                            <div className="label-micro mb-3 text-amber-300/80">Assumptions</div>
                                            {asArray(out.assumptions).length ? (
                                                <ul className="space-y-1.5 text-sm text-zinc-200">
                                                    {asArray(out.assumptions).map((a, i) => {
                                                        const txt = asText(a);
                                                        if (!txt) return null;
                                                        return (
                                                            <li key={i}><span className="text-amber-500/70 mr-2">⚠</span>{txt}</li>
                                                        );
                                                    })}
                                                </ul>
                                            ) : <div className="text-xs text-zinc-500">— none recorded —</div>}
                                        </div>
                                    </div>
                                )}
                                {out.social_analysis_disclaimer && (
                                    <div className="text-[11px] text-zinc-500 italic" data-testid="research-social-disclaimer">
                                        {String(out.social_analysis_disclaimer)}
                                    </div>
                                )}
                            </>
                        )}
                        {/* iter73 — backward-compat: old projects persisted
                            BEFORE the schema migration still keep their data
                            accessible via this collapsible block. */}
                        {hasLegacy && (
                            <details className="card-base p-5" data-testid="research-legacy-block">
                                <summary className="label-micro cursor-pointer text-zinc-400 hover:text-zinc-200">
                                    Legacy research view (old schema)
                                </summary>
                                <div className="mt-3 space-y-3">
                                    {!!asArray(out.reference_directions).length && (
                                        <div data-testid="research-legacy-reference-directions">
                                            <div className="text-xs text-zinc-500 mb-2">Reference directions</div>
                                            {asArray(out.reference_directions).map((r, i) => {
                                                const rd = asObject(r);
                                                const title = asText(rd.title);
                                                const why = asText(rd.why_it_works);
                                                const link = asText(rd.reference_link);
                                                if (!title && !why && !link) return null;
                                                return (
                                                <div key={i} className="border-b border-zinc-900 py-2 last:border-b-0">
                                                    {title && <div className="text-sm font-medium">{title}</div>}
                                                    {why && <div className="text-xs text-zinc-400">{why}</div>}
                                                    {link && (
                                                        <a href={link} target="_blank" rel="noreferrer" className="text-xs text-[#FF4400] inline-flex items-center gap-1">
                                                            <ExternalLink className="w-3 h-3" />{link}
                                                        </a>
                                                    )}
                                                </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                    {!!asArray(out.trend_angles).length && (
                                        <div data-testid="research-legacy-trend-angles">
                                            <div className="text-xs text-zinc-500 mb-2">Trend angles</div>
                                            <ul className="text-sm space-y-1">
                                                {asArray(out.trend_angles).map((t, i) => {
                                                    // iter73.2 — already partially defensive; convert
                                                    // raw object items into JSON text via asText
                                                    // fallback path.
                                                    let txt = asText(t);
                                                    if (!txt && t && typeof t === "object") {
                                                        txt = JSON.stringify(t);
                                                    }
                                                    if (!txt) return null;
                                                    return (<li key={i}>· {txt}</li>);
                                                })}
                                            </ul>
                                        </div>
                                    )}
                                    {!!asArray(out.competitor_observations).length && (
                                        <div data-testid="research-legacy-competitor-observations">
                                            <div className="text-xs text-zinc-500 mb-2">Competitor observations</div>
                                            {asArray(out.competitor_observations).map((c, i) => {
                                                const co = asObject(c);
                                                const comp = asText(co.competitor);
                                                const insight = asText(co.insight);
                                                if (!comp && !insight) return null;
                                                return (
                                                    <div key={i} className="text-sm border-b border-zinc-900 py-1.5 last:border-b-0">
                                                        {comp && <span className="text-zinc-100 font-medium">{comp}</span>}
                                                        {comp && insight && <span className="text-zinc-500"> — </span>}
                                                        {insight && <span className="text-zinc-400">{insight}</span>}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                    {!!asArray(out.campaign_inspiration).length && (
                                        <div data-testid="research-legacy-campaign-inspiration">
                                            <div className="text-xs text-zinc-500 mb-2">Campaign inspiration</div>
                                            {asArray(out.campaign_inspiration).map((c, i) => {
                                                const ci = asObject(c);
                                                const camp = asText(ci.campaign);
                                                const note = asText(ci.note);
                                                if (!camp && !note) return null;
                                                return (
                                                    <div key={i} className="text-sm border-b border-zinc-900 py-1.5 last:border-b-0">
                                                        {camp && <span className="text-zinc-100 font-medium">{camp}</span>}
                                                        {camp && note && <span className="text-zinc-500"> — </span>}
                                                        {note && <span className="text-zinc-400">{note}</span>}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </details>
                        )}
                    </div>
                );
            })()}
            <ApprovalBar project={project} stage="research" onUpdated={reload} />
        </div>
    );
}

/* ============================ CONCEPTS ============================ */
function ConceptsPanel({ project, reload }) {
    const out = project.stages?.concepts?.output?.concepts;
    const [chosen, setChosen] = useState(null);
    const stage = project.stages?.concepts;

    const conceptsShareUrl = project.public_share_enabled && (project.public_share_stages || []).includes("concepts")
        ? `${window.location.origin}/share/${project.id}/${project.public_token}#concepts`
        : "";

    const copyConceptsLink = async () => {
        if (!conceptsShareUrl) {
            toast.error("Enable public sharing for Concepts in the Share / Review tab first.");
            return;
        }
        await navigator.clipboard.writeText(conceptsShareUrl);
        toast.success("Concepts link copied to clipboard");
    };

    const openClientView = () => {
        if (!conceptsShareUrl) {
            toast.error("Enable public sharing for Concepts in the Share / Review tab first.");
            return;
        }
        window.open(conceptsShareUrl, "_blank", "noopener,noreferrer");
    };

    return (
        <div className="p-6 lg:p-10" data-testid="concepts-panel">
            <PanelHeader title="Concept Options" actions={[
                <RunAgentButton key="run" project={project} agent="creative_director" label="Run Creative Director" onDone={reload} />,
                <button key="open-client" data-testid="concepts-open-client" onClick={openClientView}
                    className="btn-secondary text-xs px-3 py-1.5">
                    <Share2 className="w-3.5 h-3.5" /> Open client view
                </button>,
                <button key="copy-link" data-testid="concepts-copy-link" onClick={copyConceptsLink}
                    className="btn-secondary text-xs px-3 py-1.5">
                    <Copy className="w-3.5 h-3.5" /> Copy link
                </button>,
                <DownloadBtn key="dl" testid="dl-concepts" path={`/exports/${project.id}/concepts.pdf`}
                    filename={`concepts-${project.id}.pdf`} label="Concepts PDF" />,
            ]} />
            <StageError project={project} stageKey="concepts" />
            <StrategicQcBanner project={project} stageKey="concepts" onChanged={reload} />
            {!out ? <Empty hint="Run Creative Director to generate 3 concept options." /> : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {out.map(c => {
                        const selected = stage?.chosen_concept_id === c.id || chosen === c.id;
                        return (
                            <div key={c.id} className={`card-base p-5 cursor-pointer transition-all ${selected ? "border-[#FF4400]" : "card-hover"}`}
                                 onClick={() => setChosen(c.id)} data-testid={`concept-${c.id}`}>
                                <div className="label-micro mb-2">{c.id.toUpperCase()}</div>
                                <div className="font-display text-xl tracking-tight">{c.name}</div>
                                <div className="text-sm text-zinc-300 mt-3">{c.big_idea}</div>
                                <div className="text-xs text-zinc-500 mt-3">Mood: <span className="text-zinc-300">{c.mood}</span></div>
                                <div className="text-xs text-zinc-500">Tone: <span className="text-zinc-300">{c.tone}</span></div>
                                <div className="text-xs text-zinc-500">Visual: <span className="text-zinc-300">{c.visual_style}</span></div>
                                <div className="text-xs text-zinc-500 mt-3">Hook: <span className="text-zinc-200">{c.hook}</span></div>
                                <div className="text-xs text-zinc-500 mt-1">Story: <span className="text-zinc-300">{c.storytelling_route}</span></div>
                                <div className="text-xs text-zinc-500 mt-1">Emotion: <span className="text-zinc-300">{c.emotional_angle}</span></div>
                            </div>
                        );
                    })}
                </div>
            )}
            {chosen && (
                <div className="mt-4 flex gap-2 items-center">
                    <span className="text-sm text-zinc-400">Selected: <span className="text-white font-mono">{chosen.toUpperCase()}</span></span>
                    <button data-testid="lock-concept" className="btn-primary text-xs px-3 py-1.5" onClick={async () => {
                        try {
                            // Phase 2C Preflight Wiring — call the new
                            // dedicated endpoint that (a) validates single-
                            // concept selection, (b) derives + saves the
                            // style_lock, and (c) syncs auto_pipeline so the
                            // CEO ticker doesn't go stale.
                            const { data } = await api.post(
                                `/projects/${project.id}/concepts/lock`,
                                { concept_ids: [chosen] }
                            );
                            toast.success(`Concept ${chosen.toUpperCase()} locked.`);
                            reload();
                        } catch (e) {
                            const detail = e?.response?.data?.detail;
                            const msg = (detail && (detail.message || detail.code))
                                ? detail.message || detail.code
                                : (e.userMessage || "Could not lock concept");
                            toast.error(msg);
                        }
                    }}><Sparkles className="w-3.5 h-3.5" /> Lock concept</button>
                </div>
            )}
            <ApprovalBar project={project} stage="concepts" onUpdated={reload} />
            <StageVersions project={project} stageKey="concepts" onRestored={reload} />
        </div>
    );
}

/* ============================ SCRIPT ============================ */
function ScriptPanel({ project, reload }) {
    const out = project.stages?.script?.output;
    // Support both new (scripts[]) and legacy (script) shapes.
    const scripts = Array.isArray(out?.scripts) && out.scripts.length
        ? out.scripts
        : (out?.script ? [{ ...out.script, variant: "performance", label: "Ad Script" }] : []);
    const chosen = project.stages?.concepts?.chosen_concept_id;

    const copyText = async (text, label) => {
        try {
            await navigator.clipboard.writeText(text);
            toast.success(`${label} copied to clipboard`);
        } catch {
            toast.error("Copy failed — long-press the script to copy manually.");
        }
    };

    const buildPlainText = (s) =>
        [`${(s.label || s.variant || "Ad Script")}\n`,
         `HOOK\n${s.hook || ""}\n`,
         `VOICEOVER\n${s.voiceover || ""}\n`,
         (s.dialogues || []).length ? `DIALOGUES\n${(s.dialogues || []).map(d => `${d.character || ""}: ${d.line || ""}`).join("\n")}\n` : "",
         (s.captions || []).length ? `CAPTIONS\n${(s.captions || []).map(c => `• ${c}`).join("\n")}\n` : "",
         `CTA\n${s.cta || ""}\n`,
        ].filter(Boolean).join("\n");

    const shareWhatsApp = (s) => {
        const body = encodeURIComponent(buildPlainText(s));
        window.open(`https://wa.me/?text=${body}`, "_blank", "noopener");
    };

    return (
        <div className="p-6 lg:p-10" data-testid="script-panel">
            <PanelHeader title="Script · 3 Variants" actions={[
                <RunAgentButton key="run" project={project} agent="script" label="Generate 3 scripts"
                    extra={{ chosen_concept_id: chosen, notes: "" }} onDone={reload} />,
                <DownloadBtn key="pdf-all" testid="dl-script-all"
                    path={`/exports/${project.id}/script.pdf`}
                    filename={`scripts-${project.id}.pdf`}
                    label="All scripts PDF" />,
            ]} />
            <StageError project={project} stageKey="script" />
            <StrategicQcBanner project={project} stageKey="script" onChanged={reload} />

            {/* Phase K-lite — Concept → Script → Storyboard mapping table.
                Lightweight per-variant approval; storyboard agent picks the first
                approved variant. Hidden when there are no scripts yet. */}
            {scripts.length > 0 && (
                <ScriptMappingTable
                    project={project}
                    scripts={scripts}
                    chosenConceptId={chosen}
                    reload={reload}
                />
            )}

            {!scripts.length ? (
                <Empty hint="Approve a concept, then click Generate 3 scripts. You'll get a Performance Ad, an Emotional Ad, and a Short Hook Ad." />
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    {scripts.map((s, i) => {
                        const variant = s.variant || `v${i + 1}`;
                        const label = s.label || (variant === "performance" ? "Performance Ad"
                                                  : variant === "emotional" ? "Emotional Ad"
                                                  : variant === "short_hook" ? "Short Hook Ad"
                                                  : `Variant ${i + 1}`);
                        return (
                            <div key={variant} className="card-base p-5 space-y-4 flex flex-col"
                                 data-testid={`script-variant-${variant}`}>
                                <div className="flex items-start justify-between gap-3">
                                    <div>
                                        <div className="label-micro mb-1">{variant.replace("_", " ")}</div>
                                        <div className="font-display text-xl">{label}</div>
                                    </div>
                                    <span className="text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 rounded-sm bg-[#FF4400]/10 text-[#FF4400] border border-[#FF4400]/30 shrink-0">
                                        {s.duration_seconds ? `${s.duration_seconds}s` : "—"}
                                    </span>
                                </div>
                                <div>
                                    <div className="label-micro mb-1">Hook</div>
                                    <div className="text-base font-display leading-snug">{s.hook}</div>
                                </div>
                                <div>
                                    <div className="label-micro mb-1">Voiceover</div>
                                    <div className="text-sm text-zinc-200 whitespace-pre-wrap leading-relaxed">{s.voiceover}</div>
                                </div>
                                {(s.dialogues || []).length > 0 && (
                                    <div>
                                        <div className="label-micro mb-1">Dialogues</div>
                                        <div className="space-y-1.5">
                                            {s.dialogues.map((d, di) => (
                                                <div key={di} className="text-sm border-b border-zinc-900 pb-1 last:border-b-0">
                                                    <span className="font-mono text-zinc-500 mr-2 uppercase text-xs">
                                                        {d.character || "VO"}
                                                    </span>{d.line}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {(s.captions || []).length > 0 && (
                                    <div>
                                        <div className="label-micro mb-1">Captions</div>
                                        <ul className="text-xs text-zinc-300 space-y-1">
                                            {s.captions.map((c, ci) => <li key={ci}>• {c}</li>)}
                                        </ul>
                                    </div>
                                )}
                                <div>
                                    <div className="label-micro mb-1">CTA</div>
                                    <div className="text-sm font-display text-zinc-100">{s.cta}</div>
                                </div>
                                {/* Action buttons — pinned to bottom of card */}
                                <div className="flex flex-wrap items-center gap-2 mt-auto pt-3 border-t border-zinc-900">
                                    <button
                                        type="button"
                                        onClick={() => copyText(buildPlainText(s), label)}
                                        data-testid={`copy-script-${variant}`}
                                        className="btn-secondary text-xs px-2.5 py-1.5"
                                    >
                                        <Clipboard className="w-3.5 h-3.5" /> Copy
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => shareWhatsApp(s)}
                                        data-testid={`whatsapp-script-${variant}`}
                                        className="btn-secondary text-xs px-2.5 py-1.5"
                                    >
                                        <Share2 className="w-3.5 h-3.5" /> WhatsApp
                                    </button>
                                    <button
                                        type="button"
                                        onClick={async () => {
                                            const filename = `script-${variant}-${project.id}.pdf`;
                                            const tid = toast.loading("Preparing PDF…", { duration: Infinity });
                                            try {
                                                const { blobUrl } = await prepareDownload(
                                                    `/exports/${project.id}/script.pdf?variant=${variant}`,
                                                    filename
                                                );
                                                toast.dismiss(tid);
                                                triggerSave(blobUrl, filename);
                                                toast.success(`${filename} ready`, { duration: 3000 });
                                            } catch (e) {
                                                toast.dismiss(tid);
                                                toast.error(e?.message || "Download failed");
                                            }
                                        }}
                                        data-testid={`pdf-script-${variant}`}
                                        className="btn-secondary text-xs px-2.5 py-1.5"
                                    >
                                        <Download className="w-3.5 h-3.5" /> PDF
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
            <ApprovalBar project={project} stage="script" onUpdated={reload} />
        </div>
    );
}

/* ============================ STORYBOARD ============================ */

/** Phase K-lite — Concept → Script → Storyboard mapping table.
 *  Compact, read-at-a-glance grid that makes the current state explicit:
 *    - Locked concept (or "no concept locked yet")
 *    - All script variants with per-variant Approve / Reject toggles
 *    - Live count of how many storyboards are eligible to be generated
 *    - Quick deeplink to the Storyboard tab once 1+ scripts are approved
 *  Storage: `project.stages.script.variant_approvals[<variant>] = "approved"|"rejected"|"draft"`.
 */
function ScriptMappingTable({ project, scripts, chosenConceptId, reload }) {
    const conceptsOut = (project.stages?.concepts?.output || {}).concepts || [];
    const conceptObj = conceptsOut.find(c => c.id === chosenConceptId) || null;
    const conceptLabel = conceptObj
        ? `${(chosenConceptId || "").toUpperCase()} · ${conceptObj.name || ""}`
        : (chosenConceptId ? chosenConceptId.toUpperCase() : "(no concept locked)");
    const approvals = project.stages?.script?.variant_approvals || {};
    const approvedCount = scripts.filter(s => (approvals[s.variant] || "").toLowerCase() === "approved").length;
    const [busyVariant, setBusyVariant] = useState(null);

    const setStatus = async (variant, status) => {
        setBusyVariant(variant);
        try {
            await api.post(`/projects/${project.id}/script/variant-approval`, { variant, status });
            toast.success(`${variant.replace("_", " ")} → ${status}`);
            reload && reload();
        } catch (e) {
            toast.error(`Update failed: ${e.userMessage || e.message}`);
        } finally {
            setBusyVariant(null);
        }
    };

    return (
        <div className="card-base p-4 mb-5" data-testid="script-mapping-table">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                <div>
                    <div className="label-micro">Concept → Script → Storyboard</div>
                    <div className="text-sm text-zinc-300 mt-1">
                        Locked concept: <span className="font-mono text-white">{conceptLabel}</span>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-[11px] font-mono uppercase tracking-wider text-zinc-500">
                        {approvedCount}/{scripts.length} approved
                    </span>
                    <span className="text-[11px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-sm border border-emerald-500/30 text-emerald-400">
                        {approvedCount} storyboard{approvedCount === 1 ? "" : "s"} eligible
                    </span>
                </div>
            </div>
            <div className="overflow-x-auto scrollbar-thin">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 border-b border-zinc-900">
                            <th className="text-left py-2 pr-3">Script</th>
                            <th className="text-left py-2 pr-3">Variant</th>
                            <th className="text-left py-2 pr-3">Duration</th>
                            <th className="text-left py-2 pr-3">Hook</th>
                            <th className="text-left py-2 pr-3">Status</th>
                            <th className="text-right py-2 pl-3">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {scripts.map((s, i) => {
                            const variant = s.variant || `v${i + 1}`;
                            const status = (approvals[variant] || "draft").toLowerCase();
                            const statusCls = status === "approved" ? "text-emerald-400 border-emerald-500/30"
                                : status === "rejected" ? "text-red-400 border-red-500/30"
                                : "text-zinc-500 border-zinc-800";
                            return (
                                <tr key={variant} className="border-b border-zinc-900 last:border-b-0"
                                    data-testid={`script-row-${variant}`}>
                                    <td className="py-2.5 pr-3 font-mono text-xs text-zinc-300">
                                        {(chosenConceptId || "?").toUpperCase()}-{i + 1}
                                    </td>
                                    <td className="py-2.5 pr-3">{s.label || variant}</td>
                                    <td className="py-2.5 pr-3 font-mono text-xs text-zinc-400">
                                        {s.duration_seconds ? `${s.duration_seconds}s` : "—"}
                                    </td>
                                    <td className="py-2.5 pr-3 text-zinc-300 max-w-xs truncate">
                                        {s.hook || ""}
                                    </td>
                                    <td className="py-2.5 pr-3">
                                        <span className={`px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider border rounded-sm ${statusCls}`}>
                                            {status}
                                        </span>
                                    </td>
                                    <td className="py-2.5 pl-3 text-right">
                                        <div className="inline-flex gap-1.5">
                                            <button
                                                type="button"
                                                disabled={busyVariant === variant}
                                                onClick={() => setStatus(variant, status === "approved" ? "draft" : "approved")}
                                                data-testid={`approve-script-${variant}`}
                                                className={`px-2.5 py-1 text-[11px] rounded-sm border ${status === "approved" ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-400" : "border-zinc-800 bg-zinc-950 hover:bg-zinc-900 text-zinc-200"} disabled:opacity-50`}
                                            >
                                                {status === "approved" ? "Approved ✓" : "Approve"}
                                            </button>
                                            <button
                                                type="button"
                                                disabled={busyVariant === variant}
                                                onClick={() => setStatus(variant, status === "rejected" ? "draft" : "rejected")}
                                                data-testid={`reject-script-${variant}`}
                                                className={`px-2.5 py-1 text-[11px] rounded-sm border ${status === "rejected" ? "border-red-500/50 bg-red-500/10 text-red-400" : "border-zinc-800 bg-zinc-950 hover:bg-zinc-900 text-zinc-200"} disabled:opacity-50`}
                                            >
                                                {status === "rejected" ? "Rejected ✗" : "Reject"}
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            {approvedCount > 0 && (
                <div className="mt-3 text-[11px] text-zinc-400 font-mono" data-testid="script-storyboard-hint">
                    {approvedCount === 1 ? (
                        <>
                            Tip: open the <span className="text-white">Storyboard</span> tab and click "Run Storyboard Agent".
                            The approved variant (<span data-testid="approved-variant-label">{(scripts.find(s => (approvals[s.variant]||"").toLowerCase()==="approved")?.label) || "—"}</span>) will be used as the source script.
                        </>
                    ) : (
                        <>
                            <span className="text-amber-300">⚠ {approvedCount} script variants are approved.</span>{" "}
                            Open the <span className="text-white">Storyboard</span> tab and use the
                            <span className="text-white"> Script picker</span> there to choose exactly one approved variant before running the storyboard agent.
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

/* Phase 2C Preflight Wiring — Active Style Lock panel.
 * Shows the persisted style_lock so beta users can see what visual
 * consistency block will be enforced on every storyboard shot. */
function ActiveStyleLockPanel({ project }) {
    const lock = project?.stages?.storyboard?.output?.storyboard?.style_lock
        || project?.stages?.concepts?.style_lock
        || null;
    if (!lock) return null;
    const fields = [
        ["art_style", "Art style"],
        ["realism_level", "Realism"],
        ["lighting", "Lighting"],
        ["camera_language", "Camera language"],
        ["environment", "Environment"],
        ["character_identity", "Character identity"],
        ["brand_treatment", "Brand treatment"],
        ["do_not_mix", "Mixing rule"],
    ];
    return (
        <div className="card-base p-4 mb-4 border-emerald-500/30 bg-emerald-500/[0.04]"
             data-testid="active-style-lock-panel">
            <div className="flex items-center gap-2 mb-3">
                <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-sm border border-emerald-500/40 text-emerald-300 bg-emerald-500/10">
                    Active style lock
                </span>
                <span className="text-xs text-zinc-500">
                    Applied to every storyboard shot.
                </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5 text-xs">
                {fields.map(([k, label]) => lock[k] ? (
                    <div key={k} data-testid={`style-lock-${k}`}>
                        <span className="text-zinc-500">{label}:</span>{" "}
                        <span className="text-zinc-200">{lock[k]}</span>
                    </div>
                ) : null)}
            </div>
        </div>
    );
}

/* Phase 2C Preflight Wiring — multi-script picker for the storyboard panel.
 * When 2+ script variants are approved, the storyboard agent refuses to run
 * without an explicit selection. This dropdown calls
 * POST /api/projects/{id}/storyboard/select-script. */
function StoryboardScriptPicker({ project, reload }) {
    const [picking, setPicking] = useState(false);
    const approvals = project?.stages?.script?.variant_approvals || {};
    const variants = project?.stages?.script?.output?.scripts || [];
    const approvedVariants = variants.filter(s =>
        (approvals[s.variant] || "").toLowerCase() === "approved"
    );
    const current = project?.stages?.storyboard?.parent_script_id || "";
    if (approvedVariants.length < 2) return null; // single-approved is automatic
    const choose = async (variantId) => {
        setPicking(true);
        try {
            await api.post(`/projects/${project.id}/storyboard/select-script`,
                            { parent_script_id: variantId });
            toast.success(`Storyboard will use ${variantId.toUpperCase()}.`);
            reload && reload();
        } catch (e) {
            toast.error(e?.userMessage || "Could not save script selection.");
        } finally { setPicking(false); }
    };
    return (
        <div className="card-base p-4 mb-4 border-amber-500/30 bg-amber-500/[0.04]"
             data-testid="multi-script-picker">
            <div className="flex items-center gap-2 mb-3">
                <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-sm border border-amber-500/40 text-amber-300 bg-amber-500/10">
                    Pick a script
                </span>
                <span className="text-xs text-zinc-400">
                    {approvedVariants.length} variants are approved.
                    Choose exactly one to storyboard.
                </span>
            </div>
            <div className="flex flex-wrap gap-2">
                {approvedVariants.map(v => (
                    <button
                        key={v.variant}
                        type="button"
                        disabled={picking}
                        onClick={() => choose(v.variant)}
                        data-testid={`pick-script-${v.variant}`}
                        className={`btn-secondary text-xs ${current === v.variant
                            ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200"
                            : ""}`}
                    >
                        {v.label || v.variant}{current === v.variant ? " ✓" : ""}
                    </button>
                ))}
            </div>
        </div>
    );
}

function StoryboardPanel({ project, reload }) {
    const out = project.stages?.storyboard?.output?.storyboard;
    const promptsByShot = useMemo(() => {
        const arr = project.stages?.prompts?.output?.prompts || [];
        const map = {};
        arr.forEach(p => { if (p.shot_id) map[p.shot_id] = p; });
        return map;
    }, [project]);
    // Storyboard Continuity Audit — fetch the production-continuity lock
    // ONCE per StoryboardPanel mount. Shared with ShotMetadataChips so
    // every shot can resolve character_ids/environment_id to NAMES and
    // render reference_asset_ids as real thumbnails.
    const { lookup: continuityLookup, loaded: continuityLoaded } = useContinuityLookup(project.id);
    const [genBusy, setGenBusy] = useState(false);
    // Reference Image Preview / Lightbox Fix — single lightbox shared by
    // ContinuityReferencesHeader and per-shot reference thumbnails.
    const [lightbox, setLightbox] = useState({ open: false, assetId: null, title: "" });
    const openAsset = useCallback(({ assetId, title }) => {
        if (!assetId) return;
        setLightbox({ open: true, assetId, title: title || "" });
    }, []);
    const closeLightbox = useCallback(() => {
        setLightbox((prev) => ({ ...prev, open: false }));
    }, []);
    const generate = async () => {
        setGenBusy(true);
        toast.message("Generating visual storyboard — ~10s per shot…", { duration: 4000 });
        try {
            const { data } = await api.post("/storyboard/visual/generate", { project_id: project.id });
            if (data.failed_count && !data.generated_count) {
                toast.message(`No images generated — copy prompts or upload references per panel.`, { duration: 6000 });
            } else if (data.failed_count) {
                toast.message(`${data.generated_count} generated · ${data.failed_count} pending. Regenerate or upload for missing panels.`, { duration: 6000 });
            } else {
                toast.success(`Generated ${data.generated_count} image(s).`);
            }
            reload && reload();
        } catch (e) {
            toast.error(e?.userMessage || "Visual storyboard failed to start.");
        } finally { setGenBusy(false); }
    };
    return (
        <div className="p-6 lg:p-10" data-testid="storyboard-panel">
            <PanelHeader title="Storyboard" actions={[
                <RunAgentButton key="run" project={project} agent="storyboard" label="Run Storyboard Agent" onDone={reload} />,
                <button key="visual" data-testid="visual-storyboard-gen" disabled={genBusy || !out} onClick={generate} className="btn-accent text-xs px-3 py-1.5 disabled:opacity-50">
                    <ImageIcon className="w-3.5 h-3.5" /> {genBusy ? "Generating…" : "Generate visuals"}
                </button>,
                <DownloadBtn key="pdf" testid="dl-storyboard" path={`/exports/${project.id}/storyboard.pdf`} filename={`storyboard-${project.id}.pdf`} label="Storyboard PDF" />,
            ]} />
            <StageError project={project} stageKey="storyboard" />
            <StrategicQcBanner project={project} stageKey="storyboard" onChanged={reload} />
            {/* Storyboard Continuity Audit — DPS Required banner.
               Renders when the auto pipeline has paused at the storyboard
               stage waiting for an approved Detailed Production Script.
               Scroll-only CTA — never triggers any AI/LLM call. */}
            <DpsRequiredBanner project={project} />
            {/* iter70 — Detailed Production Script + Character Dialogue
               Map. Renders ABOVE Production Continuity so the operator
               can expand the approved short script into a shoot-ready
               breakdown, then promote suggested characters/environments
               into the Continuity Bible below. Additive only — never
               required to run Storyboard. */}
            <DetailedProductionScriptPanel projectId={project.id} onHandoffCreated={reload} />
            {/* iter69 — Production Continuity / Reference Lock panel.
               Renders between Script approval (above) and Storyboard
               script picker (below). Warning-based gate only — never
               hard-blocks Storyboard. */}
            <ProductionContinuityPanel projectId={project.id} />
            {/* Phase 2C Preflight Wiring — surface Active Style Lock + multi-script picker. */}
            <StoryboardScriptPicker project={project} reload={reload} />
            <ActiveStyleLockPanel project={project} />
            {/* Storyboard Continuity Audit — Continuity References Used header.
               Renders when storyboard output exists. Proves which approved
               characters / environments / reference images informed the
               agent run. Empty-state surfaces an amber warning. */}
            {out && (
                <ContinuityReferencesHeader project={project} lookup={continuityLookup} loaded={continuityLoaded} onOpenAsset={openAsset} />
            )}
            {!out ? <Empty hint="Approve the script, then run the Storyboard Agent." /> : (
                <div className="space-y-6">
                    {/* Phase K-lite — explicit mapping breadcrumb so users know
                        which Concept + Script this storyboard was generated from. */}
                    {(out.parent_concept_id || out.script_variant_label || out.script_variant_id) && (
                        <div className="card-base p-3 flex items-center gap-3 flex-wrap" data-testid="storyboard-mapping-crumb">
                            <span className="label-micro">Generated for</span>
                            {out.parent_concept_id && (
                                <span className="text-xs font-mono px-2 py-0.5 rounded-sm border border-zinc-800 text-zinc-300">
                                    Concept: {String(out.parent_concept_id).toUpperCase()}
                                </span>
                            )}
                            {(out.script_variant_label || out.script_variant_id) && (
                                <span className="text-xs font-mono px-2 py-0.5 rounded-sm border border-[#FF4400]/40 bg-[#FF4400]/10 text-[#FF4400]">
                                    Script: {out.script_variant_label || out.script_variant_id}
                                </span>
                            )}
                        </div>
                    )}
                    <div className="flex items-center justify-between">
                        <div className="text-sm text-zinc-500 font-mono">Total duration: {out.total_duration_seconds}s</div>
                        <div className="text-[10px] uppercase tracking-wider font-mono text-zinc-600">
                            Tip: if AI is down, copy the prompt to Midjourney/Flow or upload a reference image per panel.
                        </div>
                    </div>
                    {(out.scenes || []).map(scene => (
                        <div key={scene.scene_number} className="card-base p-5">
                            <div className="flex items-center justify-between mb-4">
                                <div className="label-micro">Scene {scene.scene_number}</div>
                                <div className="text-zinc-300 text-sm">{scene.title}</div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {(scene.shots || []).map(shot => (
                                    <ShotPanel
                                        key={shot.shot_id}
                                        project={project}
                                        shot={shot}
                                        promptInfo={promptsByShot[shot.shot_id]}
                                        onUpdated={reload}
                                        continuityLookup={continuityLookup}
                                        onOpenAsset={openAsset}
                                    />
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}
            <ApprovalBar project={project} stage="storyboard" onUpdated={reload} />
            <StageVersions project={project} stageKey="storyboard" onRestored={reload} />
            {/* Reference Image Preview / Lightbox Fix — single shared modal. */}
            <ImageLightbox
                open={lightbox.open}
                assetId={lightbox.assetId}
                title={lightbox.title}
                onClose={closeLightbox}
            />
        </div>
    );
}

/* ---------------- ShotPanel (single storyboard frame) ---------------- */
function ShotPanel({ project, shot, promptInfo, onUpdated, continuityLookup, onOpenAsset }) {
    const token = typeof window !== "undefined" ? localStorage.getItem("aivos_token") || "" : "";
    const fileRef = useRef(null);
    const [busy, setBusy] = useState(false);
    const [regenBusy, setRegenBusy] = useState(false);
    // iter66 — Director Notes regeneration modal state.
    const [directorOpen, setDirectorOpen] = useState(false);
    const [directorBusy, setDirectorBusy] = useState(false);
    const [compareOpen, setCompareOpen] = useState(false);

    const imagePrompt = shot.image_prompt || promptInfo?.image_prompt || shot.visual_description || "";
    const videoPrompt = shot.video_prompt || promptInfo?.video_prompt || "";

    const status = shot.visual_status || (shot.visual_storage_path ? "ready" : "pending");
    const source = shot.visual_source || (shot.visual_storage_path ? "ai" : null);
    const hasImage = !!shot.visual_storage_path;
    const isFailed = status === "failed" || source === "ai_failed";
    const isUserSupplied = source === "user";

    const copy = async (text, label) => {
        if (!text) { toast.message("Nothing to copy."); return; }
        try {
            await navigator.clipboard.writeText(text);
            toast.success(`${label} copied`);
        } catch {
            toast.error("Copy failed");
        }
    };

    const onPickFile = () => fileRef.current?.click();

    const onUpload = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        if (!file.type.startsWith("image/")) { toast.error("Please select an image file"); return; }
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("project_id", project.id);
            fd.append("shot_id", shot.shot_id);
            await api.post("/storyboard/visual/upload-panel", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success("Reference image attached.");
            onUpdated && onUpdated();
        } catch (err) {
            toast.error(err?.userMessage || "Upload failed.");
        } finally { setBusy(false); }
    };

    const regenerate = async () => {
        if (isUserSupplied && !window.confirm("This panel has a user-supplied image. Regenerate with AI and overwrite it?")) {
            return;
        }
        setRegenBusy(true);
        try {
            // clear existing image first so AI can repopulate
            if (hasImage) {
                await api.post("/storyboard/visual/clear-panel", {
                    project_id: project.id, shot_id: shot.shot_id,
                });
            }
            toast.message("Regenerating AI image…", { duration: 3000 });
            const { data } = await api.post("/storyboard/visual/generate", {
                project_id: project.id, shot_ids: [shot.shot_id],
            });
            if (data.failed_count) {
                toast.error("AI image provider is still unavailable. Use 'Upload reference' or copy the prompt.");
            } else {
                toast.success("Regenerated.");
            }
            onUpdated && onUpdated();
        } catch (err) {
            toast.error(err?.userMessage || "Regenerate failed.");
        } finally { setRegenBusy(false); }
    };

    // iter66 — Storyboard Director Regeneration. POSTs to the iter65
    // backend endpoint. Original v1 image is preserved; a new versioned
    // asset row + regen row are created. The operator picks the version
    // they prefer via Compare Versions.
    const submitDirectorRegen = async (note, refs) => {
        setDirectorBusy(true);
        try {
            await api.post(
                `/projects/${project.id}/storyboard/shots/${shot.shot_id}/regenerate-with-notes`,
                {
                    raw_director_note: note,
                    uploaded_reference_asset_ids: refs || [],
                },
            );
            toast.success("New storyboard version queued. Compare versions when ready.");
            setDirectorOpen(false);
            onUpdated && onUpdated();
        } catch (err) {
            const d = err?.response?.data?.detail;
            if (d?.error === "raw_director_note_required") {
                toast.error("Please type a director note before regenerating.");
            } else {
                toast.error(err?.userMessage || "Regenerate failed");
            }
        } finally { setDirectorBusy(false); }
    };

    /* ---- visual frame ---- */
    let frame;
    if (hasImage) {
        frame = (
            <div className="relative aspect-video bg-black rounded-sm overflow-hidden border border-zinc-800 group">
                <img
                    src={`${API}/files/${shot.visual_storage_path}?auth=${encodeURIComponent(token)}`}
                    alt={`Shot ${shot.shot_number}`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                />
                <span className={`absolute top-2 left-2 text-[9px] uppercase tracking-wider font-mono px-2 py-0.5 rounded-sm ${isUserSupplied ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/30" : "bg-black/70 text-zinc-300 border border-zinc-700"}`}>
                    {isUserSupplied ? "User reference" : "AI image"}
                </span>
            </div>
        );
    } else {
        // Stylized fallback storyboard frame
        frame = (
            <div className="relative aspect-video rounded-sm overflow-hidden border border-[#FF4400]/40"
                 style={{
                     background:
                         "radial-gradient(ellipse at 30% 20%, rgba(255,68,0,0.12), transparent 60%), " +
                         "radial-gradient(ellipse at 80% 90%, rgba(255,68,0,0.08), transparent 55%), " +
                         "linear-gradient(135deg, #0c0c0c 0%, #181818 100%)",
                 }}
                 data-testid={`shot-fallback-${shot.shot_id}`}>
                {/* grain */}
                <div className="absolute inset-0 opacity-[0.07] pointer-events-none"
                     style={{
                         backgroundImage:
                             "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/><feColorMatrix values='0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.6 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
                     }} />
                {/* dashed composition guides (rule-of-thirds) */}
                <div className="absolute inset-3 border border-dashed border-zinc-700/60 rounded-sm pointer-events-none" />
                <div className="absolute left-1/3 top-3 bottom-3 w-px bg-zinc-700/40 pointer-events-none" />
                <div className="absolute left-2/3 top-3 bottom-3 w-px bg-zinc-700/40 pointer-events-none" />
                <div className="absolute top-1/3 left-3 right-3 h-px bg-zinc-700/40 pointer-events-none" />
                <div className="absolute top-2/3 left-3 right-3 h-px bg-zinc-700/40 pointer-events-none" />
                {/* corner crops */}
                {["top-2 left-2 border-t border-l", "top-2 right-2 border-t border-r",
                  "bottom-2 left-2 border-b border-l", "bottom-2 right-2 border-b border-r"].map((c, i) => (
                    <div key={i} className={`absolute w-4 h-4 ${c} border-[#FF4400]/70 pointer-events-none`} />
                ))}
                <div className="absolute inset-0 p-4 flex flex-col justify-between">
                    <div className="flex items-start justify-between gap-3">
                        <div>
                            <div className="text-[9px] uppercase tracking-wider font-mono text-[#FF4400]">
                                Shot {shot.shot_number} · {shot.shot_id}
                            </div>
                            <div className="text-sm font-display text-zinc-100 mt-1 line-clamp-2">
                                {shot.subject || shot.action || "Untitled shot"}
                            </div>
                        </div>
                        <span className="text-[9px] uppercase tracking-wider font-mono text-zinc-300 bg-black/60 border border-zinc-700 px-2 py-0.5 rounded-sm shrink-0">
                            {shot.duration_seconds || "—"}s
                        </span>
                    </div>
                    <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[10px] font-mono text-zinc-400">
                        <div><span className="text-zinc-600">Cam:</span> <span className="text-zinc-200">{shot.camera_movement || "—"}</span></div>
                        <div><span className="text-zinc-600">Light:</span> <span className="text-zinc-200">{shot.lighting || "—"}</span></div>
                        <div><span className="text-zinc-600">Mood:</span> <span className="text-zinc-200">{shot.emotion || "—"}</span></div>
                        <div><span className="text-zinc-600">Trans:</span> <span className="text-zinc-200">{shot.transition || "—"}</span></div>
                    </div>
                </div>
                {isFailed && (
                    <span className="absolute top-2 right-2 text-[9px] uppercase tracking-wider font-mono px-2 py-0.5 rounded-sm bg-amber-500/15 text-amber-300 border border-amber-500/30">
                        Pending visual — regenerate or upload
                    </span>
                )}
            </div>
        );
    }

    return (
        <div className="border border-zinc-800 rounded-sm bg-[#0a0a0a]" data-testid={`shot-${shot.shot_id}`}>
            <div className="p-4 space-y-3">
                {frame}

                {/* Actions row */}
                <div className="flex flex-wrap items-center gap-2 pt-1">
                    <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onUpload}
                           data-testid={`upload-file-${shot.shot_id}`} />
                    <button
                        type="button"
                        disabled={busy}
                        onClick={onPickFile}
                        data-testid={`upload-ref-${shot.shot_id}`}
                        className="btn-secondary text-xs px-3 py-1.5 disabled:opacity-50"
                    >
                        <Upload className="w-3.5 h-3.5" />
                        {busy ? "Uploading…" : (hasImage ? "Replace reference" : "Upload reference")}
                    </button>
                    <button
                        type="button"
                        disabled={regenBusy}
                        onClick={regenerate}
                        data-testid={`regen-${shot.shot_id}`}
                        className="btn-secondary text-xs px-3 py-1.5 disabled:opacity-50"
                    >
                        <RefreshCcw className="w-3.5 h-3.5" />
                        {regenBusy ? "Working…" : "Regenerate AI image"}
                    </button>
                    {/* iter66 — Storyboard Director Regeneration. New version
                        is created; original v1 is never overwritten. */}
                    <button
                        type="button"
                        onClick={() => setDirectorOpen(true)}
                        data-testid={`storyboard-director-regen-${shot.shot_id}`}
                        className="text-xs px-3 py-1.5 rounded-sm border border-amber-500/40 bg-amber-500/10 text-amber-100 hover:bg-amber-500/15 inline-flex items-center gap-1.5"
                        title="Type a director note and create a new storyboard version with the change applied. v1 stays preserved."
                    >
                        <Sparkles className="w-3.5 h-3.5" />
                        Regenerate with Director Input
                    </button>
                    <button
                        type="button"
                        onClick={() => setCompareOpen(true)}
                        data-testid={`storyboard-compare-${shot.shot_id}`}
                        className="text-xs px-3 py-1.5 rounded-sm border border-zinc-800 text-zinc-300 hover:text-zinc-100 inline-flex items-center gap-1.5"
                        title="See all versions and pick the one to approve to QC."
                    >
                        <History className="w-3.5 h-3.5" />
                        Compare
                    </button>
                </div>

                {/* shot metadata */}
                <div className="text-sm text-zinc-100 leading-relaxed">{shot.visual_description}</div>
                <ul className="text-xs text-zinc-400 grid grid-cols-2 gap-x-3 gap-y-1">
                    <li><span className="text-zinc-600">Subject:</span> {shot.subject || "—"}</li>
                    <li><span className="text-zinc-600">Env:</span> {shot.environment || "—"}</li>
                    <li><span className="text-zinc-600">Action:</span> {shot.action || "—"}</li>
                    <li><span className="text-zinc-600">Audio:</span> {shot.audio_notes || "—"}</li>
                </ul>

                {/* iter71 — optional shot metadata chips. Render only
                   when at least one iter71 field has data so old
                   storyboards (no metadata) stay visually unchanged. */}
                <ShotMetadataChips shot={shot} lookup={continuityLookup} onOpenAsset={onOpenAsset} />

                {/* Prompt blocks with copy */}
                <PromptBlock label="Image prompt" value={imagePrompt} testid={`img-prompt-${shot.shot_id}`}
                             onCopy={() => copy(imagePrompt, "Image prompt")} />
                {videoPrompt && (
                    <PromptBlock label="Video prompt" value={videoPrompt} testid={`vid-prompt-${shot.shot_id}`}
                                 onCopy={() => copy(videoPrompt, "Video prompt")} />
                )}
                {isFailed && (
                    <div className="text-[11px] font-mono text-amber-300/80 bg-amber-500/5 border border-amber-500/20 rounded-sm p-2">
                        {shot.visual_error || "Visual not yet generated. Click Regenerate to retry, or Upload reference."}
                    </div>
                )}
            </div>
            {/* iter66 — Director Regeneration modal + Version Compare modal */}
            <RegenerateWithNotesModal
                open={directorOpen}
                target="storyboard"
                shotId={shot.shot_id}
                busy={directorBusy}
                onClose={() => setDirectorOpen(false)}
                onConfirm={submitDirectorRegen}
            />
            <VersionCompareModal
                open={compareOpen}
                api={api}
                projectId={project.id}
                target="storyboard"
                shotId={shot.shot_id}
                onApproved={() => onUpdated?.()}
                onClose={() => setCompareOpen(false)}
            />
        </div>
    );
}

function PromptBlock({ label, value, onCopy, testid }) {
    return (
        <div data-testid={testid}>
            <div className="flex items-center justify-between">
                <div className="label-micro">{label}</div>
                <button onClick={onCopy} className="text-[10px] uppercase tracking-wider font-mono text-zinc-500 hover:text-[#FF4400] flex items-center gap-1"
                        data-testid={`${testid}-copy`}>
                    <Clipboard className="w-3 h-3" /> Copy
                </button>
            </div>
            <div className="text-xs font-mono text-zinc-300 bg-black border border-zinc-900 p-2.5 rounded-sm mt-1 whitespace-pre-wrap break-words max-h-32 overflow-y-auto">
                {value || "—"}
            </div>
        </div>
    );
}

/* iter71 — Optional shot-level metadata chips. Renders below the
   classic shot fields. Each chip renders ONLY when the corresponding
   metadata field has a non-empty value, so legacy storyboards (no
   iter71 fields) stay visually unchanged. */
function ShotMetadataChips({ shot, lookup, onOpenAsset }) {
    if (!shot || typeof shot !== "object") return null;
    const charIds = Array.isArray(shot.character_ids) ? shot.character_ids.filter(Boolean) : [];
    const envId = shot.environment_id || "";
    const dur = Number(shot.shot_duration || 0);
    const cam = shot.camera_movement || "";
    const difficulty = Number(shot.ai_video_difficulty_score || 0);
    const hint = shot.provider_suitability_hint || "";
    const stype = shot.shot_type || "";
    const lens = shot.lens_suggestion || "";
    const trans = shot.transition || "";
    const notes = shot.continuity_notes || "";
    const refs = Array.isArray(shot.reference_asset_ids) ? shot.reference_asset_ids.filter(Boolean) : [];

    // Storyboard Continuity Audit — resolve UUIDs → readable names when
    // the lookup is available. Falls back to the raw id (legacy display)
    // so storyboards generated before the lookup existed still render.
    const charLabels = charIds.map((id) => lookup?.charById?.[id]?.name || id);
    const envLabel = envId ? (lookup?.envById?.[envId]?.name || envId) : "";

    const chips = [];
    if (charLabels.length) chips.push({ k: "chars", label: charLabels.join(", "), kind: "char", title: `Characters in this shot · ${charIds.join(", ")}` });
    if (envLabel) chips.push({ k: "env", label: envLabel, kind: "env", title: `Environment · ${envId}` });
    if (dur > 0) chips.push({ k: "dur", label: `${dur}s`, kind: "dur", title: "Shot duration (seconds)" });
    if (stype) chips.push({ k: "type", label: stype.replace(/_/g, " "), kind: "type", title: "Shot type" });
    if (cam) chips.push({ k: "cam", label: cam, kind: "cam", title: "Camera movement" });
    if (lens) chips.push({ k: "lens", label: lens, kind: "lens", title: "Lens suggestion" });
    if (trans) chips.push({ k: "trans", label: `→ ${trans}`, kind: "trans", title: "Transition to next shot" });
    if (difficulty > 0) chips.push({
        k: "diff",
        label: `AI ${"●".repeat(difficulty)}${"○".repeat(Math.max(0, 5 - difficulty))} (${difficulty}/5)`,
        kind: difficulty >= 4 ? "diff-hard" : "diff",
        title: "AI video generation difficulty (1=easy, 5=hard)",
    });
    if (hint) chips.push({ k: "hint", label: hint, kind: "hint", title: "Provider suitability hint" });
    if (refs.length) chips.push({ k: "refs", label: `${refs.length} ref${refs.length > 1 ? "s" : ""}`, kind: "ref", title: refs.join(", ") });

    if (!chips.length) return null;

    const tone = {
        char: "border-emerald-500/40 text-emerald-200 bg-emerald-500/5",
        env: "border-sky-500/40 text-sky-200 bg-sky-500/5",
        dur: "border-zinc-700 text-zinc-300 bg-zinc-900",
        type: "border-zinc-700 text-zinc-300 bg-zinc-900",
        cam: "border-zinc-700 text-zinc-300 bg-zinc-900",
        lens: "border-zinc-700 text-zinc-300 bg-zinc-900",
        trans: "border-zinc-700 text-zinc-300 bg-zinc-900",
        diff: "border-amber-500/30 text-amber-200 bg-amber-500/5",
        "diff-hard": "border-red-500/40 text-red-200 bg-red-500/10",
        hint: "border-violet-500/40 text-violet-200 bg-violet-500/5",
        ref: "border-amber-500/40 text-amber-200 bg-amber-500/5",
    };
    return (
        <div className="flex flex-wrap gap-1.5 pt-1" data-testid={`shot-meta-${shot.shot_id || "shot"}`}>
            {chips.map((c) => (
                <span
                    key={c.k}
                    title={c.title}
                    className={`text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${tone[c.kind] || tone.dur}`}
                    data-testid={`shot-meta-${c.k}-${shot.shot_id || "shot"}`}
                >
                    {c.label}
                </span>
            ))}
            {/* Storyboard Continuity Audit — per-shot reference asset thumbnails. */}
            {refs.length > 0 && (
                <div
                    className="flex items-center gap-1.5 basis-full mt-1"
                    data-testid={`shot-meta-thumbs-${shot.shot_id || "shot"}`}
                >
                    <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mr-1">refs:</span>
                    {refs.slice(0, 6).map((aid) => (
                        <ContinuityThumb key={aid} assetId={aid} size={28} onOpen={onOpenAsset} title={`Shot ${shot.shot_id || ""} reference`} />
                    ))}
                    {refs.length > 6 && (
                        <span className="text-[10px] font-mono text-zinc-500">+{refs.length - 6}</span>
                    )}
                </div>
            )}
            {notes && (
                <div
                    className="text-[10px] text-zinc-400 italic basis-full mt-0.5"
                    data-testid={`shot-meta-notes-${shot.shot_id || "shot"}`}
                    title="Continuity notes"
                >
                    {notes}
                </div>
            )}
        </div>
    );
}

/* ============================ PROMPTS ============================ */
function PromptsPanel({ project, reload }) {
    const out = project.stages?.prompts?.output?.prompts;
    const copy = (text) => { navigator.clipboard.writeText(text); toast.success("Copied"); };
    return (
        <div className="p-6 lg:p-10" data-testid="prompts-panel">
            <PanelHeader title="Prompt Pack · Library" actions={[
                <RunAgentButton key="run" project={project} agent="prompt_engineer" label="Run Prompt Engineer" onDone={reload} />,
                <DownloadBtn key="csv" testid="dl-prompts" path={`/exports/${project.id}/prompts.csv`} filename={`prompts-${project.id}.csv`} label="Prompts CSV" />,
            ]} />
            <StageError project={project} stageKey="prompts" />
            {!out ? <Empty hint="Approve the storyboard, then run the Prompt Engineer." /> : (
                <div className="space-y-4">
                    {out.map((p, i) => (
                        <div key={i} className="card-base p-5">
                            <div className="flex items-center justify-between mb-3">
                                <div className="label-micro">Shot {p.shot_id}</div>
                                <button onClick={() => copy(p.video_prompt || p.image_prompt || "")} data-testid={`copy-${p.shot_id}`} className="btn-secondary text-xs px-3 py-1.5">
                                    <Copy className="w-3.5 h-3.5" /> Copy main
                                </button>
                            </div>
                            <PromptField label="Image prompt" value={p.image_prompt} onCopy={copy} />
                            <PromptField label="Video prompt" value={p.video_prompt} onCopy={copy} />
                            <PromptField label="Google Flow" value={p.google_flow_prompt} onCopy={copy} />
                            <PromptField label="Higgsfield" value={p.higgsfield_prompt} onCopy={copy} />
                            <PromptField label="Negative prompt" value={p.negative_prompt} onCopy={copy} />
                            <div className="text-xs text-zinc-500 mt-2">Camera: <span className="text-zinc-300">{p.camera_movement}</span></div>
                            <div className="text-xs text-zinc-500">Lighting: <span className="text-zinc-300">{p.lighting_style}</span></div>
                            <div className="text-xs text-zinc-500">Continuity: <span className="text-zinc-300">{p.continuity_lock}</span></div>
                            <div className="text-xs text-zinc-500">Brand: <span className="text-zinc-300">{p.brand_consistency_notes}</span></div>
                            <div className="text-xs text-zinc-500 mt-2 italic">Correction template: {p.correction_prompt_template}</div>
                        </div>
                    ))}
                </div>
            )}
            <ApprovalBar project={project} stage="prompts" onUpdated={reload} />
        </div>
    );
}

function PromptField({ label, value, onCopy }) {
    return (
        <div className="mt-3">
            <div className="flex items-center justify-between">
                <div className="label-micro">{label}</div>
                <button onClick={() => onCopy(value || "")} className="text-xs text-zinc-500 hover:text-white"><Clipboard className="w-3 h-3 inline-block mr-1" />copy</button>
            </div>
            <div className="text-xs font-mono text-zinc-300 bg-black border border-zinc-900 p-3 rounded-sm mt-1 whitespace-pre-wrap">{value || "—"}</div>
        </div>
    );
}

/* ============================ VIDEO WORKFLOW (Phase I) ============================ */
/* Beginner-friendly Google Flow / Runway guide. Per-shot prompt + reference
   instruction + continuation rule. NO video generation — prompts only. */
function VideoWorkflowPanel({ project }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [copiedId, setCopiedId] = useState(null);

    useEffect(() => {
        setLoading(true);
        api.get(`/projects/${project.id}/video-workflow`)
            .then(({ data }) => setData(data))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [project.id]);

    const copy = async (txt, key) => {
        try {
            await navigator.clipboard.writeText(txt);
            setCopiedId(key);
            toast.success("Prompt copied");
            setTimeout(() => setCopiedId(null), 1500);
        } catch {
            toast.error("Clipboard blocked");
        }
    };

    if (loading) return <div className="p-10 text-zinc-500">Loading…</div>;
    if (!data) return <div className="p-10 text-zinc-500">Not available.</div>;

    return (
        <div className="p-6 lg:p-10" data-testid="video-workflow-panel">
            <PanelHeader title={`Sequence Planning (Advanced) · ${data.platform_name}`}
                hint="Copy-ready prompts + step-by-step guide. We don't call the video API — you stay in control of cost." />

            {/* Summary */}
            <div className="card-base p-5 mb-4">
                <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                    <div className="label-micro">Production lock</div>
                    <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">
                        {data.shot_count} shot{data.shot_count === 1 ? "" : "s"} · {data.duration}
                    </span>
                </div>
                <div className="flex flex-wrap gap-2">
                    {Object.entries(data.visual_lock || {}).filter(([, v]) => v).map(([k, v]) => (
                        <span key={k} className="text-[11px] border border-orange-500/30 bg-orange-500/5 text-orange-200 rounded-sm px-2 py-1">
                            {k.replace(/_/g, " ")}: {v}
                        </span>
                    ))}
                    {Object.entries(data.global_style_lock || {}).filter(([, v]) => v).map(([k, v]) => (
                        <span key={k} className="text-[11px] border border-zinc-700 text-zinc-300 rounded-sm px-2 py-1">
                            {k.replace(/_/g, " ")}: {v}
                        </span>
                    ))}
                    {Object.keys(data.visual_lock || {}).filter(k => data.visual_lock[k]).length === 0
                        && Object.keys(data.global_style_lock || {}).filter(k => data.global_style_lock[k]).length === 0 && (
                        <span className="text-[11px] text-zinc-500">
                            No visual/style lock set. Set them in New Project or the Settings tab for maximum consistency.
                        </span>
                    )}
                </div>
            </div>

            {/* Steps */}
            <div className="card-base p-5 mb-4">
                <div className="label-micro mb-2">How to use {data.platform_name}</div>
                <ol className="list-decimal list-inside space-y-1.5 text-sm text-zinc-200">
                    {data.steps.map((s, i) => <li key={i}>{s.replace(/^Step \d+\s*[—-]\s*/, "")}</li>)}
                </ol>
                {data.platform_tips?.length > 0 && (
                    <div className="mt-4 text-xs text-zinc-400 space-y-1 border-t border-zinc-900 pt-3">
                        {data.platform_tips.map((t, i) => <div key={i}>• {t}</div>)}
                    </div>
                )}
            </div>

            {!data.has_storyboard && (
                <div className="card-base p-5 border border-amber-500/30 bg-amber-500/5 text-amber-200 text-sm">
                    No storyboard yet. Run the storyboard stage first — each shot will then appear here with its own copy-ready prompt.
                </div>
            )}

            {/* Per-shot */}
            <div className="space-y-3" data-testid="vw-shots">
                {data.shots.map((s) => (
                    <div key={s.shot_id} className="card-base p-5">
                        <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                            <div>
                                <div className="label-micro">Shot {s.shot_number} · {s.duration_hint}</div>
                                <div className="text-sm text-zinc-200 mt-1">{s.scene}</div>
                            </div>
                            <button
                                data-testid={`vw-copy-${s.shot_id}`}
                                onClick={() => copy(s.prompt, s.shot_id)}
                                className="btn-secondary text-xs px-3 py-1.5">
                                {copiedId === s.shot_id ? "Copied" : "Copy prompt"}
                            </button>
                        </div>
                        <div className="mt-2 text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Prompt (copy into {data.platform_name})</div>
                        <pre className="bg-zinc-950/60 border border-zinc-800 rounded-sm p-3 text-xs text-zinc-100 whitespace-pre-wrap leading-relaxed">{s.prompt}</pre>
                        <div className="grid md:grid-cols-2 gap-3 mt-3">
                            <div className="border border-zinc-800 rounded-sm p-3 bg-zinc-950/40">
                                <div className="text-[10px] uppercase tracking-wider text-orange-300 mb-1">Reference image</div>
                                <div className="text-xs text-zinc-300">{s.reference_instruction}</div>
                            </div>
                            <div className="border border-zinc-800 rounded-sm p-3 bg-zinc-950/40">
                                <div className="text-[10px] uppercase tracking-wider text-zinc-400 mb-1">Continuation rule</div>
                                <div className="text-xs text-zinc-300">{s.continuation_instruction}</div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <div className="mt-6 text-[10px] text-zinc-500 italic">
                {data.reminder}
            </div>
        </div>
    );
}

/* ============================ INFLUENCERS (Phase H) ============================ */
/* On-demand matcher. Never auto-runs. Never blocks the pipeline.
   Pulls from the admin-managed influencer_library and matches on brand DNA. */
function InfluencersPanel({ project, reload }) {
    const [data, setData] = useState(project.influencer_suggestions || null);
    const [busy, setBusy] = useState(false);

    // Refresh from API when tab opens (in case another device ran it)
    useEffect(() => {
        api.get(`/projects/${project.id}/influencers/suggest`)
            .then(({ data }) => setData(data || null))
            .catch(() => {});
    }, [project.id]);

    const suggest = async () => {
        if (busy) return;
        setBusy(true);
        const t = toast.loading("Matching influencers — this takes ~20s…", { duration: Infinity });
        try {
            const { data } = await api.post(`/projects/${project.id}/influencers/suggest`);
            setData(data);
            toast.dismiss(t);
            if ((data.matches || []).length === 0) {
                toast.info(data.shortlist_notes || "No relevant influencers in the library for this brand.");
            } else {
                toast.success(`${data.matches.length} influencer matches ready`);
            }
            reload?.();
        } catch (e) {
            toast.dismiss(t);
            toast.error(e?.response?.data?.detail || e?.userMessage || "Matcher failed");
        } finally { setBusy(false); }
    };

    const matches = (data?.matches) || [];
    return (
        <div className="p-6 lg:p-10" data-testid="influencers-panel">
            <PanelHeader title="Influencer / Celebrity Shortlist"
                hint="On-demand. Matched from the admin library using brand DNA, audience and geography. Never runs automatically."
                actions={[
                    <button key="run" onClick={suggest} disabled={busy}
                        data-testid="influencers-suggest-btn"
                        className="btn-primary text-sm disabled:opacity-50">
                        {busy ? "Matching…" : (matches.length ? "Re-run matcher" : "Suggest influencers")}
                    </button>,
                ]} />

            {!data && (
                <Empty hint="Click 'Suggest influencers' to generate a shortlist based on this campaign's brand DNA." />
            )}

            {data && data.source_size === 0 && (
                <div className="card-base p-5 border-amber-500/30 bg-amber-500/5 text-amber-200 text-sm">
                    The influencer library is empty. Ask your admin to upload a PDF / Excel on
                    <span className="font-mono text-xs mx-1">/admin → Influencers</span>
                    to enable matching.
                </div>
            )}

            {data && data.source_size > 0 && matches.length === 0 && (
                <div className="card-base p-5 text-sm text-zinc-300">
                    <div className="label-micro mb-2">No relevant matches</div>
                    <div>{data.shortlist_notes || "Library has entries but none fit this brand's geography or audience. Try uploading more diverse influencers."}</div>
                    <div className="text-[10px] text-zinc-500 mt-3">
                        Library size: {data.source_size} · Shortlisted: {data.shortlisted}
                    </div>
                </div>
            )}

            {matches.length > 0 && (
                <div className="space-y-4">
                    {data.shortlist_notes && (
                        <div className="card-base p-4 text-xs text-zinc-300 border-l-2 border-l-orange-500/60">
                            <span className="label-micro block mb-1">CEO note</span>
                            {data.shortlist_notes}
                        </div>
                    )}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {matches.map((m, i) => (
                            <div key={m.handle || i} className="card-base p-5"
                                data-testid={`influencer-match-${m.handle || i}`}>
                                <div className="flex items-start justify-between gap-3 flex-wrap">
                                    <div>
                                        <div className="label-micro mb-1">{m.collab_type?.replace(/_/g, " ") || "collab"}</div>
                                        <div className="font-display text-lg tracking-tight">
                                            {m.handle ? (
                                                <a target="_blank" rel="noopener noreferrer"
                                                    href={`https://instagram.com/${m.handle}`}
                                                    className="hover:text-orange-400">@{m.handle}</a>
                                            ) : (m.name || "Influencer")}
                                        </div>
                                        {m.name && m.handle && (
                                            <div className="text-xs text-zinc-500">{m.name}</div>
                                        )}
                                    </div>
                                    <span className="text-[10px] font-mono uppercase tracking-wider border border-orange-500/40 text-orange-300 px-1.5 py-0.5 rounded-sm">
                                        {Math.round((m.match_score ?? 0) * 100)}% fit
                                    </span>
                                </div>
                                {m.why && (
                                    <p className="text-xs text-zinc-400 leading-relaxed mt-3">{m.why}</p>
                                )}
                                {m.content_plan && (
                                    <div className="mt-4 space-y-2">
                                        <div className="label-micro">3-reel content plan</div>
                                        {[
                                            ["Reel 1 · Hook", m.content_plan.reel_1_hook],
                                            ["Reel 2 · Story", m.content_plan.reel_2_story],
                                            ["Reel 3 · CTA", m.content_plan.reel_3_cta],
                                        ].filter(([, v]) => v).map(([k, v]) => (
                                            <div key={k} className="text-xs text-zinc-300 border border-zinc-800 rounded-sm p-2.5 bg-zinc-950/50">
                                                <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-0.5">{k}</div>
                                                <div>{v}</div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                    <div className="text-[10px] text-zinc-500">
                        Generated {data.generated_at?.slice(0, 19).replace("T", " ")} ·
                        Library: {data.source_size} · Shortlisted: {data.shortlisted}
                    </div>
                </div>
            )}
        </div>
    );
}

/* ============================ PRESENTATION ============================ */
function PresentationPanel({ project, reload }) {
    const stage = project.stages?.presentation?.output || {};
    const out = stage.presentation;
    const isFallback = !!stage._fallback;
    const fallbackReason = stage._fallback_reason || "";
    const [basicBusy, setBasicBusy] = useState(false);

    const generateBasic = async () => {
        if (basicBusy) return;
        setBasicBusy(true);
        const t = toast.loading("Generating basic presentation (no AI)…", { duration: Infinity });
        try {
            await api.post("/agents/run", {
                project_id: project.id,
                agent: "presentation",
                extra_input: { no_ai: true },
            });
            toast.dismiss(t);
            toast.success("Basic presentation ready.");
            reload && reload();
        } catch (err) {
            toast.dismiss(t);
            toast.error(err?.userMessage || "Generate failed.");
        } finally { setBasicBusy(false); }
    };

    return (
        <div className="p-6 lg:p-10" data-testid="presentation-panel">
            <PanelHeader title="Presentation / Proposal" actions={[
                <RunAgentButton key="run" project={project} agent="presentation" label="Run Presentation Agent" onDone={reload} disabled={basicBusy} />,
                <button key="basic" type="button" disabled={basicBusy} onClick={generateBasic}
                        data-testid="presentation-no-ai"
                        className="btn-secondary text-xs px-3 py-1.5 disabled:opacity-50">
                    <FileText className="w-3.5 h-3.5" />
                    {basicBusy ? "Working…" : "Basic (No AI)"}
                </button>,
                <DownloadBtn key="pdf-deck" testid="dl-presentation-pdf" path={`/exports/${project.id}/presentation.pdf`} filename={`presentation-${project.id}.pdf`} label="Deck PDF" />,
                <DownloadBtn key="pptx-deck" testid="dl-presentation-pptx" path={`/exports/${project.id}/presentation.pptx`} filename={`presentation-${project.id}.pptx`} label="Deck PPTX" />,
                <DownloadBtn key="pdf" testid="dl-proposal" path={`/exports/${project.id}/proposal.pdf`} filename={`proposal-${project.id}.pdf`} label="Proposal PDF" />,
                <DownloadBtn key="canva" testid="dl-canva" path={`/exports/${project.id}/canva-outline.txt`} filename={`canva-${project.id}.txt`} label="Canva outline" />,
            ]} />
            <StageError project={project} stageKey="presentation" />

            {isFallback && (
                <div className="border border-amber-500/30 bg-amber-500/5 p-4 rounded-sm mb-4 flex items-start gap-3"
                     data-testid="presentation-fallback-banner">
                    <Sparkles className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
                    <div className="text-sm">
                        <div className="font-mono uppercase text-xs tracking-wider text-amber-300 mb-1">
                            Generated without AI
                        </div>
                        <div className="text-zinc-300">
                            {fallbackReason === "user_requested_no_ai"
                                ? "You asked for a basic deck — slides were compiled directly from existing stage data."
                                : fallbackReason === "ai_provider_unavailable"
                                ? "The AI provider was unavailable, so we built the deck from your existing Brand DNA, Research, Concepts, Script, Storyboard, and Assets. Click Run Presentation Agent to retry once the provider is back."
                                : "Built from your existing project data. Click Run Presentation Agent to regenerate with AI."}
                        </div>
                    </div>
                </div>
            )}

            {!out ? <Empty hint="Run the Presentation Agent to draft the pitch deck — or use 'Basic (No AI)' to compile one instantly from your existing stages." /> : (
                <div className="space-y-4">
                    <div className="card-base p-5">
                        <div className="label-micro">Title</div>
                        <div className="font-display text-2xl tracking-tight mt-1">{out.title}</div>
                        <div className="text-sm text-zinc-300 mt-3">{out.executive_summary}</div>
                    </div>
                    {(out.slides || []).map(s => (
                        <div key={s.slide_number} className="card-base p-5">
                            <div className="flex items-center justify-between">
                                <div className="label-micro">Slide {s.slide_number}</div>
                                <span className="text-xs font-mono text-zinc-500">{s.canva_layout_hint}</span>
                            </div>
                            <div className="font-display text-xl mt-2">{s.title}</div>
                            <div className="text-zinc-200 mt-2">{s.headline}</div>
                            <div className="text-sm text-zinc-400 mt-3 whitespace-pre-wrap">{s.body}</div>
                            <div className="text-xs text-zinc-500 mt-3 italic">Speaker notes: {s.speaker_notes}</div>
                        </div>
                    ))}
                    <KVList title="Next steps" items={out.next_steps} />
                </div>
            )}
            <ApprovalBar project={project} stage="presentation" onUpdated={reload} />
        </div>
    );
}

/* ============================ ASSETS UPLOAD ============================ */
function AssetsPanel({ project, reload }) {
    const [scope, setScope] = useState("project");   // "project" | "client"
    const [assets, setAssets] = useState([]);
    const [busy, setBusy] = useState(false);
    const [tags, setTags] = useState([]);
    const [notes, setNotes] = useState("");
    const [externalUrl, setExternalUrl] = useState("");
    const [urlBusy, setUrlBusy] = useState(false);
    const fileRef = useRef();
    const token = typeof window !== "undefined" ? localStorage.getItem("aivos_token") || "" : "";

    const load = async () => {
        const params = scope === "client"
            ? `?client_id=${project.client_id}&scope=client`
            : `?project_id=${project.id}`;
        const { data } = await api.get(`/assets${params}`);
        setAssets(data);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [project.id, scope]);

    const inferType = (file) =>
        (file.type || "").startsWith("video") ? "video"
        : (file.type === "application/pdf") ? "doc"
        : "image";

    const upload = async (e) => {
        const f = e.target.files?.[0];
        e.target.value = "";
        if (!f) return;
        setBusy(true);
        const fd = new FormData();
        fd.append("file", f);
        fd.append("asset_type", inferType(f));
        fd.append("usage_tags", tags.join(","));
        fd.append("notes", notes);
        if (scope === "client") fd.append("client_id", project.client_id);
        else fd.append("project_id", project.id);
        try {
            await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success(`Uploaded to ${scope === "client" ? "client brand library" : "this project"}.`);
            setNotes(""); setTags([]);
            load();
        } catch (err) {
            toast.error(err?.userMessage || err?.response?.data?.detail || "Upload failed");
        } finally { setBusy(false); }
    };

    const addUrl = async () => {
        const url = externalUrl.trim();
        if (!url) return;
        if (!/^https?:\/\//i.test(url)) {
            toast.error("URL must start with http:// or https://"); return;
        }
        setUrlBusy(true);
        const body = {
            external_url: url,
            usage_tags: tags,
            notes,
            asset_type: "reference",
            ...(scope === "client"
                ? { client_id: project.client_id }
                : { project_id: project.id }),
        };
        try {
            const { data } = await api.post("/assets/url", body);
            if (data.reachable === false) {
                toast.message(`Saved — but the URL didn't respond. Add an upload as fallback if you need it embedded.`, { duration: 6000 });
            } else {
                toast.success(`URL added to ${scope === "client" ? "brand library" : "project"}.`);
            }
            setExternalUrl(""); setNotes(""); setTags([]);
            load();
        } catch (err) {
            toast.error(err?.userMessage || err?.response?.data?.detail || "Could not add URL");
        } finally { setUrlBusy(false); }
    };

    const del = async (id) => {
        await api.delete(`/assets/${id}`);
        load();
    };

    const toggleTag = (t) =>
        setTags(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]);

    return (
        <div className="p-6 lg:p-10" data-testid="assets-panel">
            <PanelHeader title="Reference Lock" actions={[]} />

            {/* iter66 — Reference Lock explainer banner. Tells operators
                that assets uploaded here are used as generation references
                — locked to specific shots when role is set, otherwise
                project-wide. */}
            <div
                data-testid="reference-lock-explainer"
                className="rounded-md border border-zinc-800 bg-zinc-950/60 px-3 py-2.5 mb-4 text-[12px] text-zinc-300 leading-relaxed"
            >
                <div className="text-[10px] uppercase tracking-wider text-amber-300 mb-1">
                    Reference Lock
                </div>
                Uploaded assets become generation references for the
                storyboard, prompt pack, and Sora generations downstream.
                Tag each asset's role (logo · character · background ·
                storyboard frame · last frame · product) and approve it
                for generation. Continuity Bible per-shot deltas pin
                assets to specific shots.
            </div>

            {/* Scope toggle: project vs client brand library */}
            <div className="flex gap-1 border border-zinc-800 rounded-sm p-1 w-fit mb-5" data-testid="asset-scope-toggle">
                <button
                    type="button"
                    data-testid="scope-project"
                    onClick={() => setScope("project")}
                    className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider rounded-sm ${scope === "project" ? "bg-[#FF4400] text-white" : "text-zinc-500 hover:text-white"}`}
                >Project assets</button>
                <button
                    type="button"
                    data-testid="scope-client"
                    onClick={() => setScope("client")}
                    className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider rounded-sm ${scope === "client" ? "bg-[#FF4400] text-white" : "text-zinc-500 hover:text-white"}`}
                >Brand library</button>
            </div>

            <p className="text-sm text-zinc-500 mb-4">
                {scope === "client"
                    ? "Reusable brand assets — logo, intro/outro, brand guidelines, CTA refs. Available across every project of this client."
                    : "Project-specific references. Generated images/videos from external tools (Google Flow, Runway, Higgsfield) go here."}
            </p>

            {/* Composer */}
            <div className="card-base p-5 space-y-4 mb-6" data-testid="asset-composer">
                {/* Tag chips */}
                <div>
                    <div className="label-micro mb-2">Usage tags (multi-select)</div>
                    <div className="flex flex-wrap gap-1.5">
                        {ASSET_USAGE_TAGS.map(t => (
                            <button
                                type="button"
                                key={t.value}
                                data-testid={`tag-${t.value}`}
                                onClick={() => toggleTag(t.value)}
                                className={`text-[10px] uppercase tracking-wider font-mono px-2.5 py-1 rounded-sm border transition-colors ${tags.includes(t.value) ? "border-[#FF4400] bg-[#FF4400]/15 text-[#FF4400]" : "border-zinc-800 text-zinc-400 hover:border-zinc-600 hover:text-white"}`}
                            >{t.label}</button>
                        ))}
                    </div>
                </div>

                {/* Notes */}
                <div>
                    <div className="label-micro mb-2">Notes (optional)</div>
                    <textarea
                        data-testid="asset-notes"
                        rows={2}
                        value={notes}
                        onChange={e => setNotes(e.target.value)}
                        placeholder="e.g. 'use this exact lockup', 'tone reference for the intro'"
                        className="w-full bg-black border border-zinc-800 rounded-sm px-3 py-2 text-sm text-zinc-200 outline-none focus:border-zinc-500"
                    />
                </div>

                {/* URL row */}
                <div>
                    <div className="label-micro mb-2">Add by URL</div>
                    <div className="flex flex-wrap gap-2">
                        <input
                            type="url"
                            data-testid="asset-url-input"
                            value={externalUrl}
                            onChange={e => setExternalUrl(e.target.value)}
                            placeholder="https://… image / video / pdf"
                            className="flex-1 min-w-[260px] bg-black border border-zinc-800 rounded-sm px-3 py-2 text-sm text-zinc-200 font-mono outline-none focus:border-zinc-500"
                        />
                        <button
                            type="button"
                            data-testid="asset-url-add"
                            disabled={urlBusy || !externalUrl.trim()}
                            onClick={addUrl}
                            className="btn-primary text-xs px-3 py-2 disabled:opacity-50"
                        >
                            <ExternalLink className="w-3.5 h-3.5" /> {urlBusy ? "Checking…" : "Add URL"}
                        </button>
                    </div>
                </div>

                {/* Upload row */}
                <div>
                    <div className="label-micro mb-2">Or upload a file</div>
                    <input data-testid="asset-input" type="file" ref={fileRef} className="hidden" onChange={upload} accept="image/*,video/*,.pdf" />
                    <button
                        type="button"
                        data-testid="asset-upload-btn"
                        disabled={busy}
                        onClick={() => fileRef.current?.click()}
                        className="btn-secondary text-xs px-3 py-2 disabled:opacity-50"
                    >
                        <Upload className="w-3.5 h-3.5" /> {busy ? "Uploading…" : "Upload file"}
                    </button>
                </div>
            </div>

            {/* Asset grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {assets.map(a => (
                    <AssetCard key={a.id} asset={a} token={token} onDelete={del} reload={reload} project={project} />
                ))}
                {assets.length === 0 && (
                    <div className="md:col-span-2 lg:col-span-3">
                        <Empty hint={scope === "client" ? "No brand assets yet. Add a logo, brand intro/outro, or guidelines." : "No project assets yet. Add references via URL or upload."} />
                    </div>
                )}
            </div>
            {/* Workflow Hardening 2 / FIX 8 — Asset Upload must be clearly
                actionable: upload assets OR skip with reason (canonical
                transition via POST /api/approvals action=skip). */}
            {!(["approved", "manually_completed"].includes(((project.stages || {}).asset_upload || {}).status) ||
               ((((project.stages || {}).asset_upload || {}).status === "skipped") &&
                (((project.stages || {}).asset_upload || {}).skip_reason || "").trim())) && (
                <div
                    data-testid="asset-upload-skip-hint"
                    className="mt-4 border border-amber-500/30 bg-amber-500/5 rounded-sm px-4 py-3 flex items-center justify-between gap-3 flex-wrap"
                >
                    <div className="text-sm text-amber-200">
                        Upload assets or skip with reason.
                    </div>
                    <button
                        type="button"
                        data-testid="asset-upload-skip-btn"
                        onClick={async () => {
                            const reason = (window.prompt(
                                "Skip Asset Upload with reason (required):", "",
                            ) || "").trim();
                            if (!reason) { toast.error("Skip requires a non-empty reason."); return; }
                            try {
                                await api.post("/approvals", {
                                    project_id: project.id,
                                    stage: "asset_upload",
                                    action: "skip",
                                    skip_reason: reason,
                                });
                                toast.success(`Asset Upload skipped: "${reason}"`);
                                reload && reload();
                            } catch (e) {
                                const d = e?.response?.data?.detail;
                                toast.error((d && d.message) || e?.userMessage || "Skip failed");
                            }
                        }}
                        className="px-3 py-1.5 text-xs font-mono uppercase rounded-sm border border-amber-600 text-amber-300 hover:bg-amber-500/10"
                    >
                        Skip with reason…
                    </button>
                </div>
            )}
            {(((project.stages || {}).asset_upload || {}).status === "skipped") && (
                <div
                    data-testid="asset-upload-skipped-banner"
                    className="mt-4 border border-zinc-800 bg-zinc-900/40 rounded-sm px-4 py-2 text-xs font-mono text-zinc-400"
                >
                    ASSET UPLOAD SKIPPED — {((project.stages || {}).asset_upload || {}).skip_reason || "no reason recorded"}
                </div>
            )}
            <ApprovalBar project={project} stage="asset_upload" onUpdated={reload} />
        </div>
    );
}

function AssetCard({ asset, token, onDelete, reload, project }) {
    const isUrl = asset.source === "url" || (!asset.storage_path && asset.external_url);
    const previewSrc = isUrl
        ? asset.external_url
        : `${API}/files/${asset.storage_path}?auth=${encodeURIComponent(token)}`;
    const isImage = (asset.asset_type === "image") || (asset.content_type || "").startsWith("image/");
    const isVideo = (asset.asset_type === "video") || (asset.content_type || "").startsWith("video/");
    const showQc = !isUrl && !!asset.storage_path;   // QC needs a stored asset

    return (
        <div className="card-base p-4" data-testid={`asset-${asset.id}`}>
            <div className="aspect-video bg-black border border-zinc-900 rounded-sm overflow-hidden mb-3 relative">
                {isImage && (
                    <img src={previewSrc} alt={asset.original_filename}
                         className="w-full h-full object-cover"
                         loading="lazy"
                         onError={(e) => { e.currentTarget.style.display = "none"; }} />
                )}
                {isVideo && (
                    <video src={previewSrc} controls
                           className="w-full h-full object-cover bg-black" />
                )}
                {!isImage && !isVideo && (
                    <a href={previewSrc} target="_blank" rel="noopener noreferrer"
                       className="w-full h-full grid place-items-center text-zinc-500 hover:text-[#FF4400]">
                        <FileText className="w-8 h-8" />
                    </a>
                )}
                {/* Source badge */}
                <span className={`absolute top-2 left-2 text-[9px] uppercase tracking-wider font-mono px-2 py-0.5 rounded-sm ${isUrl ? "bg-blue-500/20 text-blue-300 border border-blue-500/30" : "bg-emerald-500/20 text-emerald-300 border border-emerald-500/30"}`}>
                    {isUrl ? "URL" : "Upload"}
                </span>
                {asset.reachable === false && (
                    <span className="absolute top-2 right-2 text-[9px] uppercase tracking-wider font-mono px-2 py-0.5 rounded-sm bg-amber-500/20 text-amber-300 border border-amber-500/30">
                        unreachable
                    </span>
                )}
            </div>
            <div className="text-sm truncate text-zinc-100">{asset.original_filename}</div>
            <div className="text-[10px] uppercase tracking-wider font-mono text-zinc-500">
                {asset.asset_type}
                {asset.size ? ` · ${Math.round((asset.size||0)/1024)}KB` : ""}
                {asset.client_id && !asset.project_id ? " · brand" : " · project"}
            </div>
            {(asset.usage_tags || []).length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                    {asset.usage_tags.map(t => (
                        <span key={t} className="text-[9px] uppercase tracking-wider font-mono px-1.5 py-0.5 rounded-sm bg-zinc-900 text-zinc-400 border border-zinc-800">{t.replaceAll("_", " ")}</span>
                    ))}
                </div>
            )}
            {asset.notes && (
                <div className="text-xs text-zinc-500 mt-2 italic line-clamp-2">"{asset.notes}"</div>
            )}
            <div className="flex justify-between items-center mt-3">
                <button onClick={() => onDelete(asset.id)} data-testid={`asset-del-${asset.id}`}
                        className="text-xs text-zinc-500 hover:text-red-400">
                    <Trash2 className="w-3 h-3 inline-block mr-1" />Remove
                </button>
                {showQc && <RunQcButton project={project} asset={asset} onDone={reload} />}
                {isUrl && (
                    <a href={asset.external_url} target="_blank" rel="noopener noreferrer"
                       className="text-xs text-zinc-500 hover:text-[#FF4400] flex items-center gap-1">
                        <ExternalLink className="w-3 h-3" /> Open
                    </a>
                )}
            </div>
        </div>
    );
}

function RunQcButton({ project, asset, onDone }) {
    const [busy, setBusy] = useState(false);
    return (
        <button data-testid={`qc-${asset.id}`} onClick={async () => {
            setBusy(true);
            try {
                await api.post("/qc/run", { project_id: project.id, asset_id: asset.id });
                toast.success("QC review created.");
                onDone && onDone();
            } catch (e) { toast.error(e?.userMessage || e?.response?.data?.detail || "QC failed"); }
            finally { setBusy(false); }
        }} disabled={busy} className="btn-accent text-xs px-3 py-1.5">
            <Sparkles className="w-3.5 h-3.5" /> {busy ? "Reviewing…" : "Run QC"}
        </button>
    );
}

/* ============================ QC ============================ */
function QcPanel({ project, reload }) {
    const reviews = project.stages?.qc_review?.reviews || [];
    const [studioReviews, setStudioReviews] = useState([]);
    const [studioLoading, setStudioLoading] = useState(true);
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get(`/projects/${project.id}/qc-reviews`);
                if (!cancelled) setStudioReviews(data?.items || []);
            } catch (e) {
                // Tolerate — Video Studio QC list is supplemental, not the
                // canonical source for legacy stage-driven QC.
                if (!cancelled) setStudioReviews([]);
            } finally {
                if (!cancelled) setStudioLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [project.id]);
    return (
        <div className="p-6 lg:p-10" data-testid="qc-panel">
            <PanelHeader title="QC Review" actions={[]} />

            {/* V1-A — Video Studio approve-to-QC inbox. Visible only when
                approvals have been filed from the Video Studio tab. */}
            {!studioLoading && studioReviews.length > 0 && (
                <div className="mb-6" data-testid="qc-studio-inbox">
                    <div className="flex items-center justify-between mb-2">
                        <div className="label-micro">Approved from Video Studio · {studioReviews.length}</div>
                        <div className="text-[10px] text-zinc-600 uppercase tracking-wider">
                            Pending QC sign-off
                        </div>
                    </div>
                    <div className="space-y-3">
                        {studioReviews.map((r) => (
                            <div
                                key={r.id}
                                data-testid={`qc-studio-row-${r.id}`}
                                className="card-base p-4 flex flex-wrap items-center justify-between gap-3"
                            >
                                <div className="text-sm">
                                    <div className="flex items-center gap-2">
                                        <span className="text-[10px] uppercase tracking-wider text-zinc-500">QC</span>
                                        <span className="font-mono text-zinc-100">{r.id}</span>
                                        <span className={`text-[10px] px-2 py-0.5 rounded-sm border uppercase tracking-wider ${
                                            r.status === "pending"
                                                ? "border-amber-500/40 text-amber-300"
                                                : r.status === "approved"
                                                    ? "border-emerald-500/40 text-emerald-300"
                                                    : "border-zinc-700 text-zinc-400"
                                        }`}>{r.status || "pending"}</span>
                                    </div>
                                    <div className="text-xs text-zinc-500 mt-1">
                                        Shot {r.shot_id || "—"} · from video job{" "}
                                        <span className="font-mono text-zinc-400">{(r.source_video_job_id || "").slice(0, 18)}</span>
                                    </div>
                                </div>
                                {r.asset_id && (
                                    <video
                                        controls
                                        preload="metadata"
                                        className="rounded-sm border border-zinc-800 bg-black w-[260px] max-w-full"
                                        src={`${process.env.REACT_APP_BACKEND_URL}/api/assets/${r.asset_id}/stream`}
                                    />
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {reviews.length === 0 && studioReviews.length === 0
                ? <Empty hint="Upload assets and run QC from the Asset Upload tab, or approve a clip from the Video Studio tab." />
                : (reviews.length > 0 && (
                <div className="space-y-4">
                    {reviews.map((r, i) => {
                        const v = r.review.qc_review;
                        if (!v) return null;
                        return (
                            <div key={i} className="card-base p-5">
                                <div className="flex items-center justify-between">
                                    <div className="label-micro">Review · {r.asset_id.slice(0, 8)}</div>
                                    <div className="flex items-center gap-2">
                                        <span className={`text-xs px-2 py-1 rounded-sm border ${v.pass ? "border-emerald-500/30 text-emerald-400 bg-emerald-500/10" : "border-red-500/30 text-red-400 bg-red-500/10"}`}>
                                            {v.pass ? "PASS" : "FAIL"}
                                        </span>
                                        <span className="text-xs font-mono text-zinc-400">SCORE {v.score}/100</span>
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
                                    {Object.entries(v.checks || {}).map(([k, val]) => (
                                        <div key={k} className="text-xs">
                                            <span className="text-zinc-500 font-mono uppercase tracking-wider">{k.replaceAll("_"," ")}: </span>
                                            <span className="text-zinc-200">{val}</span>
                                        </div>
                                    ))}
                                </div>
                                {v.correction_notes?.length > 0 && (
                                    <div className="mt-4">
                                        <div className="label-micro mb-1">Correction notes</div>
                                        <ul className="text-sm text-zinc-300 list-disc pl-5">
                                            {v.correction_notes.map((c, j) => <li key={j}>{c}</li>)}
                                        </ul>
                                    </div>
                                )}
                                {v.regenerate_prompt && (
                                    <div className="mt-3">
                                        <div className="label-micro mb-1">Regenerate prompt</div>
                                        <div className="text-xs font-mono text-zinc-300 bg-black border border-zinc-900 p-3 rounded-sm whitespace-pre-wrap">{v.regenerate_prompt}</div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            ))}
            <ApprovalBar project={project} stage="qc_review" onUpdated={reload} />
        </div>
    );
}

/* ============================ POST-PRODUCTION ============================ */
function PostPanel({ project, reload }) {
    const out = project.stages?.post_production?.output?.post_production;
    return (
        <div className="p-6 lg:p-10" data-testid="post-panel">
            <PanelHeader title="Post-Production Plan" actions={[
                <RunAgentButton key="run" project={project} agent="post_production" label="Run Post-Production" onDone={reload} />,
            ]} />
            <StageError project={project} stageKey="post_production" />
            {!out ? <Empty hint="Run Post-Production Agent after QC review is complete." /> : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <KV title="Edit plan" value={out.edit_plan} />
                    <KVList title="Shot order" items={out.shot_order} mono />
                    <KV title="Music direction" value={out.music_direction} />
                    <KV title="Sound design notes" value={out.sound_design_notes} />
                    <KV title="Subtitles plan" value={out.subtitles_plan} />
                    <KV title="Grading notes" value={out.grading_notes} />
                    <KVList title="VFX cleanup" items={out.vfx_cleanup_notes} />
                    <div className="card-base p-5">
                        <div className="label-micro mb-3">Transitions</div>
                        {(out.transitions || []).map((t, i) => (
                            <div key={i} className="text-xs border-b border-zinc-900 py-1.5 last:border-b-0">
                                <span className="font-mono">{t.from_shot} → {t.to_shot}</span> · <span className="text-zinc-400">{t.transition}</span>
                            </div>
                        ))}
                    </div>
                    <div className="card-base p-5 lg:col-span-2">
                        <div className="label-micro mb-3">Output formats</div>
                        <table className="w-full text-sm">
                            <thead className="text-xs text-zinc-500 font-mono uppercase">
                                <tr><th className="text-left py-2">Platform</th><th className="text-left py-2">Ratio</th><th className="text-left py-2">Duration</th><th className="text-left py-2">Codec</th></tr>
                            </thead>
                            <tbody>
                                {(out.output_formats || []).map((f, i) => (
                                    <tr key={i} className="border-t border-zinc-900">
                                        <td className="py-2">{f.platform}</td>
                                        <td className="py-2 font-mono">{f.ratio}</td>
                                        <td className="py-2 font-mono">{f.duration}</td>
                                        <td className="py-2 font-mono">{f.codec}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
            <ApprovalBar project={project} stage="post_production" onUpdated={reload} />
        </div>
    );
}

/* ============================ DELIVERY ============================ */
function DeliveryPanel({ project, reload }) {
    const completed = project.current_stage === "final_delivery";
    return (
        <div className="p-6 lg:p-10" data-testid="delivery-panel">
            <PanelHeader title="Final Delivery" actions={[
                <DownloadBtn key="rep" testid="dl-final-report" path={`/exports/${project.id}/report.pdf`} filename={`report-${project.id}.pdf`} label="Full report PDF" />,
            ]} />
            <div className="card-base p-8 text-center">
                <div className={`inline-flex w-16 h-16 rounded-full items-center justify-center mb-4 ${completed ? "bg-emerald-500/15 text-emerald-400" : "bg-zinc-900 text-zinc-500"}`}>
                    <Sparkles className="w-7 h-7" />
                </div>
                <div className="font-display text-2xl">{completed ? "Project complete" : "Awaiting final approval"}</div>
                <p className="text-zinc-500 text-sm mt-2">All stage outputs are exportable via the Project Report PDF.</p>
                {!completed && (
                    <button data-testid="mark-delivered" className="btn-primary mt-6" onClick={async () => {
                        await api.post("/approvals", { project_id: project.id, stage: "post_production", action: "approve" });
                        toast.success("Marked delivered.");
                        reload();
                    }}>Mark as delivered</button>
                )}
            </div>
        </div>
    );
}

/* ============================ SHARE ============================ */
function SharePanel({ project, reload }) {
    const [enabled, setEnabled] = useState(project.public_share_enabled || false);
    const [stages, setStages] = useState(project.public_share_stages || []);
    const [token, setToken] = useState(project.public_token);
    const link = `${window.location.origin}/share/${project.id}/${token}`;
    const toggle = (k) => setStages(s => s.includes(k) ? s.filter(x => x !== k) : [...s, k]);

    const save = async () => {
        await api.post(`/projects/${project.id}/share`, { enabled, stages });
        toast.success(enabled ? "Share link is live." : "Share link disabled.");
        reload();
    };

    const rotate = async () => {
        if (!window.confirm("Rotate the link? Any link already sent to the client will stop working.")) return;
        const { data } = await api.post(`/projects/${project.id}/share/rotate`);
        setToken(data.public_token);
        toast.success("New link generated — the old link has been revoked.");
        reload();
    };

    return (
        <div className="p-6 lg:p-10 max-w-3xl" data-testid="share-panel">
            <PanelHeader title="Client Share Link" actions={[]} />
            <div className="card-base p-5 space-y-4">
                <label className="flex items-center gap-3 cursor-pointer">
                    <input type="checkbox" data-testid="share-enable" checked={enabled} onChange={e => setEnabled(e.target.checked)} className="w-4 h-4 accent-[#FF4400]" />
                    <span className="text-sm">Enable public share link</span>
                </label>
                <div>
                    <div className="label-micro mb-2">Sections visible to the client</div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {STAGES.filter(s => !["ceo_briefing","asset_upload","final_delivery"].includes(s.key)).map(s => (
                            <label key={s.key} className="flex items-center gap-2 text-sm">
                                <input type="checkbox" data-testid={`share-stage-${s.key}`} checked={stages.includes(s.key)} onChange={() => toggle(s.key)} className="accent-[#FF4400]" />
                                {s.label}
                            </label>
                        ))}
                    </div>
                    <div className="text-[11px] text-zinc-500 mt-2">
                        Tip: include <span className="text-zinc-300">Presentation</span> for an embedded pitch-deck viewer and downloadable PPTX.
                    </div>
                </div>
                <div>
                    <div className="label-micro mb-2">Shareable link</div>
                    <div className="flex gap-2">
                        <input data-testid="share-link" readOnly value={link} className="flex-1 bg-black border border-zinc-800 rounded-sm px-3 py-2 font-mono text-xs" />
                        <button onClick={() => { navigator.clipboard.writeText(link); toast.success("Copied"); }} className="btn-secondary text-xs"><Copy className="w-3.5 h-3.5" /> Copy</button>
                    </div>
                </div>
                <div className="flex gap-2 flex-wrap">
                    <button data-testid="share-save" onClick={save} className="btn-primary"><Share2 className="w-4 h-4" /> Save share settings</button>
                    <button data-testid="share-rotate" onClick={rotate} className="btn-secondary text-xs">
                        Rotate link
                    </button>
                </div>
            </div>
        </div>
    );
}

/* ============================ small atoms ============================ */
const PanelHeader = ({ title, actions }) => (
    <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <h2 className="font-display text-2xl tracking-tight">{title}</h2>
        <div className="flex flex-wrap gap-2">{actions}</div>
    </div>
);

/* Phase N.5 — Pipeline State Consistency Banner.
 * Detects contradictory state client-side and offers Repair.
 * Triggers when:
 *   - More than one stage shows status in {running, processing, in_progress}
 *   - A stage has output but status is 'running'/'processing'/'failed'
 *   - auto_pipeline.status is 'running' but no stage is actually running
 *   - Downstream stage running while an upstream stage failed
 * The Repair button POSTs /api/projects/{id}/repair which never touches
 * outputs/versions/approvals, only stale flags.
 */
const _RUNNING_STATES_FE = new Set(["running", "processing", "in_progress", "leased"]);
const _PIPELINE_ORDER_FE = [
    "ceo_briefing", "competitor_analysis", "brand_dna",
    "research", "business_plan", "concepts", "script",
    "storyboard", "presentation", "prompts", "qc_review",
    "post_production", "final_delivery",
];

/* ─── Patch F (Phase N.8) — isActionAllowed strict guard ───
 * Single source of truth used by buttons across the board (AutoPipelineBar,
 * CeoActionButtons, RunAgentButton wrappers) so users can NEVER click an
 * action whose preconditions don't hold. Mirrors the backend prereq + state
 * machine exactly so we never round-trip a guaranteed 409.
 *
 * Returns { ok: bool, reason: string }. UIs can show `reason` in a tooltip
 * and disable the button when ok=false.
 */
const _AGENT_TO_STAGE_FE = {
    competitor_analyst: "competitor_analysis",
    brand_analyst: "brand_dna",
    research: "research",
    business_plan: "business_plan",
    creative_director: "concepts",
    script: "script",
    storyboard: "storyboard",
    presentation: "presentation",
    prompt_engineer: "prompts",
    qc: "qc_review",
    post_production: "post_production",
};

/* Workflow Hardening 2 / FIX 4+7 — the frontend reads prereq + skippable
 * rules from GET /api/workflow/canonical (single backend authority).
 * The fallback below mirrors backend/workflow.py exactly and is only used
 * until the manifest loads. The OLD local table had drifted (concepts →
 * brand_dna instead of business_plan; research had no prereq at all). */
const _WF_FALLBACK = {
    prereq: {
        competitor_analysis: "ceo_briefing",
        brand_dna: "competitor_analysis",
        research: "brand_dna",
        business_plan: "research",
        concepts: "business_plan",
        script: "concepts",
        storyboard: "script",
        presentation: "storyboard",
        prompts: "storyboard",
        qc_review: "prompts",
        post_production: "storyboard",
        final_delivery: "post_production",
    },
    labels: {},
    skippable_stages: [
        "asset_upload", "competitor_analysis", "research",
        "prompts", "qc_review", "post_production",
    ],
};
let _WF_CANON = _WF_FALLBACK;
api.get("/workflow/canonical")
    .then((r) => { if (r?.data?.prereq) _WF_CANON = r.data; })
    .catch(() => {});
const _wfPrereqFor = (k) => (_WF_CANON.prereq || {})[k];
const _wfLabel = (k) => (_WF_CANON.labels || {})[k] || k;
const _wfSkippable = () => new Set(_WF_CANON.skippable_stages || _WF_FALLBACK.skippable_stages);
const _stageSatisfiedFE = (row) => {
    const st = ((row || {}).status || "").toLowerCase();
    if (st === "approved" || st === "manually_completed") return true;
    return st === "skipped" && !!(((row || {}).skip_reason) || "").trim();
};

const _APPROVED_STATES_FE = new Set(["approved", "manually_completed"]);

/** isActionAllowed(project, action, opts?) -> {ok, reason}
 *  action ∈ "approve" | "regenerate" | "resume" | "skip" | "run" | "restart"
 *  opts.stage  — stage_key the action targets (required for run/approve/skip/regenerate)
 *  opts.agent  — agent name (used for run; resolved to stage if stage absent)
 */
const isActionAllowed = (project, action, opts = {}) => {
    if (!project) return { ok: false, reason: "Project not loaded" };
    const stages = project.stages || {};
    const ap = project.auto_pipeline || {};
    let stageKey = opts.stage;
    if (!stageKey && opts.agent) stageKey = _AGENT_TO_STAGE_FE[opts.agent] || opts.agent;
    const sv = (stageKey && stages[stageKey]) || {};
    const st = (sv.status || "").toLowerCase();
    const lease = sv.lease || null;
    const maxAttempts = (project.pipeline_constants || {}).lease_max_attempts || 3;

    // Phase O.3 — lease-aware gate: a stage with an active lease blocks
    // every action except cancel. Cancel always works.
    if (st === "leased" && action !== "cancel") {
        const reasonExtra = lease && lease.attempt > 1
            ? ` (attempt ${lease.attempt}/${maxAttempts})`
            : "";
        return {
            ok: false,
            reason: `Stage '${stageKey}' is leased — wait for completion or Cancel${reasonExtra}`,
        };
    }

    if (action === "run" || action === "regenerate") {
        if (_RUNNING_STATES_FE.has(st)) {
            return { ok: false, reason: `${stageKey} is already running` };
        }
        // Phase O.3 — retry attempt cap.
        const historyAttempts = ((sv.lease_history || []).length) || 0;
        if (historyAttempts >= maxAttempts) {
            return {
                ok: false,
                reason: `${stageKey} reached max ${maxAttempts} attempts. Use Manual Override or Skip.`,
            };
        }
        const prereqKey = _wfPrereqFor(stageKey);
        if (prereqKey) {
            const prevRow = stages[prereqKey] || {};
            if (!_stageSatisfiedFE(prevRow)) {
                return {
                    ok: false,
                    reason: `${_wfLabel(prereqKey)} must be approved first (currently: ${(prevRow.status || "idle").toLowerCase()})`,
                };
            }
        }
        // Workflow Hardening 2 — Campaign Strategy gate mirror (backend is
        // authoritative; this only prevents a guaranteed 409 round-trip).
        if (stageKey === "business_plan" && project.campaign_inputs_ready === false) {
            const miss = (project.missing_campaign_inputs || []).join(", ");
            return {
                ok: false,
                reason: `Campaign Strategy inputs pending review${miss ? ` — ${miss}` : ""}. Review or skip each input first.`,
            };
        }
        return { ok: true, reason: "" };
    }

    if (action === "approve") {
        // Stage-level approve: must have output, must not already be approved.
        if (_APPROVED_STATES_FE.has(st)) {
            return { ok: false, reason: "Stage is already approved" };
        }
        if (!sv.output && st !== "output_pending" && st !== "pending") {
            return { ok: false, reason: "No output to approve yet" };
        }
        // Auto-pipeline gate: when awaiting_stage is set on a different stage,
        // block this approve action so users never clobber the orchestrator.
        if (ap.awaiting_stage && stageKey && ap.awaiting_stage !== stageKey) {
            return { ok: false, reason: `Pipeline is awaiting approval for '${ap.awaiting_stage}'` };
        }
        return { ok: true, reason: "" };
    }

    if (action === "resume") {
        if (ap.status === "running") {
            return { ok: false, reason: "Pipeline is currently running" };
        }
        if (ap.status !== "failed") {
            return { ok: false, reason: `Resume only works after a failure (status: ${ap.status || "idle"})` };
        }
        return { ok: true, reason: "" };
    }

    if (action === "skip") {
        // Workflow Hardening 2 / FIX 4 — skippable stages come from the
        // canonical manifest, not a local allow-list.
        if (!_wfSkippable().has(stageKey)) {
            return { ok: false, reason: "This stage is required and cannot be skipped" };
        }
        if (_APPROVED_STATES_FE.has(st)) {
            return { ok: false, reason: "Stage is already approved" };
        }
        return { ok: true, reason: "" };
    }

    if (action === "restart") {
        if (ap.status === "running") {
            return { ok: false, reason: "Cancel the running pipeline first" };
        }
        return { ok: true, reason: "" };
    }

    return { ok: true, reason: "" };
};

/* ─── Patch E (Phase N.8) — CEO action buttons ───
 * Maps the CEO Agent's chat replies to inline action chips so the user
 * never has to copy-paste or hunt for the matching button. We scan the
 * message text for action cues (Approve / Regenerate / Resume / Skip /
 * Repair) and render data-testid'd buttons gated by `isActionAllowed`.
 *
 * The current/awaiting stage is resolved from `auto_pipeline.awaiting_stage`
 * (preferred) → `auto_pipeline.agent_running` → `current_stage`.
 */
const _CEO_ACTION_TRIGGERS = [
    { action: "approve",    re: /\bapprove\b/i,   label: "Approve",      cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40 hover:bg-emerald-500/25" },
    { action: "regenerate", re: /\bregenerate\b/i, label: "Regenerate",  cls: "bg-amber-500/15 text-amber-200 border-amber-500/40 hover:bg-amber-500/25" },
    { action: "resume",     re: /\bresume\b/i,    label: "Resume",       cls: "bg-orange-500/15 text-orange-200 border-orange-500/40 hover:bg-orange-500/25" },
    { action: "skip",       re: /\bskip\b/i,      label: "Skip stage",   cls: "bg-zinc-700/30 text-zinc-200 border-zinc-700 hover:bg-zinc-700/50" },
    { action: "repair",     re: /\brepair\b/i,    label: "Repair state", cls: "bg-rose-500/15 text-rose-200 border-rose-500/40 hover:bg-rose-500/25" },
];

const _resolveActiveStage = (project) => {
    const ap = project?.auto_pipeline || {};
    if (ap.awaiting_stage) return ap.awaiting_stage;
    if (ap.agent_running) return _AGENT_TO_STAGE_FE[ap.agent_running] || ap.agent_running;
    return project?.current_stage || null;
};

const CeoActionButtons = ({ message, project, reload }) => {
    const [busy, setBusy] = useState(false);
    if (!message || message.role === "user") return null;
    const text = String(message.text || "");
    const triggers = _CEO_ACTION_TRIGGERS.filter(t => t.re.test(text));
    if (triggers.length === 0) return null;

    const stageKey = _resolveActiveStage(project);

    const fire = async (action) => {
        setBusy(true);
        try {
            if (action === "approve") {
                await api.post(`/projects/${project.id}/auto/approve`);
                toast.success("Approved");
            } else if (action === "regenerate") {
                if (!stageKey) throw new Error("No active stage to regenerate");
                const agent = Object.entries(_AGENT_TO_STAGE_FE)
                    .find(([, s]) => s === stageKey)?.[0] || stageKey;
                await api.post(`/agents/run`, { project_id: project.id, agent });
                toast.success(`${agent} re-running…`);
            } else if (action === "resume") {
                await api.post(`/projects/${project.id}/auto/resume`);
                toast.success("Resuming…");
            } else if (action === "skip") {
                if (!stageKey) throw new Error("No active stage to skip");
                await api.post(`/projects/${project.id}/stages/${stageKey}/skip`,
                    { reason: "Skipped via CEO chat" });
                toast.success(`${stageKey} skipped`);
            } else if (action === "repair") {
                const { data } = await api.post(`/projects/${project.id}/repair`);
                toast.success(`Repair done — ${(data?.fixes || []).length} fix(es)`);
            }
            reload && (await reload());
        } catch (e) {
            toast.error(e.userMessage || e.message || "Action failed");
        } finally { setBusy(false); }
    };

    return (
        <div className="mt-3 flex flex-wrap gap-2" data-testid={`ceo-actions-${message.id}`}>
            {triggers.map(t => {
                const guard = isActionAllowed(project, t.action, { stage: stageKey });
                const enabled = guard.ok && !busy;
                return (
                    <button
                        key={t.action}
                        type="button"
                        disabled={!enabled}
                        title={enabled ? `${t.label} — ${stageKey || "current"}` : guard.reason}
                        onClick={() => fire(t.action)}
                        data-testid={`ceo-action-${t.action}`}
                        className={`text-[11px] font-mono uppercase tracking-wider px-2.5 py-1 rounded-sm border ${t.cls} disabled:opacity-40 disabled:cursor-not-allowed`}
                    >
                        {t.label}
                    </button>
                );
            })}
        </div>
    );
};

const detectInconsistency = (project) => {
    const issues = [];
    const stages = project?.stages || {};
    const ap = project?.auto_pipeline || {};
    let runningCount = 0;
    let runningKeys = [];
    let failedUpstream = false;
    for (const sk of _PIPELINE_ORDER_FE) {
        const sv = stages[sk] || {};
        const st = (sv.status || "").toLowerCase();
        if (_RUNNING_STATES_FE.has(st)) {
            runningCount += 1;
            runningKeys.push(sk);
            if (failedUpstream) {
                issues.push(`'${sk}' is running while an upstream stage has failed`);
            }
        }
        if (st === "failed") failedUpstream = true;
        if (sv.output && (st === "running" || st === "processing" || st === "failed")) {
            issues.push(`'${sk}' has output but status is '${st}'`);
        }
    }
    // Also scan stages outside the canonical order
    for (const [sk, sv] of Object.entries(stages)) {
        if (_PIPELINE_ORDER_FE.includes(sk) || !sv || typeof sv !== "object") continue;
        const st = (sv.status || "").toLowerCase();
        if (_RUNNING_STATES_FE.has(st)) {
            runningCount += 1;
            runningKeys.push(sk);
        }
    }
    if (runningCount > 1) {
        issues.unshift(`${runningCount} stages marked running simultaneously: ${runningKeys.join(", ")}`);
    }
    return issues;
};

const PipelineStateConsistencyBanner = ({ project, reload }) => {
    const [busy, setBusy] = useState(false);
    // Phase O.3 — lease-pipeline projects cannot be inconsistent by
    // construction. The heuristic banner would only produce false
    // positives during brief state transitions, so skip it entirely.
    if (project?.lease_pipeline === true) return null;
    const issues = detectInconsistency(project);
    if (issues.length === 0) return null;
    const repair = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`/projects/${project.id}/repair`);
            const fixed = (data?.fixes || []).length;
            toast.success(
                fixed > 0
                    ? `Repaired pipeline state — ${fixed} fix(es) applied. Outputs preserved.`
                    : "Pipeline state already clean."
            );
            await reload();
        } catch (e) {
            toast.error(`Repair failed: ${e.userMessage || e.message}`);
        } finally {
            setBusy(false);
        }
    };
    return (
        <div
            className="mt-4 p-4 rounded border border-rose-500/40 bg-rose-500/5"
            data-testid="state-corruption-banner"
        >
            <div className="flex items-start gap-3">
                <span className="w-2 h-2 mt-1.5 rounded-full bg-rose-400 pulse-dot" />
                <div className="flex-1">
                    <div className="label-micro text-rose-300 mb-1">
                        Project state corrupted
                    </div>
                    <div className="text-sm text-zinc-200">
                        Click Repair to clean it. Outputs, versions and approvals are preserved.
                    </div>
                    <ul className="text-xs text-zinc-400 mt-2 list-disc pl-5 space-y-0.5">
                        {issues.slice(0, 4).map((it, i) => (
                            <li key={i}>{it}</li>
                        ))}
                        {issues.length > 4 && <li>…and {issues.length - 4} more</li>}
                    </ul>
                    <button
                        type="button"
                        onClick={repair}
                        disabled={busy}
                        data-testid="state-repair-btn"
                        className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 text-xs font-mono uppercase tracking-wider rounded-sm bg-rose-500/20 text-rose-200 border border-rose-500/40 hover:bg-rose-500/30 disabled:opacity-50 transition-colors"
                    >
                        {busy ? "Repairing…" : "Repair project state"}
                    </button>
                </div>
            </div>
        </div>
    );
};


/* Phase N — Genie Progress Banner.
 * Non-blocking inline banner shown above the project board content when
 * the auto-pipeline is actively running a stage. Surfaces:
 *   – the Genie mascot + rotating magical messages
 *   – the stage currently executing (stage label + agent id)
 *   – elapsed time (mm:ss)
 *   – a soft hint that work is safe and recovery is one click away if it stalls
 * Hides automatically once status leaves 'running'. The Recovery Panel
 * (StageError) takes over on failure / heartbeat staleness.
 */
const _GENIE_MESSAGES = [
    "Preparing your Furmais…",
    "Understanding your brief…",
    "Aligning brand strategy…",
    "Channeling Genie magic…",
    "Crafting story arcs…",
    "Building your output…",
    "Kuch jadu ho raha hai…",
    "Polishing the details…",
    "Almost ready…",
];

const _STAGE_LABELS = {
    competitor_analysis: "Competitor Analyst",
    brand_dna: "Brand Analyst",
    research: "Research Agent",
    business_plan: "Brand Strategist",
    concepts: "Creative Director",
    script: "Script Agent",
    storyboard: "Storyboard Agent",
    presentation: "Presentation Agent",
    prompts: "Prompt Engineer",
    qc_review: "QC Agent",
    post_production: "Post-Production Agent",
};

const _AGENT_TO_STAGE = {
    brand_analyst: "brand_dna",
    competitor_analyst: "competitor_analysis",
    creative_director: "concepts",
    prompt_engineer: "prompts",
    research: "research",
    business_plan: "business_plan",
    script: "script",
    storyboard: "storyboard",
    presentation: "presentation",
};

const GenieProgressBanner = ({ project }) => {
    const ap = project?.auto_pipeline || {};
    const stages = project?.stages || {};
    const isRunning = ap.status === "running";
    // Find the stage currently running (by ap.agent_running OR stage status)
    const apStageKey = _AGENT_TO_STAGE[ap.agent_running] || ap.agent_running || "";
    let activeStageKey = apStageKey;
    let activeStage = stages[apStageKey] || null;
    if (!activeStage) {
        // fall back: scan stages for the first running/processing one
        for (const [k, v] of Object.entries(stages)) {
            const st = (v?.status || "").toLowerCase();
            if (st === "running" || st === "processing" || st === "in_progress") {
                activeStageKey = k;
                activeStage = v;
                break;
            }
        }
    }
    const startedAt = activeStage?.started_at || ap.started_at || null;
    const [tick, setTick] = useState(0);
    const [msgIdx, setMsgIdx] = useState(0);
    useEffect(() => {
        if (!isRunning) return;
        const t = setInterval(() => setTick(x => x + 1), 1000);
        const m = setInterval(() => setMsgIdx(i => (i + 1) % _GENIE_MESSAGES.length), 3500);
        return () => { clearInterval(t); clearInterval(m); };
    }, [isRunning]);

    if (!isRunning) return null;

    let elapsed = "—";
    if (startedAt) {
        try {
            const sec = Math.max(0, Math.floor((Date.now() - new Date(startedAt).getTime()) / 1000));
            const mm = String(Math.floor(sec / 60)).padStart(2, "0");
            const ss = String(sec % 60).padStart(2, "0");
            elapsed = `${mm}:${ss}`;
        } catch (_) {}
    }
    // Use tick to keep ESLint happy and trigger re-renders
    void tick;

    const stageLabel = _STAGE_LABELS[activeStageKey] || (activeStageKey || ap.agent_running || "Pipeline");

    return (
        <div
            className="mt-4 p-4 rounded border border-orange-500/30 bg-gradient-to-r from-orange-500/5 via-amber-500/5 to-orange-500/5 flex items-center gap-4"
            data-testid="genie-progress-banner"
        >
            <div className="shrink-0 relative">
                <img
                    src="/genie/genie.png"
                    alt="Genie"
                    className="w-12 h-12 object-contain"
                    style={{ animation: "genie-float 3s ease-in-out infinite" }}
                />
                <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-amber-400 pulse-dot" />
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] font-mono uppercase tracking-wider text-orange-400">
                        Working on
                    </span>
                    <span className="text-sm font-medium text-zinc-100" data-testid="genie-active-stage">
                        {stageLabel}
                    </span>
                    <span className="text-[10px] font-mono px-2 py-0.5 rounded-sm bg-amber-500/10 text-amber-300 border border-amber-500/30">
                        Phase {ap.phase || 1}
                    </span>
                </div>
                <div className="mt-1 text-sm text-zinc-300 font-light italic" data-testid="genie-rotating-message">
                    {_GENIE_MESSAGES[msgIdx]}
                </div>
                <div className="mt-1 text-[11px] font-mono text-zinc-500">
                    Elapsed: <span className="text-zinc-300" data-testid="genie-elapsed">{elapsed}</span>
                    {" · "}Your work is safe — if this stalls, recovery options will appear automatically.
                </div>
            </div>
        </div>
    );
};

/* Phase N — Stage Recovery Panel.
 * Shown when a stage is failed, manually_completed, skipped, OR appears
 * stuck in "running" with no auto_pipeline progress. Provides Retry / Skip /
 * Manual Override / Resume controls that hit the new endpoints:
 *   POST /api/projects/{id}/stages/{stage}/skip
 *   POST /api/projects/{id}/stages/{stage}/manual-output
 *   POST /api/projects/{id}/stages/{stage}/unstick
 * The Resume button reuses the existing /api/projects/{id}/auto/resume.
 */
// Workflow Hardening 2 / FIX 4 — skippable stages come from the canonical
// manifest (see _wfSkippable above). No local allow-list.

const StageError = ({ project, stageKey }) => {
    const s = (project?.stages || {})[stageKey] || {};
    const ap = project?.auto_pipeline || {};
    const status = (s.status || "").toLowerCase();
    const isFailed = status === "failed";
    const isSkipped = status === "skipped";
    const isManual = status === "manually_completed";
    // Heartbeat staleness — a stage that has been running for >3 min while
    // auto_pipeline isn't actively running it (or is itself stale) is stuck.
    const isRunning = status === "running" || status === "processing" || status === "in_progress";
    const startedAt = s.started_at || s.running_at;
    let ageSec = 0;
    if (startedAt) {
        try { ageSec = Math.max(0, (Date.now() - new Date(startedAt).getTime()) / 1000); } catch (_) {}
    }
    const HEARTBEAT_STALE = 1020; // FIX 7 — matches backend hard ceiling (900s) + buffer
    const apActiveStage = ap.agent_running || "";
    const apIsRunningThis = ap.status === "running" && (
        apActiveStage === stageKey ||
        // crude agent→stage mapping for the running indicator
        (apActiveStage === "brand_analyst" && stageKey === "brand_dna") ||
        (apActiveStage === "competitor_analyst" && stageKey === "competitor_analysis") ||
        (apActiveStage === "creative_director" && stageKey === "concepts") ||
        (apActiveStage === "prompt_engineer" && stageKey === "prompts")
    );
    // Workflow Hardening 2 / FIX 7 — a healthy manual run is NOT "stuck"
    // merely because the Auto Pipeline isn't running it. Stuck requires
    // either a backend-declared recoverable failure (isFailed) or an age
    // beyond the real hard-ceiling threshold.
    const isStuckRunning = isRunning && !apIsRunningThis && ageSec > HEARTBEAT_STALE;
    const visible = isFailed || isSkipped || isManual || isStuckRunning;

    const errMsg = safeText(s.last_error || s.error || ap.last_error || "");
    const isOptional = _wfSkippable().has(stageKey);
    const [busy, setBusy] = useState(false);
    const [showManual, setShowManual] = useState(false);
    const [manualText, setManualText] = useState("");
    const [manualNote, setManualNote] = useState("");
    const navigate = useNavigate();

    const call = async (path, body) => {
        setBusy(true);
        try {
            const { data } = await api.post(path, body);
            toast.success(data?.unstuck ? "Stage unstuck — controls now active" : "Done");
            // Reload by navigating to the same project (re-fetches via useEffect)
            navigate(0);
        } catch (e) {
            toast.error(`Action failed: ${e.userMessage || e.message}`);
        } finally { setBusy(false); }
    };

    // Final QA Beta — Presentation No-AI fallback. Surfaced inside both
    // stuck-running and failed recovery panels so the user can unstick
    // the pipeline with a deterministic deck assembled from existing
    // stage data (no LLM call).
    const runPresentationNoAi = async () => {
        if (busy) return;
        setBusy(true);
        const t = toast.loading("Compiling Basic (No AI) presentation…", { duration: Infinity });
        try {
            // Best-effort: try to clear the stuck flag first so /agents/run
            // is not rejected by the duplicate-run guard. Ignore failures.
            try {
                await api.post(`/projects/${project.id}/stages/${stageKey}/unstick`);
            } catch (_) { /* idempotent */ }
            await api.post(`/agents/run`, {
                project_id: project.id,
                agent: "presentation",
                extra_input: { no_ai: true },
            });
            toast.dismiss(t);
            toast.success("Basic presentation ready.");
            setTimeout(() => navigate(0), 600);
        } catch (e) {
            toast.dismiss(t);
            toast.error(`Basic deck failed: ${e.userMessage || e.message}`);
        } finally { setBusy(false); }
    };

    if (!visible) return null;
    if (isStuckRunning) {
        return (
            <div className="border border-amber-500/40 bg-amber-500/5 p-4 rounded-sm mb-6" data-testid={`stage-stuck-${stageKey}`}>
                <div className="flex items-start gap-3">
                    <span className="w-2 h-2 rounded-full bg-amber-400 mt-1.5 pulse-dot" />
                    <div className="flex-1">
                        <div className="label-micro text-amber-400 mb-1">Stage appears stuck</div>
                        <div className="text-sm text-zinc-200">
                            This stage is still marked as running, but the pipeline isn't actively executing.
                            Likely a stale state from a timeout. Your project is safe.
                        </div>
                        <div className="flex flex-wrap gap-2 mt-3">
                            <button
                                type="button"
                                data-testid={`stage-unstick-${stageKey}`}
                                onClick={() => call(`/projects/${project.id}/stages/${stageKey}/unstick`)}
                                disabled={busy}
                                className="btn-secondary text-xs disabled:opacity-50"
                            >
                                Unstick this stage
                            </button>
                            <button
                                type="button"
                                data-testid={`stage-kill-pipeline-${stageKey}`}
                                onClick={() => {
                                    if (!window.confirm("Cancel the running pipeline? Outputs are preserved; the failed stage will appear in Recovery so you can Resume.")) return;
                                    call(`/projects/${project.id}/auto/cancel`);
                                }}
                                disabled={busy}
                                title="Hard-cancel any in-flight pipeline task to stop credit burn"
                                className="text-xs px-3 py-1.5 rounded-sm border border-rose-500/40 text-rose-300 hover:bg-rose-500/10 disabled:opacity-50 transition-colors"
                            >
                                Cancel pipeline (kill orphan task)
                            </button>
                            {/* Patch C (Phase N.8) — Repair button surfaced in stuck-running banner too. */}
                            <button
                                type="button"
                                data-testid={`stage-repair-stuck-${stageKey}`}
                                onClick={() => call(`/projects/${project.id}/repair`)}
                                disabled={busy}
                                title="Clear stale running/processing flags. Outputs preserved."
                                className="text-xs px-3 py-1.5 rounded-sm border border-rose-500/40 text-rose-300 hover:bg-rose-500/10 disabled:opacity-50 transition-colors"
                            >
                                Repair project state
                            </button>
                            {stageKey === "presentation" && (
                                <button
                                    type="button"
                                    data-testid={`stage-presentation-no-ai-stuck`}
                                    onClick={runPresentationNoAi}
                                    disabled={busy}
                                    title="Compile a deterministic deck from existing stage data — no LLM call. Use this to unstick a presentation pipeline immediately."
                                    className="text-xs px-3 py-1.5 rounded-sm border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 disabled:opacity-50 transition-colors"
                                >
                                    Generate Basic (No AI) deck
                                </button>
                            )}
                            {isOptional && (
                                <button
                                    type="button"
                                    data-testid={`stage-skip-stuck-${stageKey}`}
                                    onClick={() => {
                                        const r = window.prompt(
                                            "Skip reason (optional):",
                                            "Presentation provider is stuck — skipping for now",
                                        );
                                        if (r === null) return;
                                        call(`/projects/${project.id}/stages/${stageKey}/skip`, { reason: r });
                                    }}
                                    disabled={busy}
                                    className="text-xs px-3 py-1.5 rounded-sm border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 disabled:opacity-50 transition-colors"
                                >
                                    Skip this stage
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (isSkipped) {
        return (
            <div className="border border-zinc-800 bg-zinc-950 p-3 rounded-sm mb-6 flex items-start gap-3" data-testid={`stage-skipped-${stageKey}`}>
                <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-sm border border-amber-500/30 text-amber-400 mt-0.5">Skipped</span>
                <div className="text-xs text-zinc-400 leading-relaxed">
                    Skipped {s.skipped_at ? `at ${new Date(s.skipped_at).toLocaleString()}` : ""} — {s.skip_reason || "no reason given"}.
                    Downstream agents will continue using prior context.
                </div>
            </div>
        );
    }

    if (isManual) {
        return (
            <div className="border border-emerald-500/30 bg-emerald-500/5 p-3 rounded-sm mb-6 flex items-start gap-3" data-testid={`stage-manual-${stageKey}`}>
                <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-sm border border-emerald-500/40 text-emerald-400 mt-0.5">Manual</span>
                <div className="text-xs text-zinc-300">
                    Output supplied manually by {s.manual_by || "user"} {s.manual_at ? `at ${new Date(s.manual_at).toLocaleString()}` : ""}. Pipeline will use this as the stage output.
                </div>
            </div>
        );
    }

    // Failed — full recovery panel
    // Phase O.Y — friendly first line + collapsible technical details.
    const telemetry = Array.isArray(s.run_telemetry) ? s.run_telemetry : [];
    return (
        <div className="border border-red-500/40 bg-red-500/5 p-5 rounded-sm mb-6" data-testid={`stage-error-${stageKey}`}>
            <div className="flex items-start gap-3 mb-3">
                <span className="w-2 h-2 rounded-full bg-red-400 mt-2 pulse-dot" />
                <div className="flex-1">
                    <div className="label-micro text-red-400 mb-1">Stage failed</div>
                    <div className="text-sm text-zinc-100 font-medium">
                        Provider failed while generating this stage. Your project is safe.
                        You can resume, retry in compressed mode, or continue manually.
                    </div>
                    {errMsg && (
                        <details className="mt-2" data-testid={`stage-error-details-${stageKey}`}>
                            <summary className="text-[11px] uppercase tracking-wider text-zinc-500 cursor-pointer hover:text-zinc-300">
                                Show technical details
                            </summary>
                            <pre className="text-[11px] text-zinc-400 font-mono mt-1 whitespace-pre-wrap leading-snug">
                                {errMsg.slice(0, 400)}
                            </pre>
                        </details>
                    )}
                    {telemetry.length > 0 && (
                        <div className="mt-2 text-[11px] font-mono text-zinc-500"
                             data-testid={`stage-ladder-telemetry-${stageKey}`}>
                            Retry attempts: {telemetry.map((t, i) => {
                                const label = t.compression_level === 0 ? "normal"
                                    : t.compression_level === 1 ? "compressed"
                                    : "short";
                                const result = t.status === "ok" ? "✓" : "✗";
                                return `${t.attempt}:${label}${result}`;
                            }).join(" → ")}
                        </div>
                    )}
                    {/* Phase O.3 — lease attempt history for opted-in projects. */}
                    {project?.lease_pipeline === true
                        && Array.isArray(s.lease_history)
                        && s.lease_history.length > 0 && (
                        <div
                            data-testid={`stage-lease-history-${stageKey}`}
                            className="mt-3 text-[11px] font-mono text-zinc-500"
                        >
                            Previous attempts: {s.lease_history.map((h, i) => {
                                const reason = h.reason || h.error || "ended";
                                return `${h.attempt ?? i + 1} (${String(reason).slice(0, 30)})`;
                            }).join(", ")}
                        </div>
                    )}
                </div>
            </div>
            <div className="text-xs text-zinc-400 mb-3">
                Choose how to recover. <span className="text-zinc-300">Retry</span> re-runs this single stage; <span className="text-zinc-300">Resume</span> continues the pipeline from here; <span className="text-zinc-300">Manual Override</span> pastes your own output; <span className="text-zinc-300">Skip</span> works for optional stages only.
            </div>
            <div className="flex flex-wrap gap-2">
                <RunAgentButton project={project} agent={stageKey === "brand_dna" ? "brand_analyst" : (stageKey === "concepts" ? "creative_director" : stageKey)} label="Retry stage" onDone={() => navigate(0)} />
                <button
                    type="button"
                    data-testid={`stage-retry-compressed-${stageKey}`}
                    onClick={async () => {
                        // Universal "Retry in compressed mode" — flips the
                        // next-retry hint via /unstick?compressed=1, then
                        // triggers a fresh Run via the existing endpoint.
                        if (busy) return;
                        setBusy(true);
                        try {
                            await api.post(
                                `/projects/${project.id}/stages/${stageKey}/unstick?compressed=1`,
                            );
                            const agent = stageKey === "brand_dna" ? "brand_analyst"
                                : stageKey === "concepts" ? "creative_director"
                                : stageKey;
                            await api.post(`/agents/run`, {
                                project_id: project.id, agent,
                            });
                            toast.success("Compressed retry started. Watch the Genie banner.");
                            setTimeout(() => navigate(0), 800);
                        } catch (e) {
                            toast.error(`Compressed retry failed: ${e.userMessage || e.message}`);
                        } finally { setBusy(false); }
                    }}
                    disabled={busy}
                    title="Re-run this stage with a compressed upstream context to avoid provider 502s on huge prompts."
                    className="text-xs px-3 py-1.5 rounded-sm border border-blue-500/40 text-blue-300 hover:bg-blue-500/10 disabled:opacity-50 transition-colors"
                >
                    Retry in compressed mode
                </button>
                <button
                    type="button"
                    data-testid={`stage-resume-${stageKey}`}
                    onClick={() => call(`/projects/${project.id}/auto/resume`)}
                    disabled={busy}
                    className="btn-secondary text-xs disabled:opacity-50"
                >
                    Resume from here
                </button>
                <button
                    type="button"
                    data-testid={`stage-manual-toggle-${stageKey}`}
                    onClick={() => setShowManual(v => !v)}
                    disabled={busy}
                    className="btn-secondary text-xs disabled:opacity-50"
                >
                    Manual override
                </button>
                {/* Patch C (Phase N.8) — Repair project state always reachable from
                 * Recovery Panel. Idempotent on the backend; never touches outputs. */}
                <button
                    type="button"
                    data-testid={`stage-repair-${stageKey}`}
                    onClick={() => call(`/projects/${project.id}/repair`)}
                    disabled={busy}
                    title="Clear stale running/processing flags. Outputs, versions and approvals are preserved."
                    className="px-3 py-1.5 text-xs border border-rose-500/40 text-rose-300 hover:bg-rose-500/10 rounded-sm disabled:opacity-50"
                >
                    Repair project state
                </button>
                {isOptional && (
                    <button
                        type="button"
                        data-testid={`stage-skip-${stageKey}`}
                        onClick={() => {
                            const r = window.prompt("Skip reason (optional):", "Not relevant for this campaign");
                            if (r === null) return;
                            call(`/projects/${project.id}/stages/${stageKey}/skip`, { reason: r });
                        }}
                        disabled={busy}
                        className="px-3 py-1.5 text-xs border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 rounded-sm disabled:opacity-50"
                    >
                        Skip this stage
                    </button>
                )}
                {stageKey === "presentation" && (
                    <button
                        type="button"
                        data-testid={`stage-presentation-no-ai-failed`}
                        onClick={runPresentationNoAi}
                        disabled={busy}
                        title="Compile a deterministic deck from existing stage data — no LLM call. Use this to unstick the pipeline when the AI provider keeps failing."
                        className="px-3 py-1.5 text-xs border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 rounded-sm disabled:opacity-50"
                    >
                        Generate Basic (No AI) deck
                    </button>
                )}
            </div>

            {showManual && (
                <div className="mt-4 border border-zinc-800 rounded-sm p-3 bg-[#0a0a0a]" data-testid={`stage-manual-form-${stageKey}`}>
                    <div className="label-micro mb-2">Paste output JSON</div>
                    <textarea
                        data-testid={`stage-manual-output-${stageKey}`}
                        value={manualText}
                        onChange={(e) => setManualText(e.target.value)}
                        placeholder='{"summary":"...", "strengths":["..."]}'
                        rows={6}
                        className="w-full bg-black border border-zinc-800 rounded-sm px-3 py-2 text-xs font-mono focus:border-zinc-500 outline-none resize-y"
                    />
                    <input
                        data-testid={`stage-manual-note-${stageKey}`}
                        value={manualNote}
                        onChange={(e) => setManualNote(e.target.value)}
                        placeholder="Note (optional)"
                        className="w-full bg-black border border-zinc-800 rounded-sm px-3 py-2 mt-2 text-xs focus:border-zinc-500 outline-none"
                    />
                    <div className="flex justify-end gap-2 mt-2">
                        <button type="button" onClick={() => setShowManual(false)} className="btn-secondary text-xs">Cancel</button>
                        <button
                            type="button"
                            data-testid={`stage-manual-save-${stageKey}`}
                            onClick={async () => {
                                let parsed;
                                try { parsed = JSON.parse(manualText); }
                                catch { toast.error("Invalid JSON"); return; }
                                await call(`/projects/${project.id}/stages/${stageKey}/manual-output`, { output: parsed, note: manualNote || null });
                            }}
                            disabled={busy || !manualText.trim()}
                            className="btn-primary text-xs disabled:opacity-50"
                        >
                            Save manual output
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

const Empty = ({ hint }) => (
    <div className="text-sm text-zinc-500 text-center py-12 border border-dashed border-zinc-900 rounded-sm">{hint}</div>
);

const KV = ({ title, value, mono }) => (
    <div className="card-base p-5">
        <div className="label-micro mb-2">{title}</div>
        <div className={`text-sm text-zinc-200 whitespace-pre-wrap ${mono ? "font-mono" : ""}`}>{value || <span className="text-zinc-600">—</span>}</div>
    </div>
);

const KVList = ({ title, items, accent, mono }) => (
    <div className="card-base p-5">
        <div className="label-micro mb-2">{title}</div>
        <ul className="space-y-2">
            {(items || []).map((it, i) => {
                const dotCls = accent === "emerald" ? "text-emerald-400" : accent === "red" ? "text-red-400" : "text-zinc-500";
                // Phase K-demo — structured SWOT / insights entries (Brand DNA v2 schema):
                //   {strength|weakness|opportunity|risk|insight, evidence|why_it_hurts|time_window|likelihood|action, ...}
                if (it && typeof it === "object" && !Array.isArray(it)) {
                    const headKey = ["strength","weakness","opportunity","risk","insight","gap"]
                        .find(k => typeof it[k] === "string" && it[k].trim());
                    if (headKey) {
                        const head = it[headKey];
                        const meta = Object.entries(it)
                            .filter(([k, v]) => k !== headKey && typeof v === "string" && v.trim())
                            .map(([k, v]) => ({ k: k.replaceAll("_", " "), v }));
                        return (
                            <li key={i} className={`text-sm ${mono ? "font-mono" : ""}`}>
                                <div className="flex gap-2">
                                    <span className={dotCls}>•</span>
                                    <span className="text-zinc-100 font-medium">{head}</span>
                                </div>
                                {meta.length > 0 && (
                                    <div className="ml-4 mt-1 space-y-0.5">
                                        {meta.map((m, mi) => (
                                            <div key={mi} className="text-[11px] text-zinc-500">
                                                <span className="uppercase tracking-wider text-zinc-600 mr-1">{m.k}:</span>
                                                <span className="text-zinc-300">{m.v}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </li>
                        );
                    }
                }
                // Legacy: plain string bullet
                return (
                    <li key={i} className={`text-sm flex gap-2 ${mono ? "font-mono" : ""}`}>
                        <span className={dotCls}>•</span>
                        <span className="text-zinc-200">{typeof it === "string" ? it : JSON.stringify(it)}</span>
                    </li>
                );
            })}
            {(!items || items.length === 0) && <li className="text-zinc-600 text-sm">—</li>}
        </ul>
    </div>
);

/* Plan card rendered inside a CEO agent chat message */
function PlanCard({ plan, onRun }) {
    const agents = plan.plan || [];
    return (
        <div className="mt-3 border border-zinc-800 rounded-sm bg-black/40 p-3" data-testid="plan-card">
            <div className="flex items-center justify-between gap-3 mb-2">
                <span className="text-[10px] font-mono uppercase tracking-wider text-[#FF4400]">
                    🧠 CEO PLAN · {plan.scope?.replaceAll("_"," ") || "plan"}
                </span>
                <span className="text-[10px] font-mono text-zinc-500">{agents.length} step{agents.length === 1 ? "" : "s"}</span>
            </div>

            {plan.intent_detected && (
                <div className="text-[11px] mb-2" data-testid="plan-intent">
                    <span className="font-mono uppercase tracking-wider text-zinc-500 mr-1.5">Intent:</span>
                    <span className="text-zinc-200">{plan.intent_detected}</span>
                </div>
            )}

            <div className="text-[11px] uppercase tracking-wider font-mono text-zinc-500 mb-1">Plan</div>
            <ol className="space-y-1.5 mb-3" data-testid="plan-steps">
                {agents.map((a, i) => (
                    <li key={i} className="text-xs flex gap-2">
                        <span className="font-mono text-zinc-500 w-6">{String(i + 1).padStart(2, "0")}</span>
                        <div>
                            <span className="text-zinc-100 font-medium">{a.agent.replaceAll("_", " ")}</span>
                            {a.why && <span className="text-zinc-500"> — {a.why}</span>}
                        </div>
                    </li>
                ))}
                {agents.length === 0 && <li className="text-xs text-zinc-600">No new agents needed.</li>}
            </ol>

            {plan.skipped_stages && plan.skipped_stages.length > 0 && (
                <div className="mb-3" data-testid="plan-skipped">
                    <div className="text-[11px] uppercase tracking-wider font-mono text-zinc-500 mb-1">Skipped</div>
                    <ul className="text-[11px] text-zinc-400 space-y-0.5">
                        {plan.skipped_stages.map((s, i) => (
                            <li key={i}>
                                <span className="font-mono text-zinc-500 mr-1.5">{s.stage?.replaceAll("_"," ")}:</span>
                                <span>{s.reason}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {plan.missing_inputs && plan.missing_inputs.length > 0 && (
                <div className="mt-2 mb-3 p-2 border border-amber-500/30 bg-amber-500/5 rounded-sm" data-testid="plan-missing">
                    <div className="label-micro text-amber-400 mb-1">Missing inputs</div>
                    <ul className="text-xs text-zinc-300 space-y-1.5">
                        {plan.missing_inputs.map((m, i) => (
                            <li key={i}>
                                <div>• <span className="text-amber-400 font-mono uppercase">{m.type}</span> — {m.reason}</div>
                                {m.options && m.options.length > 0 && (
                                    <div className="ml-4 mt-1 flex flex-wrap gap-1.5">
                                        {m.options.map((o, j) => (
                                            <span key={j} className="text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 rounded-sm border border-amber-500/40 text-amber-200">
                                                {j + 1}. {o}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {plan.next_action && (
                <div className="text-[11px] mb-3" data-testid="plan-next-action">
                    <span className="font-mono uppercase tracking-wider text-zinc-500 mr-1.5">Next:</span>
                    <span className="text-zinc-100">{plan.next_action}</span>
                </div>
            )}

            <button
                data-testid="run-plan-auto"
                onClick={() => onRun(plan)}
                disabled={agents.length === 0}
                className="btn-accent text-xs px-3 py-1.5 disabled:opacity-50"
            >
                <Sparkles className="w-3.5 h-3.5" /> Execute plan (AUTO)
            </button>
            {plan.optional_next && plan.optional_next.length > 0 && (
                <div className="text-[10px] font-mono text-zinc-500 mt-2">
                    Optional next: {plan.optional_next.join(", ")}
                </div>
            )}
        </div>
    );
}

/* ============================ MODE TOGGLE ============================ */
function ModeToggle({ project, onChange }) {
    const [mode, setMode] = useState(project.execution_mode || "manual");
    const [busy, setBusy] = useState(false);
    useEffect(() => { setMode(project.execution_mode || "manual"); }, [project.execution_mode]);
    const swap = async (next) => {
        setBusy(true);
        try {
            await api.post(`/projects/${project.id}/mode`, { mode: next });
            setMode(next);
            toast.success(`Mode → ${next.toUpperCase()}`);
            onChange && onChange();
        } catch (e) {
            toast.error(e?.userMessage || "Failed to switch mode");
        } finally { setBusy(false); }
    };
    return (
        <div className="inline-flex rounded-sm border border-zinc-800 overflow-hidden" data-testid="mode-toggle">
            {["manual", "auto"].map((m) => (
                <button
                    key={m}
                    data-testid={`mode-${m}`}
                    disabled={busy}
                    onClick={() => mode !== m && swap(m)}
                    className={`px-3 py-1.5 text-[11px] font-mono uppercase tracking-wider transition-colors ${mode === m ? "bg-[#FF4400] text-white" : "bg-transparent text-zinc-400 hover:text-white"}`}
                >
                    {m}
                </button>
            ))}
        </div>
    );
}

/* ============================ COST PILL ============================ */
function CostPill({ projectId }) {
    const [c, setC] = useState(null);
    useEffect(() => {
        let alive = true;
        const load = () => api.get(`/projects/${projectId}/cost`).then(({ data }) => alive && setC(data)).catch(() => {});
        load();
        const t = setInterval(load, 15000);
        return () => { alive = false; clearInterval(t); };
    }, [projectId]);
    if (!c) return null;
    return (
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-sm border border-zinc-800 text-[11px] font-mono" data-testid="cost-pill" title={`${c.total_tokens} tokens across ${c.total_runs} runs`}>
            <span className="text-zinc-500">COST</span>
            <span className="text-white">${c.total_cost_usd.toFixed(4)}</span>
        </div>
    );
}



/* ─────────────────────── AUTO PIPELINE BAR ─────────────────────── */
function AutoPipelineBar({ project, reload, hidePhaseChips = false }) {
    const [state, setState] = useState(project?.auto_pipeline || null);
    const [phases, setPhases] = useState([]);
    const [busy, setBusy] = useState(false);
    // Phase O.3 — single ticker for all LeaseBadge instances so we don't
    // re-render the entire board every second.
    const [nowMs, setNowMs] = useState(Date.now());
    const [leaseDetailOpen, setLeaseDetailOpen] = useState(false);

    // P0 Workflow Integrity — resolve next-runnable stage from the
    // backend's canonical source of truth. Never guessed locally.
    const wf = useCanonicalWorkflow();
    const canonicalNextStage = useMemo(
        () => wf.computeNextStage(project),
        [project, wf.ready, wf.order],
    );
    const canonicalNextLabel = canonicalNextStage
        ? (wf.labels[canonicalNextStage] || canonicalNextStage)
        : null;

    // Find the currently-leased stage (at most one per project) so we
    // can surface it inline with the pipeline-bar badge.
    const activeLeaseStage = useMemo(() => {
        const stages = project?.stages || {};
        for (const [sk, sv] of Object.entries(stages)) {
            if ((sv?.status || "").toLowerCase() === "leased" && sv?.lease) {
                return { stage_key: sk, lease: { ...sv.lease, stage_key: sk } };
            }
        }
        return null;
    }, [project?.stages]);

    useEffect(() => {
        if (!activeLeaseStage) return;
        const t = setInterval(() => setNowMs(Date.now()), 1000);
        return () => clearInterval(t);
    }, [activeLeaseStage]);

    const refresh = async () => {
        try {
            const { data } = await api.get(`/projects/${project.id}/auto/status`);
            setState(data.auto_pipeline);
            setPhases(data.phases || []);
        } catch { /* ignore */ }
    };

    useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [project.id]);

    // Poll while running so the bar live-updates
    useEffect(() => {
        if (!state || !["running", "ready_for_approval"].includes(state.status)) return;
        const t = setInterval(async () => {
            await refresh();
            await reload();
        }, 4000);
        return () => clearInterval(t);
        // eslint-disable-next-line
    }, [state?.status]);

    const start = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`/projects/${project.id}/auto/start`);
            setState(data.auto_pipeline);
            toast.success("Auto pipeline started — Phase 1 running");
            await reload();
        } catch (e) {
            toast.error(`Could not start: ${e.userMessage || e.message}`);
        } finally { setBusy(false); }
    };

    const approve = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`/projects/${project.id}/auto/approve`);
            setState(data.auto_pipeline);
            toast.success("Approved — next phase running");
            await reload();
        } catch (e) {
            toast.error(`Approve failed: ${e.userMessage || e.message}`);
        } finally { setBusy(false); }
    };

    const resume = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`/projects/${project.id}/auto/resume`);
            setState(data.auto_pipeline);
            toast.success("Resuming from the failed stage — completed stages preserved");
            await reload();
        } catch (e) {
            // P1 Demo Hardening — backend now returns structured detail
            // `{code, hint, message}` for 409s. `e.userMessage` is populated
            // by api.js (_stringify extracts `v.message` from dict detail),
            // so the friendly hint (e.g. "Output is already ready for review.
            // Please approve this stage to continue.") shows in the toast.
            // safeText is a final safety net for any legacy string-detail
            // 409 still in the wild.
            toast.error(`Resume: ${e?.userMessage || safeText(e?.response?.data?.detail) || e.message}`);
        } finally { setBusy(false); }
    };

    const restartFull = async () => {
        if (!window.confirm(
            "Restart the FULL pipeline from Phase 1?\n\n" +
            "This will regenerate every stage (concepts, script, deck will be re-created). " +
            "Prior outputs are kept as version history, but live outputs will be replaced.\n\n" +
            "Click OK to restart, or Cancel and use 'Resume from failed stage' to keep your work."
        )) return;
        setBusy(true);
        try {
            const { data } = await api.post(`/projects/${project.id}/auto/start`);
            setState(data.auto_pipeline);
            toast.success("Pipeline restarted from Phase 1");
            await reload();
        } catch (e) {
            toast.error(`Restart failed: ${e.userMessage || e.message}`);
        } finally { setBusy(false); }
    };

    if (!state) return null;
    const idle = state.status === "idle" || !state.status;
    const cur = phases.find(p => p.id === state.phase) || null;

    // Patch F (Phase N.8) — strict guards. Used to disable buttons whose
    // preconditions don't hold so users can never click into a 409.
    const awaitingStage = state.awaiting_stage || null;
    const approveGuard = awaitingStage
        ? isActionAllowed(project, "approve", { stage: awaitingStage })
        : { ok: state.status === "ready_for_approval", reason: state.status === "ready_for_approval" ? "" : "Pipeline is not awaiting approval" };
    const resumeGuard = isActionAllowed(project, "resume");
    const restartGuard = isActionAllowed(project, "restart");

    return (
        <div className="mt-4 p-3 rounded border border-zinc-800 bg-zinc-950/60"
             data-testid="auto-pipeline-bar">
            <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-[10px] uppercase tracking-wider font-mono text-orange-400">
                        Auto Pipeline
                    </span>
                    {idle ? (
                        <span className="text-xs text-zinc-500">Not started</span>
                    ) : (
                        <>
                            <span className="text-xs font-mono text-zinc-300">
                                Phase {state.phase}/{phases.length} · {cur?.name || ""}
                            </span>
                            <span className={`text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 rounded-sm border ${
                                state.status === "running" ? "bg-amber-500/10 text-amber-300 border-amber-500/30" :
                                state.status === "ready_for_approval" ? "bg-orange-500/15 text-orange-300 border-orange-500/40" :
                                state.status === "completed" ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/30" :
                                state.status === "failed" ? "bg-red-500/10 text-red-300 border-red-500/30" :
                                "bg-zinc-800/40 text-zinc-300 border-zinc-700"
                            }`}>
                                {state.status.replaceAll("_"," ")}
                            </span>
                            {state.agent_running && (
                                <span className="text-xs font-mono text-amber-300">
                                    <span className="inline-block w-2 h-2 rounded-full bg-amber-400 pulse-dot mr-1" />
                                    Running: {state.agent_running}
                                </span>
                            )}
                            {project?.lease_pipeline === true && activeLeaseStage && (
                                <span className="relative inline-block">
                                    <LeaseBadge
                                        lease={activeLeaseStage.lease}
                                        maxAttempts={(project.pipeline_constants || {}).lease_max_attempts || 3}
                                        nowMs={nowMs}
                                        onClick={() => setLeaseDetailOpen(v => !v)}
                                    />
                                    <LeaseDetailTooltip
                                        lease={activeLeaseStage.lease}
                                        open={leaseDetailOpen}
                                        onClose={() => setLeaseDetailOpen(false)}
                                    />
                                </span>
                            )}
                        </>
                    )}
                    {/* P0 Workflow Integrity — canonical next-runnable stage
                        chip. Sourced from /api/workflow/canonical + client-side
                        mirror of backend compute_next_stage(). Advisory only —
                        never lets the UI silently pick a downstream stage. */}
                    {canonicalNextStage && state?.status !== "running" && (
                        <span
                            className="text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 rounded-sm border bg-sky-500/10 text-sky-300 border-sky-500/40"
                            data-testid="canonical-next-stage-chip"
                            title={`Backend canonical next stage: ${canonicalNextStage}`}
                        >
                            Next: {canonicalNextLabel}
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    {idle && (
                        <button
                            onClick={start}
                            disabled={busy}
                            data-testid="auto-start-btn"
                            className="px-4 py-1.5 bg-orange-500 hover:bg-orange-600 text-white text-xs font-medium rounded disabled:opacity-50"
                        >
                            {busy ? "Starting…" : "Start Auto Pipeline"}
                        </button>
                    )}
                    {state.status === "ready_for_approval" && (
                        <button
                            onClick={() => {
                                // Sprint UI Completion — top banner is now
                                // status-only. Clicking "Review" scrolls to
                                // the bottom ApprovalBar, which is the ONLY
                                // approval control. No approvals happen here.
                                const target = awaitingStage
                                    ? document.querySelector(`[data-testid="approval-${awaitingStage}"]`)
                                    : null;
                                if (target) {
                                    target.scrollIntoView({ behavior: "smooth", block: "center" });
                                    target.classList.add("ring-2", "ring-orange-500/60");
                                    setTimeout(() => target.classList.remove("ring-2", "ring-orange-500/60"), 1600);
                                } else {
                                    // fall back — scroll page bottom so any ApprovalBar is visible
                                    window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
                                }
                            }}
                            disabled={busy}
                            data-testid="auto-review-btn"
                            title="Scroll to approval controls below"
                            className="px-4 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-100 text-xs font-medium rounded border border-zinc-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {awaitingStage
                                ? `Review ${awaitingStage.replaceAll("_"," ")} ↓`
                                : "Review ↓"}
                        </button>
                    )}
                    {state.status === "running" && (
                        <span className="text-[11px] font-mono text-zinc-500">
                            Working in background…
                        </span>
                    )}
                    {state.status === "failed" && (
                        <>
                            <button
                                onClick={resume}
                                disabled={busy || !resumeGuard.ok}
                                data-testid="auto-resume-btn"
                                title={resumeGuard.ok ? "Resume from failed stage" : resumeGuard.reason}
                                className="px-3 py-1.5 bg-orange-500 hover:bg-orange-600 text-white text-xs font-medium rounded disabled:opacity-50 disabled:cursor-not-allowed">
                                {busy ? "Resuming…" : "Resume from failed stage"}
                            </button>
                            <button
                                onClick={restartFull}
                                disabled={busy || !restartGuard.ok}
                                data-testid="auto-restart-btn"
                                title={restartGuard.ok ? "Regenerate every stage from Phase 1" : restartGuard.reason}
                                className="px-3 py-1.5 border border-zinc-700 text-zinc-400 hover:text-zinc-200 hover:border-zinc-500 text-xs font-medium rounded disabled:opacity-50 disabled:cursor-not-allowed">
                                Restart full pipeline
                            </button>
                        </>
                    )}
                </div>
            </div>
            {state.last_error && (
                <div className="mt-2 text-[11px] font-mono text-red-300/80">
                    Last error: {safeText(state.last_error)}
                </div>
            )}
            {phases.length > 0 && !hidePhaseChips && (
                <div className="mt-2 flex gap-1.5 flex-wrap">
                    {phases.map(p => {
                        const done = state.phase > p.id || state.status === "completed";
                        const active = state.phase === p.id && state.status !== "completed";
                        return (
                            <span key={p.id}
                                  className={`text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 rounded-sm border ${
                                      done ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/30" :
                                      active ? "bg-orange-500/15 text-orange-300 border-orange-500/40" :
                                      "bg-zinc-900 text-zinc-500 border-zinc-800"
                                  }`}>
                                P{p.id} · {p.name}
                            </span>
                        );
                    })}
                </div>
            )}
        </div>
    );
}


/** Phase N.6 — Project archive control.
 *  - If `project.is_archived` → shows "Unarchive" pill that restores it.
 *  - Else → shows "Archive" pill. On click:
 *      1. Confirms with the user (and warns about cancelling running pipeline).
 *      2. Cancels any running pipeline first via /auto/cancel (best-effort).
 *      3. POSTs /api/projects/{id}/archive — the backend ALSO cancels the
 *         active task as a final safety net so credits never burn.
 *      4. Toasts success and navigates back to /projects (archived projects
 *         are hidden from the default list).
 *  No hard-delete. Outputs / versions / approvals / share links untouched.
 */
function ArchiveProjectButton({ project, reload, navigate }) {
    const [busy, setBusy] = useState(false);
    const archived = !!project.is_archived;

    const onUnarchive = async () => {
        if (busy) return;
        setBusy(true);
        try {
            await api.post(`/projects/${project.id}/unarchive`);
            toast.success("Project restored to active dashboard.");
            await reload();
        } catch (e) {
            toast.error(`Could not unarchive: ${e.userMessage || e.message}`);
        } finally {
            setBusy(false);
        }
    };

    const onArchive = async () => {
        if (busy) return;
        const ap = project.auto_pipeline || {};
        const isRunning = ap.status === "running" || ap.agent_running;
        const msg = isRunning
            ? "This project's pipeline is still running.\n\n" +
              "Archiving will cancel the running task and hide the project from your active dashboard.\n\n" +
              "Outputs, versions, approvals and exports are preserved. You can unarchive any time.\n\n" +
              "Continue?"
            : "Archive this project?\n\n" +
              "It will be hidden from the active dashboard. Outputs, versions, approvals and exports are preserved.\n\n" +
              "You can unarchive any time.";
        if (!window.confirm(msg)) return;
        setBusy(true);
        try {
            // Best-effort pre-cancel so the pipeline state is clean before
            // we archive. The backend also cancels as a final safety net.
            if (isRunning) {
                try { await api.post(`/projects/${project.id}/auto/cancel`); } catch { /* backend handles */ }
            }
            await api.post(`/projects/${project.id}/archive`);
            toast.success("Project archived. Hidden from the active dashboard.");
            navigate("/projects");
        } catch (e) {
            toast.error(`Could not archive: ${e.userMessage || e.message}`);
            setBusy(false);
        }
    };

    if (archived) {
        return (
            <button
                type="button"
                data-testid="project-unarchive-btn"
                onClick={onUnarchive}
                disabled={busy}
                className="text-xs px-3 py-1.5 rounded-sm border border-amber-500/40 text-amber-200 hover:bg-amber-500/10 disabled:opacity-50 transition-colors flex items-center gap-1.5"
                title="Restore this project to the active dashboard"
            >
                <ArchiveRestore className="w-3.5 h-3.5" />
                {busy ? "Restoring…" : "Unarchive"}
            </button>
        );
    }

    return (
        <button
            type="button"
            data-testid="project-archive-btn"
            onClick={onArchive}
            disabled={busy}
            className="text-xs px-3 py-1.5 rounded-sm border border-zinc-700 text-zinc-300 hover:bg-zinc-900 hover:border-zinc-500 disabled:opacity-50 transition-colors flex items-center gap-1.5"
            title="Soft-archive this project (hidden from dashboard, fully recoverable)"
        >
            <Archive className="w-3.5 h-3.5" />
            {busy ? "Archiving…" : "Archive"}
        </button>
    );
}


/* ============================================================
 *  Phase 2E-lite — Distribution tab.
 *  Wraps SocialProfilesPanel + DistributionOpportunityPanel with a
 *  shared honesty header so the user always sees this is a registry
 *  + rule-based recommendation surface (no scraping, no posting).
 * ============================================================ */
function DistributionPanel({ project, client }) {
    return (
        <div className="p-6 lg:p-8 max-w-7xl space-y-6" data-testid="distribution-tab">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                    <div className="label-micro mb-1">Phase 2E-lite</div>
                    <h2 className="font-display text-2xl">Social registry &amp; distribution</h2>
                    <p className="text-sm text-zinc-500 mt-2 max-w-2xl leading-relaxed">
                        Centralized profile links and a deterministic, rule-based
                        channel map for this project.
                        <span className="text-amber-300"> No scraping, no OAuth,
                        no posting</span> — content is not deeply analyzed in this
                        phase.
                    </p>
                </div>
                {client?.company_name && (
                    <div className="text-[11px] font-mono uppercase tracking-wider text-zinc-500 border border-zinc-800 rounded-sm px-2 py-1">
                        Client: <span className="text-zinc-300">{client.company_name}</span>
                    </div>
                )}
            </div>

            <SocialProfilesPanel projectId={project.id} />
            <DistributionOpportunityPanel projectId={project.id} />
        </div>
    );
}

/* ============================================================
 *  Phase 2F — Social Strategy tab wrapper. Keeps the panel framed
 *  with the same honesty header pattern as the Distribution tab.
 * ============================================================ */
function SocialStrategyTab({ project, client }) {
    return (
        <div className="p-6 lg:p-8 max-w-7xl space-y-6" data-testid="social-strategy-tab">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                    <div className="label-micro mb-1">Phase 2F</div>
                    <h2 className="font-display text-2xl">Social strategy report</h2>
                    <p className="text-sm text-zinc-500 mt-2 max-w-2xl leading-relaxed">
                        Launch-ready content strategy fused from Brand DNA, Research,
                        Concepts, Profile Registry and Distribution.
                        <span className="text-amber-300"> No scraping, no OAuth,
                        no posting</span> — platform content is not deeply analyzed.
                    </p>
                </div>
                {client?.company_name && (
                    <div className="text-[11px] font-mono uppercase tracking-wider text-zinc-500 border border-zinc-800 rounded-sm px-2 py-1">
                        Client: <span className="text-zinc-300">{client.company_name}</span>
                    </div>
                )}
            </div>

            <SocialStrategyPanel projectId={project.id} />
        </div>
    );
}


/* ============================================================
 *  Phase 2G — Video Prompts tab wrapper.
 *  Same honesty header pattern as the Social Strategy tab.
 * ============================================================ */
function VideoPromptsTab({ project, client }) {
    return (
        <div className="p-6 lg:p-8 max-w-7xl space-y-6" data-testid="video-prompts-tab">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                    <div className="label-micro mb-1">Phase 2G · Prompt Pack</div>
                    <h2 className="font-display text-2xl">Prompt Pack — Video</h2>
                    <p className="text-sm text-zinc-500 mt-2 max-w-2xl leading-relaxed">
                        Provider-ready prompts fused from your locked concept,
                        approved script, approved storyboard, active style lock,
                        and asset library.
                        <span className="text-amber-300"> No video generation,
                        no uploads, no OAuth, no posting</span> — production
                        handoff only.
                    </p>
                </div>
                {client?.company_name && (
                    <div className="text-[11px] font-mono uppercase tracking-wider text-zinc-500 border border-zinc-800 rounded-sm px-2 py-1">
                        Client: <span className="text-zinc-300">{client.company_name}</span>
                    </div>
                )}
            </div>

            <VideoPromptsPanel projectId={project.id} />
        </div>
    );
}

